<template>
  <div id="app" data-testid="app-root">
    <component :is="currentComponent" :camera-permissions="cameraPermissions" :device-id="deviceId"
      :raw-orders="processedOrders" :restaurant="restaurant" :settings="settings"
      :available-cameras="availableCameras"
      :stream-info="streamInfo"
      :delivered-rider-order-ids="riderDeliveredOrderIds"
      :startup-status="startupStatus"
      :periscope-source="periscopeTutorialSource"
      :show-qr-gun-tutorial="currentComponent === 'SettingsScreen' && showQrGunTutorial ? true : undefined"
      :qr-gun-source="qrGunTutorialSource"
      :qr-gun-initial-step="qrGunTutorialStep"
      :qr-gun-scan-event="currentComponent === 'QrGunTutorialScreen' ? qrGunScanEvent : null"
      :photo-video="currentComponent === 'SettingsScreen' || currentComponent === 'PhotoVideoTutorialScreen' ? photoVideo : undefined"
      :photo-video-source="currentComponent === 'PhotoVideoTutorialScreen' ? photoVideoSource : undefined"
      @login-success="handleLoginSuccess"
      @retry-startup="handleRetryStartup"
      @open-settings="openSettings" @open-help="openHelp" @sign-out="handleSignOut" @open-queue-screen="openQueueScreen"
      @open-changelog="openChangelog"
      @open-periscope-tutorial="openPeriscopeTutorial('settings')"
      @periscope-close="closePeriscopeTutorial"
      @open-qr-gun-tutorial="openQrGunTutorial('settings')"
      @qr-gun-close="closeQrGunTutorial"
      @open-photo-video-tutorial="openPhotoVideoTutorial('settings')"
      @photo-video-close="closePhotoVideoTutorial"
      @play-closed-sound="playOrderClosedSound"
      @go-back="goBack" @photo-saved="handlePhotoSaved" @update-settings="updateSettings"
      @cameras-enumerated="availableCameras = $event"
      @stream-info="streamInfo = $event"
      @mark-rider-delivered="markRiderDelivered"
      @order-selection-changed="isPhotographing = $event"
      @stale-camera-id="handleStaleCameraId"
      @camera-state="onCameraState"
      @device-synced="handleDeviceSynced"
      @orders-on-screen="helpOrdersOnScreen = $event"
      @ai-wrong-reason="onAiWrongReason" />
  </div>
  <ChangelogComponent
      :db="db"
      :active="currentComponent === 'CameraScreen' && !isPhotographing && changelogGateOpen && !helpFlowVisible"
      :open-signal="changelogOpenRequests"
    />
  <NotificationComponent
      :notification="currentNotification"
      :restaurantId="restaurant?.id || ''"
      @notification-closed="handleNotificationClosed"
      @option-selected="handleNotificationOption"
    />
  <!-- Non-blocking top banner: orders and camera stay reachable underneath, any tap
       outside dismisses it, and App.vue auto-closes it after AUTO_DISMISS_MIN. -->
  <PhotoCoverageAlert
      :visible="coverageBannerVisible"
      :count="coverageAlert.count"
      :auto-dismiss-ms="coverageAutoDismissMs"
      :timer-started-at="coverageAutoCloseArmedAt"
      @ack="onCoverageAck"
      @busy="onCoverageBusy"
      @support="onCoverageSupport"
      @dismiss="onCoverageDismiss"
      @step-change="onCoverageStepChange"
    />
  <!-- Guided help: one-tap topics → automatic remedy → a support message written for them
       (docs/flujo-ayuda-guiado.md). Sits above the coverage banner (9600) and below rider
       toasts / blocking notifications (≥ 9999). -->
  <GuidedFlowModal
      flow-id="help"
      :visible="helpFlowVisible"
      :step="helpFlow ? helpFlow.step : null"
      :options="helpTopics"
      :pick-items="helpPickItems"
      :multi="helpFlow && helpFlow.topic ? Boolean(helpFlow.topic.multi) : false"
      :pick-title-key="helpPickTitleKey"
      :pick-hint-key="helpPickHintKey"
      :pick-none-key="helpPickNoneKey"
      :evidence="helpFlowEvidence"
      :confirm-key="helpFlow && helpFlow.topic ? helpFlow.topic.confirmKey : null"
      :unavailable-reason="helpFlow ? helpFlow.unavailableReason : null"
      :pick-status="helpPickStatus"
      pick-loading-key="message.helpPhotosLoading"
      pick-failed-key="message.helpPhotosFailed"
      :pick-fallback-key="helpPickFallbackKey"
      :pick-empty-key="helpPickEmptyKey"
      :pick-more="helpPickMore"
      pick-more-key="message.helpPhotosMore"
      :pick-two-columns="helpPickTwoColumns"
      :view-item="helpViewItem"
      :view-photos="helpViewPhotos"
      :view-status="helpPhotoView.status"
      view-support-key="message.orderPhotosSupport"
      title-key="message.helpFlowTitle"
      working-key="message.helpFlowWorkingReload"
      working-sub-key="message.helpFlowWorkingSub"
      confirm-sub-key="message.helpFlowConfirmSub"
      @choose="onHelpChoose"
      @pick="onHelpPick"
      @submit-text="onHelpSubmitText"
      @submit-code="onHelpSubmitCode"
      @answer="onHelpAnswer"
      @retry="onHelpRetry"
      @reload="onHelpReloadApp"
      @back="onHelpBack"
      @dismiss="onHelpDismiss"
      @refresh="onHelpPhotosRefresh"
      @more="onHelpPhotosMore"
      @view-support="onHelpViewSupport"
      @refresh-view="onHelpViewRefresh"
      @photo-load="onHelpPhotoLoad"
    />
  <!-- Positive close of the coverage loop: they said "yes" and actually resumed. A snackbar of the system, sat
       ABOVE the order tray so it never covers a card (norm 3). -->
  <transition name="coverage-thanks">
    <div v-if="coverageThanksVisible" class="bz-snackbar coverage-thanks-toast" data-testid="coverage-thanks" role="status">
      <BzIcon name="check_circle" class="bz-snackbar__icon" />
      <span>{{ $t('message.coverageThanks') }}</span>
    </div>
  </transition>
  <!-- Rider sound assets (preloaded) -->
  <audio ref="riderArrivedSound" src="/rider-arrived.mp3" preload="auto"></audio>
  <audio ref="riderWaitingLongSound" src="/rider-waiting-long.mp3" preload="auto"></audio>
  <!-- Rider toasts (max 2 visible) — designed for QSR kitchen: huge order code, wait time, swipe-to-dismiss. Cards of the
       system (.bz-toast, src/ui/system.css): the rider's in its own red, the messages on the raised surface. The
       container is a live region, so a screen reader reads each toast as it arrives. -->
  <!-- Rider toasts stack above the coverage banner (z10000 vs 9600), so while the banner
       is up they slide below it instead of covering its amber evidence chip. -->
  <div class="rider-toast-container" :class="{ 'is-below-coverage': coverageBannerVisible }" role="status" aria-live="polite">
    <div
      v-for="toast in visibleRiderToasts"
      :key="toast.id"
      class="bz-toast"
      data-testid="rider-toast"
      :data-toast-kind="toast.kind || 'rider'"
      :data-toast-type="toast.type"
      :data-toast-via="toast.via || null"
      :class="[
        `is-${toast.type}`,
        toast.kind === 'message' ? `is-message is-msg-${toast.variant}` : (toast.type === 'delivered' ? '' : 'bz-toast--rider'),
        { 'is-reminder': toast.isReminder, 'is-urgent': toast.waitMinutes >= 10 && toast.type !== 'delivered', 'is-swiping': swipeStates[toast.id] && swipeStates[toast.id].dragging }
      ]"
      :style="swipeStates[toast.id] ? { transform: `translateX(${swipeStates[toast.id].offset}px)`, opacity: 1 - Math.min(Math.abs(swipeStates[toast.id].offset) / 260, 0.85) } : {}"
      @click="onToastClick(toast.id)"
      @touchstart.passive="onSwipeStart(toast.id, $event)"
      @touchmove.passive="onSwipeMove(toast.id, $event)"
      @touchend="onSwipeEnd(toast.id)"
      @touchcancel="onSwipeEnd(toast.id)"
    >
      <!-- Generic app message (offline notices, background/queued analysis results).
           When an orderLabel is given it renders in the SAME card layout as a rider check-in
           (tag + big order id + a body line), so staff read it as the same toast family.
           Plain text (no orderLabel) is the icon and one line. -->
      <template v-if="toast.kind === 'message'">
        <span class="bz-toast__icon" :class="`is-msg-${toast.variant}`"><BzIcon :name="appToastIcon(toast.variant)" /></span>
        <span v-if="toast.orderLabel" class="bz-toast__body">
          <span class="bz-chip rider-toast-tag" :class="{ 'bz-chip--ok': toast.variant === 'success', 'bz-chip--warn': toast.variant === 'warning' }">{{ toast.tag }}</span>
          <span class="bz-toast__code bz-toast__code--sm" data-testid="rider-toast-order-id" translate="no">{{ toast.orderLabel }}</span>
          <span v-if="toast.body" class="bz-toast__text rider-toast-msg-body" data-testid="rider-toast-body">
            <template v-if="toast.bodySegments && toast.bodySegments.length">
              <span v-for="(segment, segIndex) in toast.bodySegments" :key="segIndex" :translate="segment.data ? 'no' : null">{{ segment.text }}</span>
            </template>
            <template v-else>{{ toast.body }}</template>
          </span>
        </span>
        <span v-else class="bz-toast__body">
          <span class="rider-toast-msg-text" data-testid="rider-toast-text">{{ toast.text }}</span>
        </span>
      </template>

      <!-- A rider: the tag is a capsule at 14 (never capitals with tracking), the minutes waiting at its right, and
           the code under them, the whole width of the card: a 12-digit code at 36 does not fit beside them.
           The urgency (≥10 min) is told by the red, the border and the words — nothing pulses (norm 1). -->
      <template v-else>
        <span class="bz-toast__icon"><BzIcon :name="toast.type === 'delivered' && toast.via !== 'scan' ? 'check_circle' : 'delivery_dining'" /></span>
        <span class="bz-toast__body">
          <span class="rider-toast-meta">
            <span class="bz-chip rider-toast-tag" :class="toast.type === 'delivered' ? 'bz-chip--ok' : 'bz-chip--rider'">{{ toastTitle(toast) }}</span>
            <span v-if="toast.type !== 'delivered'" class="bz-toast__aside rider-toast-wait" data-testid="rider-toast-wait" :class="`urgency-${waitUrgency(toast.waitMinutes)}`">
              <BzIcon name="schedule" />
              {{ toast.waitMinutes === 0 ? $t('message.riderJustArrived') : $t('message.riderWaitMinutes', { minutes: toast.waitMinutes }) }}
            </span>
          </span>
          <span class="bz-toast__code" data-testid="rider-toast-order-id" translate="no">{{ toast.primaryId }}</span>
          <span v-if="toast.secondaryId || toast.platform" class="bz-toast__text rider-toast-sub">
            <span v-if="toast.secondaryId" data-testid="rider-toast-secondary-id" translate="no">{{ toast.secondaryId }}</span>
            <span v-if="toast.platform" class="rider-toast-platform">
              <img :src="getPlatformIcon(toast.platform)" alt="" class="rider-toast-platform-icon" />
              <span data-testid="rider-toast-platform">{{ platformLabel(toast.platform) }}</span>
            </span>
          </span>
        </span>
      </template>

      <!-- Countdown line — duration matches this toast's auto-dismiss (rider 8s, messages shorter) -->
      <div class="rider-toast-progress" aria-hidden="true">
        <div
          class="rider-toast-progress-fill"
          :class="toast.kind === 'message' ? `is-msg-${toast.variant}` : `is-${toast.type}`"
          :style="{ animationDuration: ((toast.dismissMs || 8000) / 1000) + 's' }"
        ></div>
      </div>
    </div>
  </div>

</template>

<script>
import { useI18n } from 'vue-i18n';
import LoginScreen from './components/LoginScreen.vue';
import CameraScreen from './components/CameraScreen.vue';
import SettingsScreen from './components/SettingsScreen.vue';
import PeriscopeTutorialScreen from './components/PeriscopeTutorialScreen.vue';
import QrGunTutorialScreen from './components/QrGunTutorialScreen.vue';
import PhotoVideoTutorialScreen from './components/PhotoVideoTutorialScreen.vue';
import QueueScreen from './components/QueueScreen.vue';
import HelpScreen from './components/HelpScreen.vue';
import ChangelogComponent from './components/ChangelogComponent.vue';
import NotificationComponent from './components/NotificationComponent.vue';
import PhotoCoverageAlertComponent from './components/PhotoCoverageAlertComponent.vue';
// The one component of the system the critical lane may draw (an <svg> with one path): the toasts' icons.
import BzIcon from '@/ui/components/BzIcon.vue';
import GuidedFlowModal from './components/GuidedFlowModal.vue';
import {
  STEP as FLOW_STEP, EFFECT as FLOW_EFFECT, OUTCOME as FLOW_OUTCOME, FOLLOW_UP,
  createFlow, chooseTopic, pickSubject, answerConfirm, retryEscalation, submitText, askAboutSubject, submitCode,
  goBack as flowGoBack, dismiss as flowDismiss, resumeFlow, followUpDecision,
  savePendingRemedy, takePendingRemedy, clearPendingRemedy,
} from './guidedFlow.js';
import {
  PHOTO_HISTORY_PAGE, describePhotoOrder, photoHistoryFromServer, orderHistoryFromServer, incorrectFirst,
  appendPhotoPage, canLoadMorePhotos, photoTimeLabel, parseServerTime,
} from './orderPhotos.js';
import {
  HELP_FLOW_ID, HELP_REMEDY, HELP_TOPICS, HELP_SURFACE, findHelpTopic, describeOrder,
  describePlatform, helpCandidates, orderHelpTopics, snapshotHelpContext, composeHelpMessage,
  helpEvidence, isMessengerReady,
} from './helpFlow.js';
import { rememberLastLogin, readLastLogin } from './loginHelp.js';
import {
  periscopeTutorialOnLogin, readPeriscopeTutorialState, writePeriscopeTutorialState, stateAfterClose, trackPeriscope,
} from './periscopeTutorial.js';
import {
  isQrGunTutorialClient, classifyTutorialScan, readQrGunResume, clearQrGunResume, trackQrGun,
} from './qrGunTutorial.js';
import { photoVideoFor } from './photoVideoTutorial.js';
import { takeTutorialLinkFromLocation, catchTutorialLinks, TUTORIAL_LINKS } from './tutorialLink.js';
import { platformName } from './platformNames.js';
import { findAiWrongReason, missingItemNames, aiWrongEventMetadata, AI_WRONG_EVENT } from './aiWrongFlow.js';
import {
  focusIntercomComposer, startIntercomVoiceNote, replyInIntercomConversation, messengerShown, messengerHasLine,
} from './intercomComposer.js';
import {
  INTERCOM_APP_ID, INTERCOM_REGION, CONTINUE_WINDOW_MS, conversationToContinue, rememberConversation, forgetConversation,
  conversationsBefore, messengerConversations, waitForNewConversation,
} from './intercomConversation.js';

import { setupBackgroundUploader, startBackgroundUploader, uploadPendingPhotos, isUploadInProgress } from './backgroundUploader.js';
import { initAckQueue, stopAckQueue } from './ackQueue.js';
import { initDeviceEvents, stopDeviceEvents } from './deviceEvents.js';
import { initForensic, stopForensic, reportForensicEvent, getPageId, getPhotosSinceLoad } from './forensicEvents.js';
import {
  startLoafObserver, stopLoafObserver, loafSince, collectDeviceProfile, appStartProps,
  writeTombstone, storageUsageMb, heapMb, domNodes, cpuBenchMs,
} from './perfTelemetry.js';
import { getSharedAudioContext, reportAudioPlaybackOk, reportAudioPlaybackBlocked } from './audio.js';
import {
  initEventLedger, registerChannel, onPushEvent, resyncChannel, stopEventLedger, seedCursor,
  getSocketProbe, registeredChannelCount, recoverEventsSocket, noteChannelRejected, getCursor,
} from './eventLedger.js';
import { decideSeqHint } from './seqHint.js';
import { createOrdersQueryGuard } from './ordersQueryGuard.js';
import { isDecidedAnalysis } from './verdictState.js';
import {
  WATCHDOG_TICK_MS, resumePlan, evaluateSocketRules, isSocketOpen, isProbeDead, firstActionDelayMs,
  spendRecovery, DAILY_RECOVERY_CAP, ZOMBIE_KA_AGE_MS, ZOMBIE_RESUME_HOLDOFF_MS, socketHealthFields,
  planOnlineReturnRebuild, connectUnlessStale, planRecoveryAttempt,
} from './socketWatchdog.js';
import { executeGraphQLQuery, fetchRawOrdersDoc, fetchRawOrdersWideDoc, fetchOrderForDuplicateDoc, fetchRestaurantDoc, classifyCurrentDevice, fetchDeviceSettingsDoc, setDeviceSetting, fetchDeliveryPlatformsForCurrentDevice, fetchOwnDeliveryCodePresets, registerRiderLeft, recordRiderLeft, fetchRecentOrderPhotos, fetchRecentOrdersWithoutPhoto, fetchOrderPhotos } from './graphql.js';
import { reduceDeviceSettings, diffSettingsToParameterWrites, planFirstRunMigration } from './deviceSettings.js';
import { getOrderFromIndexedDB, openDatabase } from '@/utils';
import { saveOrderToIndexedDB, retry, withTimeout, getPhotosByOrderId, resolveOrderAnalysisId } from '@/utils';
import { initAnalysisQueue, stopAnalysisQueue } from './analysisQueue.js';
import { lookupDuplicate, rawRowFromOrder, resolveManualDuplicate } from './manualDuplicate.js';
import { analysisResultToast, extractMissingItems } from './analysisSummary.js';
import { version } from '../package.json';

import Logger from '@/logger';
import posthog from 'posthog-js';
import { liveDemoMode, liveDemoOn, isLiveDemoId, enforceLiveDemoGate } from './liveDemo/flag';
import { events } from 'aws-amplify/data';
import { uiStartupTelemetry } from './ui/install';
import { startTranslationTelemetry, stopTranslationTelemetry } from './translationTelemetry';
import { startQrScanner } from './qrScanner';


const logger = new Logger({ screen: 'App' });

// Amplify ConnectionStates in which the shared Events WebSocket cannot deliver events.
// Amplify's keep-alive watchdog ticks every 5s and reports the socket unhealthy after 65s
// without a `ka` ('ConnectedPendingKeepAlive'), then closes it after connectionTimeoutMs
// (5 min by default for the Events provider) — which surfaces as 'ConnectionDisrupted'.
// Neither step errors or completes the per-channel Observables, which is why the app has
// to read these states instead of `subscription.closed`.
// 'ConnectionDisruptedPendingNetwork' is deliberately absent: the OS interface is down, so
// reconnecting is futile and the 'online' listener already handles the return.
// The ONE Amplify ConnectionState that means "Amplify still believes this socket is
// connected, yet nothing is arriving on it" — the zombie. Amplify's watchdog raises it
// after 65 s without a single frame (`ka` included), and it is always the FIRST state a
// silent death produces, before the 5-min timeout turns it into 'ConnectionDisrupted'.
//
// 'ConnectionDisrupted' / 'Disconnected' / 'Connecting' are deliberately NOT actionable:
// Amplify cycles the socket through them as a normal part of its lifecycle (idle
// keep-alive, backgrounding, silent reconnects), frequently enough on healthy restaurant
// networks that gating anything on them caused a fleet-wide false-offline outage (see the
// computeIsOnline comment in eventLedger.js). Reacting to them would tear down and rebuild
// five healthy channels on a routine event. When the socket really is dead we get
// 'ConnectedPendingKeepAlive' first anyway, so nothing is lost by ignoring them.
const DEAF_SOCKET_STATE = 'ConnectedPendingKeepAlive';

// How long the unhealthy state must PERSIST before we spend anything on it. A `ka` that
// merely arrives late trips the state and clears it seconds later; a dead socket stays.
// Without this debounce every late keep-alive on a healthy tablet would cost a full
// five-channel re-init (~6 GraphQL requests), which across ~440 tablets is exactly the
// traffic multiplication this design exists to avoid. Detection is then ~65 s + this.
const DEAF_CONFIRM_MS = 30000;

// Backoff for repeated recovery attempts within one session. A tablet whose socket cannot
// be revived would otherwise re-init every ~95 s forever (~230 GraphQL requests/hour, on
// the one device least able to make use of them).
// The backoff itself (30 s, doubling, max 10 min, each step ±25%) is planRecoveryAttempt in socketWatchdog.js. The
// local socket watchdog (checkSocketTruth) shares it and the one-at-a-time guard with this
// Hub-driven path, so the two together never rebuild more often than that.

// Types of events - using an enum for better type safety and maintainability
const EventTypes = {
  NEW_ORDER: 'new_order',
  ORDER_UPDATE: 'order_update',
  PHOTO_CREATED: 'photo_created',
  ORDER_ANALYSIS: 'order_analysis',
  ITEM_CROSS_OUT: 'item_cross_out'
};

// Photo coverage alert thresholds (local kitchen nudge). Conservative defaults to
// avoid false positives — one slow order is normal, several at once is a real lapse.
// Candidates to move to remote config so they can be tuned per client without a deploy.
// Guided help flow timings (ms). After a remedy reload, the follow-up question waits for the
// orders to have re-arrived, retries while an order is being photographed, and gives up on a
// question that has gone stale.
const HELP_FLOW = {
  // La pregunta tras el reinicio es lo PRIMERO que se ve al volver: con "no me aparece un pedido"
  // basta un vistazo a la lista para saber si funcionó, así que se pregunta pronto. El chip de
  // evidencia es reactivo (ver la computed helpFlowEvidence), así que si los pedidos llegan un
  // segundo más tarde el dato se actualiza solo en vez de mentir.
  FOLLOWUP_DELAY_MS: 3000,
  FOLLOWUP_RETRY_MS: 5000,
  FOLLOWUP_DEADLINE_MS: 10 * 60 * 1000,
  // The resumed question closes itself if nobody answers: the kitchen never asked for a modal
  // at THAT moment, and a fixed problem nobody confirms must not hold the screen hostage.
  CONFIRM_TIMEOUT_MS: 60 * 1000,
  // El aviso y su segunda línea tienen que LEERSE antes de que la pantalla se vaya: la transición
  // de paso ya come ~240 ms, así que esto deja el texto visible unos dos segundos. Sin el aviso, la
  // app parecía reiniciarse sola tras el toque; sin la línea, los diez o veinte segundos de
  // pantalla negra de una tablet barata parecían una tablet muerta.
  RELOAD_FEEDBACK_MS: 2200,
  // "Ver las fotos": volver de una foto a la lista dentro de este plazo la reutiliza tal cual. Una
  // lista recargada puede traer un pedido nuevo arriba y mover las baldosas bajo el dedo, que es
  // justo como un toque acaba abriendo otro pedido; pasado el plazo se pide de nuevo, con su espera.
  PHOTOS_LIST_FRESH_MS: 30 * 1000,
};

// Devuelto por helpPickItems cuando el paso no es PICK: una referencia estable, para que una
// invalidación no propague un array nuevo al modal.
const NO_PICK_ITEMS = Object.freeze([]);

// "Quiero ver las fotos de un pedido": la lista de lo ya fotografiado y el pedido que se mira.
// `seq` invalida cualquier respuesta que llegue después de cerrar el modal o de pedir otra cosa.
// `source` is which list it is ('photos' or 'no_photo') and `key` that list in the order a topic
// shows it: a list fetched for one topic is not the answer to another.
const emptyHelpPhotoList = (seq = 0) => ({
  status: 'idle', orders: [], offset: 0, lastPageSize: 0, loadingMore: false, fetchedAt: 0, seq, source: null, key: null,
});
const emptyHelpPhotoView = (seq = 0) => ({
  status: 'ready', order: null, from: null, openedAt: 0, firstReported: false, seq,
});

const COVERAGE_ALERT = {
  THRESHOLD: 3,             // N overdue orders that trigger the alert
  // Floor for "this order is overdue for a photo". 8 min sat ON the network median time from
  // order to first photo (8.3 min over 51.872 photos / 401 stores), so 52% of perfectly normal
  // orders were overdue by definition and the alert fired on healthy operation — the Goiko
  // BCNPM complaint (its own median is 10.9 min, and every order did get photographed).
  // 15 leaves ~81% of the normal flow alone, and since 3 must pile up at once the false
  // positive becomes rare. Per-client overrides on top of this floor are the next step:
  // rhythm varies as much within a client as between clients (KFC 6.7 min, Telepizza 16.5).
  GRACE_MIN: 15,            // an order counts as overdue after this many min without a photo
  MAX_MIN: 60,              // ignore stale orders older than this (they never age out locally)
  SNOOZE_MIN: 10,           // "Too busy right now" → re-remind after this
  COOLDOWN_ACK_MIN: 20,     // "Got it" → don't re-nag for this long (also the promise window)
  COOLDOWN_ACK_REPEAT_MIN: 10, // ...but a "yes" that was already broken buys half the peace
  COOLDOWN_SUPPORT_MIN: 30, // "We have a problem" → support engaged, back off longer
  REARM_MIN: 5,             // after any close without an answer, hold off before re-firing (anti-flap)
  // The banner never outstays its welcome: unanswered, it closes itself after this long.
  // The KFC Gaudí forensics (aug-2026) showed the old blocking sheet sat for 122 min on
  // average and photos simply stopped while it was up — the alert must never be the thing
  // standing between the operator and the camera.
  AUTO_DISMISS_MIN: 2,
  // A tap-anywhere dismissal this fast almost certainly means "I was already touching the
  // screen", not "I read it and chose to ignore it" — refund the budget slot it spent (the
  // REARM hold-off still applies, so it comes back in a few minutes instead of never).
  DISMISS_REFUND_SEC: 10,
  MAX_SOUND_LEVEL: 2,       // ceiling for the escalation: insistent, never a siren
  // Budgets. Past these the tablet stops asking: a store that needs this many nudges has a
  // problem no nudge can fix, and it becomes a manager signal (fx_coverage_capped) instead.
  MAX_PER_SHIFT: 2,         // alerts allowed inside a rolling SHIFT_WINDOW_H window
  SHIFT_WINDOW_H: 6,        // rolling, not clock shifts — no schedules or timezones to configure
  MAX_PER_DAY: 6            // alerts allowed in a rolling 24 h window
};

// The alert budget must survive a reload: PWA updates, manual refreshes and crashes are most
// frequent exactly in the struggling stores the budget exists to protect, and in-memory
// counters would reset there every time. Keyed by restaurant so a different device logging
// into the same tablet starts clean.
const COVERAGE_HISTORY_KEY = 'coverageAlertHistory';

function loadCoverageHistory(restaurantId) {
  try {
    const raw = JSON.parse(window.localStorage.getItem(COVERAGE_HISTORY_KEY) || 'null');
    if (!raw || raw.restaurantId !== restaurantId || !Array.isArray(raw.ts)) return [];
    const cutoff = Date.now() - 24 * 3600 * 1000; // anything older can't bind either window
    return raw.ts.filter(t => typeof t === 'number' && t > cutoff);
  } catch (e) {
    return [];
  }
}

function saveCoverageHistory(restaurantId, ts) {
  try {
    window.localStorage.setItem(COVERAGE_HISTORY_KEY, JSON.stringify({ restaurantId, ts }));
  } catch (e) {
    // Private mode / quota: the budget degrades to in-memory, never breaks the alert.
  }
}

// --- Startup resilience ------------------------------------------------------
// The restaurant fetch (currentDevice) is the last network call standing between
// the "Cargando..." screen and CameraScreen. Without a timeout + retry, any slow
// or failed AppSync response left the app stuck on the loading spinner forever.
// Session-health cadence. The tick runs every 5 min, but a session with no new photos since the
// last beat only reports every 30: these tablets are powered 24/7 and most of that is an empty
// kitchen, so a flat 5-minute beat would be two thirds night-time noise for no added signal.
const PERF_HEARTBEAT_MS = 5 * 60 * 1000;
const PERF_IDLE_HEARTBEAT_MS = 30 * 60 * 1000;
const FOREGROUND_SYNC_MIN_INTERVAL_MS = 30_000; // a tab that flips visible every few seconds must not refetch every time
const RESTAURANT_FETCH_TIMEOUT_MS = 10000; // per-attempt timeout
const RESTAURANT_FETCH_ATTEMPTS = 3;       // total attempts before giving up
const RESTAURANT_FETCH_RETRY_DELAY_MS = 2000;
// An answer of «no such device» is checked once more before the session is dropped: signing a
// tablet out costs a trip to the PIN, so one odd null must not do it.
const DEVICE_MISSING_RECHECK_MS = 1500;
const SLOW_STARTUP_MS = 8000;              // above this we flag the startup as slow in telemetry

export default {
  name: 'App',
  components: {
    LoginScreen,
    CameraScreen,
    SettingsScreen,
    PeriscopeTutorialScreen,
    QrGunTutorialScreen,
    PhotoVideoTutorialScreen,
    QueueScreen,
    HelpScreen,
    ChangelogComponent,
    NotificationComponent,
    PhotoCoverageAlert: PhotoCoverageAlertComponent,
    GuidedFlowModal,
    BzIcon
  },
  setup() {
    const { locale, availableLocales } = useI18n();
    return {
      i18nLocale: locale,
      i18nAvailableLocales: availableLocales
    };
  },
  provide() {
    return {
      db: () => this.db,
      // Reactive connectivity getter (same pattern as db): consumers inject `isOnline`
      // and call it in a computed/template. Optimistic true until proven offline.
      isOnline: () => this.isOnline,
      // Non-blocking toast API for children (e.g. CameraScreen's offline flow):
      // notify({ text, variant?, duration? }). Returns the toast id.
      notify: (payload) => this.showAppToast(payload),
      // Children call this right before/after showing a verdict in the foreground, so the
      // verdict-transition detector below won't also toast the same order.
      markForegroundVerdict: (orderId) => this.markForegroundVerdict(orderId),
      // Undo the mark — used when a foreground analysis is instead sent to the background
      // during upload, so its queued result IS toasted by the detector.
      unmarkForegroundVerdict: (orderId) => this.unmarkForegroundVerdict(orderId),
      // The order currently open in the camera capture screen. While it is open the operator is
      // still building it, so its auto/realtime verdict must NOT toast ("faltan productos" while
      // taking photos). Persistent (unlike the one-shot foreground mark): set on open, cleared on
      // leave/unmount.
      setOpenCameraOrder: (orderId) => this.setOpenCameraOrder(orderId),
      clearOpenCameraOrder: () => this.clearOpenCameraOrder(),
      // "This manual order already exists": find it (repairing the tablet's own state on the way)
      // and hand it back for CameraScreen to open. See handleManualDuplicate.
      handleManualDuplicate: (target) => this.handleManualDuplicate(target)
    };
  },
  data() {
    return {
      currentComponent: 'LoginScreen',
      previousComponent: null,
      // Where the periscope tutorial was opened from ('first_login' | 'settings' | 'link'), so closing
      // it returns to the right screen; null while it is not open.
      periscopeTutorialSource: null,
      // The QR gun tutorial (docs/tutorial-pistola-qr.md): where it was opened from ('settings' | 'resume'),
      // the step it starts at, and the last read of the gun while it is open (`seq` counts them) — no order
      // is closed meanwhile (see onQrScan). null while it is not open.
      qrGunTutorialSource: null,
      qrGunTutorialStep: null,
      qrGunScanEvent: { seq: 0, kind: null },
      // A tutorial link (src/tutorialLink.js: 'periscope' | 'good-photos') arrived before the session was
      // up: the tutorial opens as soon as login succeeds.
      tutorialLinkPending: null,
      // While the messenger is shown, its frames are re-armed to catch taps on the tutorial links.
      tutorialLinkTimer: null,
      // Where «Cómo hacer buenas fotos» was opened from ('settings' | 'link'): where its close goes back to.
      photoVideoSource: null,
      // Performance telemetry bookkeeping. Plain state, never rendered: the heartbeat timer, the
      // visibilitychange handler it installs, and the marks that decide whether a tick is worth
      // reporting (see startPerfTelemetry).
      perfHeartbeat: null,
      perfOnHide: null,
      perfHeartbeatSince: 0,
      perfLastActivityPhotos: -1,
      perfLastBeatAt: 0,
      perfVisChanges: 0,           // visibilitychange / online events since the last session_health beat
      perfOnlineChanges: 0,
      lastForegroundSyncAt: 0,     // ms; the last foreground refetch + dead-connection check that actually ran
      // Startup lifecycle, surfaced to LoginScreen so it can show a retry affordance
      // instead of a mute, infinite spinner. 'idle' | 'fetching' | 'failed' | 'ok'
      startupStatus: 'idle',
      restaurant: null,
      rawOrders: [],
      processedOrders: [],
      db: null,
      cameraPermissions: false,
      settings: {
        flipVertically: false,
        resolution: 1920,
        immersiveViewport: true
      },
      // Last config we know the backend holds (set on login/reconfigure reads); user edits are
      // diffed against this so a debounced flush only pushes the real delta.
      deviceSettingsBaseline: {},
      deviceSettingsSyncTimer: null,
      deviceId: null,
      appVersion: version,
      eventsChannels: {},
      isRealTimeConnected: {},
      // Last ConnectionState Amplify reported for the shared Events WebSocket, and the last
      // time a DATA frame was delivered to a channel subscriber. The provider forwards only
      // `type: 'data'` to observer.next: `ka` frames never reach the app, so this stamp says
      // nothing about keep-alives. The socket's own keepAliveTimestamp (every ka AND data
      // frame) comes from getSocketProbe(). Amplify never errors the per-channel Observables,
      // so `subscription.closed` cannot be trusted either (see isConnectionAlive).
      socketState: null,
      lastSocketMessageAt: null,
      channelSubscribedAt: {},     // channel name -> ms when its current subscription was (re)established (seq hint)
      socketWatchdogTimer: null,
      deafSince: null,      // ms timestamp of the CONFIRMED deaf episode, null when healthy
      deafVia: null,        // what opened the episode: 'hub_state' | 'watchdog' | 'manual_duplicate' | 'seq_hint'
      deafRule: null,       // the watchdog rule that opened it (dead | zombie | aged | bomb)
      deafRecoveryStartedAt: null, // ms when the last rebuild began
      deafRecoverySocketSeq: null, // the probe's socketSeq then: only a frame from a LATER socket proves recovery
      nextDeafRecoveryAt: 0,       // ms; no recovery before this (jittered backoff, shared by every path)
      watchdogActAt: null,         // ms; the jittered moment the watchdog may first act on what it sees
      zombieHoldoffUntil: null,    // ms; ZOMBIE is not judged while the app is resuming
      lastWatchdogTickAt: null,
      reconnectingDead: false,     // a checkAndReconnectDeadConnections run is in flight
      // Bumped by every teardown (session expiry, sign-out): a rebuild or channel init that started
      // under an older epoch stops instead of subscribing behind the logout.
      realtimeEpoch: 0,
      // True once handleLoginSuccess has started the upstream queues (ACKs, ledger): only then may
      // the channels register. See the `restaurant` watcher.
      realtimeReady: false,
      watchdogCappedDay: null,     // local day on which fx_conn_watchdog_capped was already emitted
      deafSuspectedAt: null, // unhealthy seen, not yet confirmed (debounce window)
      deafConfirmTimer: null,
      deafRecovering: false,
      lastDeafRecoveryAt: null,
      deafRecoveryAttempts: 0,
      // Global connectivity, fed by the event ledger's Events-socket state + navigator.onLine.
      // Optimistic default (true) so nothing reads "offline" before the ledger initialises.
      isOnline: true,
      // orderId -> timestamp of a verdict just shown to the employee in the foreground
      // (analysis modal / manual mark-correct). The verdict-transition detector consults this
      // to avoid double-signalling: a foreground result is already visible, so don't also toast.
      foregroundVerdictOrders: {},
      // orderId currently open in the camera capture screen (set by CameraScreen.selectOrder,
      // cleared on clearSelectedOrder/beforeUnmount). While set, both verdict-toast paths skip
      // THIS order — the operator is still capturing it, so a premature "faltan productos" toast
      // is noise; they see the verdict when they press Analizar. The tile still colours from
      // orderAnalyses regardless. Not consumed on read (unlike foregroundVerdictOrders).
      openCameraOrderId: null,
      // analysisId -> timestamp of a verdict already toasted. A verdict can reach the client
      // by two paths that race — the queue relaunch's HTTP response (handleQueuedVerdict, with
      // the missing-item detail) and the realtime order_analysis event (generic fallback). This
      // dedups them by analysisId so a single toast fires, and the detailed one wins.
      verdictToasted: {},
      resyncHeartbeatInterval: null,
      pollingIntervals: {},
      // Cleanup fn returned by startBackgroundUploader, so an expired session can
      // actually stop the 15s upload loop (and re-login doesn't stack a second one).
      stopBackgroundUploader: null,
      // Re-entry guard: several loops can hit the expired session in the same tick
      // and each dispatches 'sessionExpired'; only the first one must run the teardown.
      sessionExpiredHandled: false,
      subscriptions: {},
      channelHandlers: {},
      currentNotification: null,
      notificationQueue: [],
      backgroundTimestamp: null,
      availableCameras: [],
      streamInfo: null,
      riderToastQueue: [],
      riderToastCounter: 0,
      riderToastTimers: {},
      riderReminderTracked: {},
      riderDeliveredOrderIds: {},
      // Orders closed by the QR gun whose RIDER_LEFT never reached the backend: written again on the next
      // scan of the same ticket or when the network comes back. Id → its primary code, for the toast.
      riderLeftUnsaved: {},
      scansInFlight: {},
      swipeStates: {},
      lastRiderSoundAt: 0,
      // --- Photo coverage alert (100% local detection; bottom-up nudge) ---
      // coverageAlert.visible doubles as the "is an alert active" flag (single source of truth).
      coverageAlert: { visible: false, count: 0 },
      // --- Guided help flow (help button → one-tap topics → automatic remedy → contextual ticket) ---
      helpFlow: null,           // guidedFlow.js state while the modal is up; null = closed
      // Estado REAL de la cámara, elevado desde CameraPreviewComponent:
      // { ready, permission, fault, code }. Antes el mensaje de soporte leía `cameraPermissions`,
      // que se declaraba `false` y NADIE asignaba nunca, así que un tique de "no puedo hacer la
      // foto" decía "Cámara: sin permiso" con la cámara funcionando y mandaba a soporte a mirar
      // los permisos de Android en vez del fallo real.
      helpCameraState: null,
      // The order list CameraScreen is actually painting. `processedOrders` is not the same thing:
      // a manual order lives only inside CameraScreen until the backend returns it, so a kitchen
      // that had just created one was offered a help list without it (and got it only after a
      // reload). This is the list the person is looking at, which is the one the flow must offer.
      helpOrdersOnScreen: [],
      // Las agregadoras del restaurante, para preguntar de cuál era el pedido que no aparece.
      // Mismo origen (y misma caché con TTL) que el selector del pedido manual: la lista la manda
      // el backend por dispositivo, así que aquí no se inventa ninguna plataforma.
      helpPlatforms: [],
      // Las fotos de pedidos que ya salieron (src/orderPhotos.js): la lista del paso PICK y el
      // pedido abierto en el paso VIEW. Solo se llenan cuando el modal las pide.
      helpPhotoList: emptyHelpPhotoList(),
      helpPhotoView: emptyHelpPhotoView(),
      // "Mandar una nota de voz": decidido al abrir el modal (flag + que el navegador pueda grabar).
      helpVoiceEnabled: false,
      // Cuenta las entregas a Intercom: si se solapan dos, solo la última apunta la conversación.
      helpDeliverySeq: 0,
      helpResumeTimer: null,    // pending "ask after the reload" timer
      // La pregunta del remedio que está esperando su momento. Vive aquí, y no solo dentro del
      // temporizador, porque el botón de Ayuda tiene que poder adelantarla: al volver de un
      // reinicio lo normal es mirar la lista, ver que sigue sin haber pedidos y volver a pulsar
      // Ayuda antes de que aparezca la pregunta. En ese caso enseñarle la parrilla otra vez es
      // hacerle empezar de cero cuando lo que hay que hacer es cerrar el bucle que ya está abierto.
      helpPendingFollowUp: null, // { record, topic, deadline } | null
      helpConfirmTimer: null,   // auto-close of a resumed, unanswered "did that fix it?"
      // Changelog auto-open gate: true only when the kitchen is idle (no order waiting
      // for a photo, no coverage alert). Re-evaluated on the 30 s interval, so the modal
      // shows on the morning boot (empty list) and never lands mid-shift.
      changelogGateOpen: false,
      // Bumped by Ajustes → "Novedades"; the modal watches it and re-opens on demand.
      changelogOpenRequests: 0,
      coverageSnoozedUntil: 0,      // suppress until this ms timestamp (after "busy")
      coverageCooldownUntil: 0,     // suppress until this ms timestamp (after ack/support/re-arm)
      coverageAlertShownAt: 0,      // when the current alert appeared (for minutes_shown)
      coverageAutoCloseArmedAt: 0,  // when the auto-close timer was (re)armed — drives the bar
      coverageAlertStep: 'prompt',  // which step the banner is on (for resolved telemetry)
      coverageVariant: null,        // 'banner' | 'shadow' (experiment arm) | null = off
      coverageRecentLocalPhoto: {}, // orderId -> ts of a photo taken locally but maybe not yet
                                    // confirmed by the backend (avoids false "sin foto" on slow uploads)
      coverageAckAt: 0,             // when they answered "yes" — watch for recovery to thank them
      coverageAckUntil: 0,          // deadline of that promise (= the ack cooldown)
      coverageAckBroken: 0,         // consecutive "yes" promises that expired without recovery.
                                    // Drives the escalation: shorter peace + insistent sound.
      coverageThanksVisible: false, // green "photos back on track" toast (positive reinforcement)
      isPhotographing: false,       // an order is selected / camera open → hide the banner so it
                                    // never covers the shutter (the very action we're asking for)
      coverageHistory: [],          // ms timestamps of alerts shown (rolling 24 h, persisted)
      coverageCappedScope: null     // 'shift' | 'day' while a budget is spent (emit the event once)
    };
  },
  computed: {
    // Deleito's clients use the Bluetooth QR gun: only they (and Bronze's own tablets) see its tutorial in Ajustes (src/qrGunTutorial.js).
    showQrGunTutorial() {
      return isQrGunTutorialClient(this.restaurant?.client);
    },
    // «Cómo hacer buenas fotos»: the video of the language running, or null (no row) when it has none yet.
    photoVideo() {
      return photoVideoFor(this.i18nLocale);
    },
    visibleRiderToasts() {
      return this.riderToastQueue.slice(0, 2);
    },
    waitingRidersCount() {
      if (!this.processedOrders) return 0;
      return this.processedOrders.filter(o =>
        !this.riderDeliveredOrderIds[o.orderId] &&
        o.order?.orderEvents?.some(e => e.event === 'RIDER_CHECK_IN')
      ).length;
    },
    coverageAutoDismissMs() {
      return COVERAGE_ALERT.AUTO_DISMISS_MIN * 60000;
    },
    // ---- Guided help flow ----
    helpFlowVisible() {
      return Boolean(this.helpFlow) && this.helpFlow.step !== FLOW_STEP.CLOSED;
    },
    // El orden de los temas depende de dónde se pulsó Ayuda: con un pedido abierto en la cámara,
    // "no me aparece un pedido" no es lo que está pasando.
    helpTopics() {
      const topics = this.helpVoiceEnabled ? HELP_TOPICS : HELP_TOPICS.filter((topic) => !topic.voice);
      return orderHelpTopics(topics, this.helpSurface);
    },
    helpSurface() {
      if (this.currentComponent !== 'CameraScreen') return HELP_SURFACE.OTHER;
      return (this.openCameraOrderId || this.isPhotographing) ? HELP_SURFACE.ORDER : HELP_SURFACE.ORDERS;
    },
    helpPickTitleKey() {
      if (this.helpFlow?.topic?.pickTitleKey) return this.helpFlow.topic.pickTitleKey;
      if (this.helpFlow?.topic?.view) return 'message.helpPhotosPickTitle';
      if (this.helpFlow?.topic?.subject === 'platform') return 'message.helpFlowPickPlatformTitle';
      return this.helpFlow?.topic?.multi ? 'message.helpFlowPickOrdersTitle' : 'message.helpFlowPickTitle';
    },
    helpPickHintKey() {
      return this.helpFlow?.topic?.multi ? 'message.helpFlowPickOrdersHint' : null;
    },
    helpPickNoneKey() {
      return this.helpFlow?.topic?.subject === 'platform'
        ? 'message.helpFlowPickPlatformNone'
        : 'message.helpFlowPickNone';
    },
    // The way out of a list that failed to load: where the code can be typed, that — the same place
    // "not in the list" leads; otherwise, as before, asking support for the photos.
    helpPickFallbackKey() {
      return this.helpFlow?.topic?.askCode ? 'message.helpFlowPickTypeCode' : 'message.helpPhotosAskSupport';
    },
    helpPickEmptyKey() {
      if (!this.helpIsFetchedPick) return null;
      return this.helpFlow.topic?.fetch === 'no_photo' ? 'message.helpFlowPickNoPhotoEmpty' : 'message.helpPhotosEmpty';
    },
    // Every question about ONE order is asked the same way: two columns of the same tile. The
    // multi-select list keeps its one column (its tile carries a check, and on a phone two columns
    // would squeeze it out), and a list of aggregators is not a list of orders.
    helpPickTwoColumns() {
      const topic = this.helpFlow?.topic;
      return this.helpFlow?.step === FLOW_STEP.PICK && topic?.subject === 'order' && !topic.multi;
    },
    // The fact shown under "did that fix it?", recomputed as the app changes: the question opens
    // ~3 s after the reload and the orders may land a moment later, so a string frozen at open
    // time would tell the kitchen "no orders on screen" while the tiles fill up behind the modal.
    helpFlowEvidence() {
      if (this.helpFlow?.step !== FLOW_STEP.CONFIRM) return null;
      return helpEvidence(this.helpFlow.topic, this.buildHelpContext(), (key, ...rest) => this.$t(key, ...rest));
    },
    // The orders offered on "which order?": what the kitchen sees, with the verdict as a badge
    // so the red one — the one they are almost always talking about — is recognisable at a glance.
    helpPickItems() {
      // Only while the pick step is up: this walks every order (and resolves platform icons), and
      // App.vue re-renders on every processedOrders mutation whether or not the modal is open.
      if (this.helpFlow?.step !== FLOW_STEP.PICK) return NO_PICK_ITEMS;
      // Los pedidos del servidor (fotografiados, o sin foto): lo que salió de la pantalla también cuenta.
      if (this.helpIsFetchedPick) return this.helpPhotoList.orders.map((d) => this.photoPickItem(d));
      // Las agregadoras del restaurante cuando el tema pregunta por el canal del pedido.
      if (this.helpFlow.topic?.subject === 'platform') {
        return this.helpPlatforms.map((id) => ({
          id,
          label: this.platformLabel(id),
          sublabel: '',
          icon: this.getPlatformIcon(id),
          descriptor: describePlatform(id, (pf) => this.platformLabel(pf)),
        }));
      }
      // El tono es el color que ESE pedido lleva ahora en la lista de la cocina, no una paleta
      // aparte: misma regla que OrderSelectionComponent.getOrderClass — azul mientras no hay
      // veredicto, verde correcto, naranja incorrecto — y pizarra solo cuando la foto está
      // esperando análisis sin conexión, que es el único caso en que la lista lo pinta así.
      const badge = {
        incorrect: ['message.helpFlowBadgeIncorrect', 'orange'],
        correct: ['message.helpFlowBadgeCorrect', 'green'],
        pending: ['message.helpFlowBadgePending', this.isOnline ? 'blue' : 'slate'],
        none: ['message.helpFlowBadgeNoPhoto', 'blue'],
      };
      // La misma baldosa que la lista del servidor: agregadora, hace cuánto llegó y cuántas fotos.
      const rows = new Map(this.helpOrderSource().map((order) => [order.orderId, order]));
      return this.helpCandidateList(this.helpFlow?.topic).map((d) => ({
        id: d.orderId,
        label: d.code,
        sublabel: [
          d.platformName,
          this.photoTime(parseServerTime(rows.get(d.orderId)?.createdAt)),
          d.photoCount ? this.$t('message.helpPhotosCount', d.photoCount) : null,
        ].filter(Boolean).join(' · '),
        icon: d.platform ? this.getPlatformIcon(d.platform) : null,
        badgeKey: badge[d.verdict]?.[0] || null,
        badgeTone: badge[d.verdict]?.[1] || 'slate',
        descriptor: d,
      }));
    },
    // ---- The lists fetched from the server ("ver las fotos", and the topics about an order that
    // has usually left the screen: src/orderPhotos.js) ----
    helpIsFetchedPick() {
      return this.helpFlow?.step === FLOW_STEP.PICK && Boolean(this.helpFlow.topic?.view || this.helpFlow.topic?.fetch);
    },
    // Only the fetched list has states; a list read from the screen is always 'ready'.
    helpPickStatus() {
      if (!this.helpIsFetchedPick) return 'ready';
      return this.helpPhotoList.status === 'idle' ? 'loading' : this.helpPhotoList.status;
    },
    helpPickMore() {
      if (!this.helpIsFetchedPick || this.helpPhotoList.status !== 'ready') return 'none';
      if (this.helpPhotoList.loadingMore) return 'loading';
      return canLoadMorePhotos({ loaded: this.helpPhotoList.offset, lastPageSize: this.helpPhotoList.lastPageSize })
        ? 'available' : 'none';
    },
    helpViewItem() {
      if (this.helpFlow?.step !== FLOW_STEP.VIEW || !this.helpPhotoView.order) return null;
      return this.photoPickItem(this.helpPhotoView.order);
    },
    helpViewPhotos() {
      if (this.helpFlow?.step !== FLOW_STEP.VIEW || !this.helpPhotoView.order) return NO_PICK_ITEMS;
      return (this.helpPhotoView.order.photos || []).map((photo) => ({
        url: photo.url,
        timeLabel: this.photoTime(photo.takenAt),
      }));
    },
    // Single source of truth for "the coverage banner is on screen": the alert itself plus
    // the two surfaces that must never be covered (only CameraScreen, never mid-capture).
    coverageBannerVisible() {
      return this.coverageAlert.visible
        && this.coverageVariant === 'banner'
        && this.currentComponent === 'CameraScreen'
        && !this.isPhotographing;
    }
  },
  watch: {
    currentComponent(next) {
      // The lifted camera fault belongs to the CameraScreen that reported it; elsewhere it is stale.
      if (next !== 'CameraScreen') this.helpCameraState = null;
      // A screen change under an open help modal (session expiry, remote reconfigure) pulls the
      // ground from under the question — close it rather than ask about a screen that is gone.
      if (this.helpFlow && next !== 'CameraScreen') this.onHelpDismiss('screen_change');
    },
    restaurant(newVal) {
      // Login starts realtime itself, after the ledger and ACK queue init (the watcher would run
      // first and the channels would seed a ledger with no device id); later changes still re-init.
      if (newVal && (this.realtimeReady || __BRONZE_DEMO__)) this.initializeRealtimeHandling();
    },
    // Start the 8 s auto-dismiss countdown only once a toast becomes visible.
    // Without this, if 3 toasts are pushed at once the 3rd one's timer runs while it's still
    // hidden behind the first two and the user never sees it.
    visibleRiderToasts: {
      immediate: true,
      handler(newVisible) {
        for (const toast of newVisible) {
          if (!this.riderToastTimers[toast.id]) {
            this.riderToastTimers[toast.id] = setTimeout(() => {
              delete this.riderToastTimers[toast.id];
              this.dismissRiderToastWithAnimation(toast.id);
            }, toast.dismissMs || 8000);
          }
        }
      }
    }
  },
  created() {
    // Non-reactive: decides which overlapping full orders fetch may write the list (see ordersQueryGuard.js).
    this.ordersQueryGuard = createOrdersQueryGuard();
    window.addEventListener('swUpdated', this.handleSwUpdate);
    window.addEventListener('clearIndexedDB', this.clearDatabase);
    window.addEventListener('sessionExpired', this.handleSessionExpired);
    window.addEventListener('online', this.handleBackOnline);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('touchstart', this.handleHapticFeedback, { passive: true });
    // A Bluetooth QR gun reading the feedback QR of the ticket (Deleito's validated delivery, a test).
    this.stopQrScanner = startQrScanner({ onScan: (scan) => this.onQrScan(scan) });
    this.tutorialLinkPending = takeTutorialLinkFromLocation();
    if (!__BRONZE_DEMO__) {
      this.initializeIntercom();
      // Runs independent of login (Chrome can translate the login screen too), reusing
      // the same props app_startup sends. See src/translationTelemetry.js.
      startTranslationTelemetry(() => ({
        uiLocale: this.i18nLocale,
        browserLanguage: (typeof navigator !== 'undefined' && navigator.language) || null,
        restaurant: this.restaurant?.name || null,
        client: this.restaurant?.client || null,
        deviceId: this.deviceId,
        appVersion: this.appVersion,
      }));
    }
    // Check every 30s: re-emit reminder every 2 min for unprocessed orders with rider waiting,
    // and auto-deliver green orders whose rider has been waiting > 10 min.
    this.riderReminderInterval = setInterval(() => {
      this.checkRiderReminders();
      this.checkRiderAutoDelivery();
      this.checkPhotoCoverage();
      this.updateChangelogGate();
    }, 30000);
  },
  beforeUnmount() {
    window.removeEventListener('swUpdated', this.handleSwUpdate);
    window.removeEventListener('clearIndexedDB', this.clearDatabase);
    window.removeEventListener('sessionExpired', this.handleSessionExpired);
    window.removeEventListener('online', this.handleBackOnline);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('touchstart', this.handleHapticFeedback);
    if (this.stopQrScanner) this.stopQrScanner();
    if (this.riderReminderInterval) clearInterval(this.riderReminderInterval);
    if (this.tutorialLinkTimer) clearInterval(this.tutorialLinkTimer);
    this.stopResyncHeartbeat();
    for (const id in this.riderToastTimers) clearTimeout(this.riderToastTimers[id]);
    this.riderToastTimers = {};
    clearTimeout(this.deviceSettingsSyncTimer);
    clearTimeout(this.coverageThanksTimer);
    clearTimeout(this.coverageAutoCloseTimer);
    clearTimeout(this.helpResumeTimer);
    clearTimeout(this.helpConfirmTimer);
    stopTranslationTelemetry();
  },
  methods: {
    // ── Generic non-blocking toasts ──────────────────────────────────────────
    // Pushed into the SAME queue as the Rider check-in toasts, as a `message` kind, so both
    // share one component, one auto-dismiss timer, and swipe/tap-to-close. `dismissMs` sets
    // this toast's countdown (messages are shorter than the rider default 8 s).
    showAppToast({ text, orderLabel = null, tag = null, body = null, bodySegments = null, variant = 'info', duration = 4500 } = {}) {
      if (!text && !orderLabel) return null;
      const id = ++this.riderToastCounter;
      this.riderToastQueue.push({ id, kind: 'message', text, orderLabel, tag, body, bodySegments, variant, dismissMs: duration });
      return id;
    },
    // The QR gun read the feedback QR of a ticket: the rider has the bag, so the order is closed as if
    // «Entregado» had been tapped (RIDER_LEFT_THE_RESTAURANT in the backend), with its own sound and a
    // rider toast that names the order. Anything else the gun reads is only shown.
    async onQrScan({ orderId, text, usText, layoutMismatch, partial }) {
      logger.info('qr_scan', { orderId, text, usText, layoutMismatch, partial });
      // The tutorial's test step reads the gun itself: whatever it reads is told to it and closes no order.
      if (this.currentComponent === 'QrGunTutorialScreen') {
        this.qrGunScanEvent = { seq: this.qrGunScanEvent.seq + 1, kind: classifyTutorialScan({ orderId, partial }) };
        return;
      }
      if (partial) {
        this.showAppToast({ text: this.$t('message.qrScanPartial'), variant: 'warning', duration: 6000 });
        return;
      }
      if (!orderId) {
        this.showAppToast({ text: this.$t('message.qrScanUnknown', { text }), variant: 'warning', duration: 8000 });
        return;
      }
      // Without a session there is no list nor a backend to write to.
      if (this.currentComponent === 'LoginScreen' || !this.restaurant) return;
      // The gun repeats the code while the trigger is held: the second read must not close it twice.
      if (this.scansInFlight[orderId]) return;
      if (this.riderLeftUnsaved[orderId]) {
        this.saveRiderLeftFromScan(orderId, this.riderLeftUnsaved[orderId]);
        this.playOrderClosedSound();
        return;
      }
      if (this.riderDeliveredOrderIds[orderId]) {
        const known = this.findOrderById(orderId);
        this.showAppToast({
          orderLabel: known ? this.getPrimaryDisplayId(known) : orderId,
          tag: this.$t('message.qrScanAlreadyTag'),
          body: this.$t('message.qrScanAlreadyBody'),
          variant: 'info',
          duration: 6000,
        });
        return;
      }
      let order = this.findOrderById(orderId);
      if (!order) {
        // Not in this tablet's list (it aged out, or it never was here): ask the backend for it, which
        // only answers for an order this tablet may see. Offline it cannot answer; the scan still closes
        // the order, named by its id.
        let remote;
        this.scansInFlight[orderId] = true;
        try {
          remote = await fetchOrderPhotos(orderId);
        } catch (e) {
          remote = undefined;
        } finally {
          delete this.scansInFlight[orderId];
        }
        if (this.currentComponent === 'LoginScreen') return;
        if (!remote) {
          // null: the backend does not show this order to this tablet (another store's ticket). undefined:
          // it could not be asked. Either way the order is not closed blind: a rider may carry bags of
          // other stores.
          this.showAppToast({ text: this.$t(remote === null ? 'message.qrScanNotHere' : 'message.qrScanOffline'), variant: 'warning', duration: 8000 });
          reportForensicEvent('rider_scan_rejected', { order_id: orderId, reason: remote === null ? 'not_visible' : 'unverified' });
          return;
        }
        if (this.riderDeliveredOrderIds[orderId]) return; // closed meanwhile (a tap, or another tablet)
        order = { orderId, ticketId: remote.ticketId, deliveryId: remote.deliveryId, deliveryPlatform: remote.deliveryPlatform };
      }
      this.markRiderDelivered(order, { source: 'scan' });
    },
    // RIDER_LEFT for an order the gun closed, retried on a network failure; one that never lands is kept
    // in riderLeftUnsaved and said on screen.
    saveRiderLeftFromScan(orderId, label) {
      if (this.scansInFlight[`save:${orderId}`]) return;
      this.scansInFlight[`save:${orderId}`] = true;
      recordRiderLeft(orderId, { attempts: 3 }).then((outcome) => {
        delete this.scansInFlight[`save:${orderId}`];
        reportForensicEvent('rider_scan_saved', { order_id: orderId, outcome });
        if (outcome === 'failed') {
          this.riderLeftUnsaved[orderId] = label;
          if (this.currentComponent !== 'LoginScreen') {
            this.showAppToast({ orderLabel: label, tag: this.$t('message.qrScanNotSavedTag'), body: this.$t('message.qrScanNotSavedBody'), variant: 'warning', duration: 10000 });
          }
        } else {
          delete this.riderLeftUnsaved[orderId];
        }
      });
    },
    saveUnsavedRiderLeft() {
      for (const orderId of Object.keys(this.riderLeftUnsaved)) {
        this.saveRiderLeftFromScan(orderId, this.riderLeftUnsaved[orderId]);
      }
    },
    findOrderById(orderId) {
      return this.processedOrders?.find((o) => o.orderId === orderId) ||
        this.rawOrders?.find((o) => o.orderId === orderId) || null;
    },
    appToastIcon(variant) {
      if (variant === 'success') return 'check_circle';
      if (variant === 'warning') return 'error';
      return 'info';
    },
    // A queued analysis (offline / upload-phase-background) finished server-side. The queue
    // hands us the raw /item-comparison response — the ONLY place the missing-item detail
    // exists on the client (the realtime event carries none). Toast it with the full
    // breakdown, every time (first run and re-runs of the same order alike — unlike the
    // processQuery diff, this does not depend on a pending→decided transition).
    handleQueuedVerdict(orderId, result) {
      if (!result) return;
      // Live sales demo on: a real order's relaunched analysis toasts nothing (the demo orders' own do).
      if (liveDemoOn() && !isLiveDemoId(orderId)) return;
      const order = this.processedOrders?.find((o) => o.orderId === orderId) ||
        this.rawOrders?.find((o) => o.orderId === orderId) || null;
      const label = order
        ? (order.ticketId || order.deliveryId || order.posId || order.orderId)
        : orderId;
      const missingItems = extractMissingItems(result.missing_items);
      const isCorrect = result.is_correct === true || missingItems.length === 0;
      const { tag, body, bodySegments, variant } = analysisResultToast(
        { isCorrect, missingItems }, (k, ...rest) => this.$t(k, ...rest)
      );
      // Claim this verdict's analysisId BEFORE the realtime fallback can, so the generic
      // realtime toast (no detail) yields to this detailed one. The realtime event usually
      // carries the same analysis id; fall back to the order's current analysis id.
      const analysisId = order?.order?.orderAnalyses?.id || result.analysis_id || orderId;
      this.verdictToasted[analysisId] = Date.now();
      // Paint the tile from THIS same HTTP result, right now. The verdict otherwise only
      // colours the tile when the separate realtime order_analysis event arrives (~1-2 s
      // later on its own pub/sub channel), so the toast beat the green. Optimistically set the
      // order's analysis to the same values the realtime event will carry; when that event
      // lands it just confirms them (no desync).
      this.applyOptimisticVerdict(orderId, {
        id: analysisId,
        isCorrect: result.is_correct === true,
        appResponse: result.app_response ?? null,
      });
      const verdictSurface = this.openCameraOrderId === orderId ? 'modal' : 'toast';
      if (verdictSurface === 'modal') {
        // The operator is back inside this exact order right when its queued analysis landed —
        // show the usual result modal instead of a toast. CameraScreen builds it from the raw
        // result (which carries the full missing-item breakdown the toast shape drops).
        window.dispatchEvent(new CustomEvent('showVerdictModal', { detail: { orderId, result } }));
      } else {
        this.showAppToast({ orderLabel: label, tag, body, bodySegments, variant });
      }
      // The queued/background verdict lane was previously invisible in the forensic stream —
      // this is the ONLY client-side place the full missing-item detail exists for it (the
      // realtime event carries none). Record what the result was and how it reached the
      // operator (background toast vs the re-entry modal). Payload built from the raw result,
      // capped like the http path.
      const rawMissing = result.missing_items || {};
      const missingSummary = Object.values(rawMissing).slice(0, 20).map((v) => ({
        name: v.group_name, missing: v.missing_quantity, total: v.total_quantity,
      }));
      const missingCount = Object.values(rawMissing).reduce((s, v) => s + (Number(v.missing_quantity) || 0), 0);
      reportForensicEvent('analysis_verdict', {
        order_id: orderId,
        order_analysis_id: analysisId,
        is_correct: result.is_correct === true,
        source: 'queued',
        surface: verdictSurface,
        app_response: result.app_response ?? null,
        missing_count: missingCount,
        missing_groups: missingSummary.length,
        missing_items: missingSummary,
      });
      this.pruneVerdictToasted();
      // The processQuery diff path also consults foregroundVerdictOrders — mark it so a
      // later full refetch doesn't double-signal this same result.
      this.markForegroundVerdict(orderId);
    },
    // Mirror the realtime order_analysis in-place update from an HTTP verdict result, so the
    // tile colour (getOrderClass reads order.order.orderAnalyses) flips at the same instant as
    // the toast instead of waiting for the realtime event. Writes camelCase to match the
    // GraphQL shape the tile expects. Idempotent: the realtime event later overwrites with the
    // authoritative row.
    applyOptimisticVerdict(orderId, analyses) {
      const paint = (arr) => {
        const i = arr?.findIndex((o) => o.orderId === orderId);
        if (i === undefined || i < 0) return;
        arr[i].order.orderAnalyses = analyses;
        arr[i].orderAnalysis = analyses.id;
      };
      paint(this.processedOrders);
      paint(this.rawOrders);
    },
    // Opportunistically drop verdictToasted entries older than the realtime/HTTP race window
    // so the map can't grow unbounded on a long-lived tablet session.
    pruneVerdictToasted() {
      const cutoff = Date.now() - 5 * 60 * 1000;
      for (const id in this.verdictToasted) {
        if (this.verdictToasted[id] < cutoff) delete this.verdictToasted[id];
      }
    },
    // A realtime order_analysis event just mutated an order to a verdict IN PLACE. The
    // processQuery old-vs-new diff is blind to this (the in-place mutation makes oldOrder
    // already-terminal on the next refetch), so the verdict toast must fire here — otherwise
    // a verdict that arrives without the tablet's queue driving it (backend auto-analysis on
    // reconnect) lands silently. Deferred + deduped by analysisId so a detailed queue toast
    // (handleQueuedVerdict) claims it first and this generic fallback yields.
    emitVerdictToastRealtime(order) {
      if (!order || !this.hasTerminalVerdict(order)) return;
      const analysisId = order.order?.orderAnalyses?.id || order.orderId;
      if (this.verdictToasted[analysisId]) return;
      setTimeout(() => {
        if (this.verdictToasted[analysisId]) return; // a detailed queue toast won the race
        const orderId = order.orderId;
        // The operator has this order open in the camera right now — don't nag with an auto-
        // verdict toast while they are still capturing it. They see the verdict on Analizar.
        if (this.openCameraOrderId && orderId === this.openCameraOrderId) return;
        const marked = this.foregroundVerdictOrders[orderId];
        if (marked && Date.now() - marked < 20000) {
          delete this.foregroundVerdictOrders[orderId]; // already shown in the foreground modal
          return;
        }
        this.verdictToasted[analysisId] = Date.now();
        this.pruneVerdictToasted();
        const a = order.order?.orderAnalyses;
        const isCorrect = a?.isCorrect === true || order.order?.manualIsCorrect === true ||
          a?.appResponse === 'EMPTY_ORDER_CONTENT';
        const label = order.ticketId || order.deliveryId || order.posId || order.orderId;
        reportForensicEvent('analysis_verdict', {
          order_id: orderId,
          order_analysis_id: order.order?.orderAnalyses?.id ?? null,
          is_correct: isCorrect,
          source: 'realtime',
          surface: 'toast',
          // The realtime order_analysis event carries no missing-item detail, so app_response
          // is the only result content available on this path (missing_count stays null).
          app_response: a?.appResponse ?? null,
          missing_count: null,
        });
        this.showAppToast(isCorrect
          ? { orderLabel: label, tag: this.$t('message.tagCorrect'), body: '', variant: 'success' }
          : { orderLabel: label, tag: this.$t('message.tagReview'), body: this.$t('message.reviewGeneric'), variant: 'warning' });
      }, 1500);
    },
    handleHapticFeedback(e) {
      const interactive = e.target.closest('button, a, [role="button"], .order-button, .header-button, .option-button, .mobile-toggle-btn, .mode-toggle, .close-button, .back-button, .back-icon, .update-icon');
      if (!interactive || interactive.disabled) return;

      // Android: navigator.vibrate (50ms minimum for motor spin-up)
      if (navigator.vibrate) {
        navigator.vibrate(50);
        return;
      }

      // iOS Safari 17.4+: hidden checkbox switch triggers native haptic
      this.triggerIOSHaptic();
    },
    triggerIOSHaptic() {
      try {
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.setAttribute('switch', '');
        checkbox.style.cssText = 'position:fixed;top:-100px;left:-100px;opacity:0;pointer-events:none;';

        const label = document.createElement('label');
        label.style.cssText = 'position:fixed;top:-100px;left:-100px;opacity:0;pointer-events:none;';
        label.appendChild(checkbox);
        document.body.appendChild(label);

        checkbox.click();

        requestAnimationFrame(() => label.remove());
      } catch (_) {
        // Silently fail on unsupported browsers
      }
    },
    async initializeRealtimeHandling() {
      if (!this.restaurant?.id) return;

      this.startResyncHeartbeat();

      // Get channel configurations
      const channelConfigs = this.getChannelConfigs();

      // Initialize each channel
      for (const config of channelConfigs) {
        await this.initializeChannel(config);
      }
    },
    
    /**
     * Initializes a real-time channel with fallback polling strategy
     * 
     * This function implements a robust connection strategy:
     * 1. First starts a fallback polling mechanism (if enabled in config)
     * 2. Then attempts to establish a real-time WebSocket connection
     * 3. If real-time connection succeeds, stops the fallback polling
     * 4. If real-time connection fails, continues with fallback polling
     * 
     * This "fallback-first" approach ensures:
     * - Users receive data immediately through polling (no initial waiting period)
     * - Seamless transition to real-time when connection is established
     * - Graceful degradation if real-time connection fails or disconnects
     * - Continuous data availability regardless of network conditions
     */
    async initializeChannel(config, { resyncOnConnect = true } = {}) {
      const epoch = this.realtimeEpoch;
      // Clean up existing connection if any
      this.cleanupChannelConnection(config.name);

      // Track the channel in the event ledger: it owns dedup, the per-channel cursor,
      // acking (only after the handler applied without throwing), gap-triggered resync
      // and apply_failed reporting. `coalesceResync: true` for channels whose handler
      // refetches full state ignoring the payload (orders): a gap becomes ONE refetch
      // instead of N stale replays.
      registerChannel(config.path, {
        name: config.name,
        applyFn: config.handler,
        coalesceResync: config.name === 'orders',
      });

      // Boot/reconnect full sync, INDEPENDENT of the polling fallback: the initial
      // fetch used to live inside startFallbackForChannel, so disabling polling
      // would have left a fresh login (no resync cursor yet) with no orders at all.
      if (config.fallback?.handler) {
        config.fallback.handler();
      }

      // Give the channel a resync cursor even before its first live event — otherwise
      // anything published during a disconnection that precedes the first push is
      // unrecoverable (found by the tracker team: fresh session + airplane mode).
      seedCursor(config.path);

      // Start fallback if enabled
      if (config.fallback?.enabled) {
        this.startFallbackForChannel(config.name, config.fallback.interval, config.fallback.handler);
      }

      try {
        // Try to establish realtime connection
        // A logout or an expired session tearing the connection down while we connect must not be
        // followed by a subscription (connectUnlessStale closes the channel instead).
        const channel = await connectUnlessStale(() => events.connect(config.path), () => epoch !== this.realtimeEpoch);
        if (!channel) return;
        this.eventsChannels[config.name] = channel;

        this.subscriptions[config.name] = this.eventsChannels[config.name].subscribe({
          next: (data) => {
            // A DATA frame reached this channel: proof the socket delivers. Amplify's Events
            // provider forwards only `type: 'data'` to observer.next, so `ka` frames never
            // arrive here (an earlier version of this comment claimed they did). Keep-alive
            // evidence comes from the provider's own keepAliveTimestamp, read by
            // checkSocketTruth, which closes a quiet restaurant's episode.
            this.lastSocketMessageAt = Date.now();
            if (this.deafSince !== null) this.noteSocketRecovered(config.name, 'frame_received');
            // Only process real events (ignore connection and keepalive)
            if (!['connection_init', 'connection_ack', 'ka'].includes(data.type)) {
              // Add channel information to the event data
              const enrichedData = {
                ...data,
                _channelName: config.name,
                _channelPath: config.path
              };
              // The ledger applies the event through config.handler, tracks the seq
              // cursor, acks on success, and resyncs missed events when it sees a gap.
              onPushEvent(config.path, enrichedData);
            }
          },
          error: () => {
            // A rejected subscribe (an invalid channel name, say) is never acked: tell the ledger so a
            // socket rebuild does not wait for it or count it as a failure.
            noteChannelRejected(config.path);
            // On any error, fall back to fallback mechanism if enabled
            if (config.fallback?.enabled) {
              this.startFallbackForChannel(config.name, config.fallback.interval, config.fallback.handler);
            }
            this.isRealTimeConnected[config.name] = false;
          },
          complete: () => {
            // On completion, fall back to fallback mechanism if enabled
            if (config.fallback?.enabled) {
              this.startFallbackForChannel(config.name, config.fallback.interval, config.fallback.handler);
            }
            this.isRealTimeConnected[config.name] = false;
          }
        });

        // If we get here, connection was successful
        this.isRealTimeConnected[config.name] = true;
        this.channelSubscribedAt[config.name] = Date.now();
        if (config.fallback?.enabled) {
          this.stopFallbackForChannel(config.name);
        }
        logger.success(`Realtime connection established for channel: ${config.name}`);

        // Recover anything published while this channel was disconnected (no-op until
        // the channel has a cursor, i.e. has seen at least one seq-carrying event). A socket
        // rebuild passes resyncOnConnect: false: it resyncs once, AFTER the subscribe ack (see
        // recoverEventsSocket), instead of a second time before it.
        if (resyncOnConnect) resyncChannel(config.path, 'connected');

      } catch (error) {
        if (error.name === 'NonRetryableError' || error.message?.includes('UnauthorizedException')) {
          logger.error('Authentication failed, redirecting to login', {error});
          window.dispatchEvent(new CustomEvent('sessionExpired'));
        } else {
          // For other errors, we're already using fallback if enabled
          logger.error(`Failed to establish realtime connection for channel ${config.name}, using fallback if available`, {error});
          this.isRealTimeConnected[config.name] = false;
        }
      }
    },
    
    startFallbackForChannel(channelName, interval = 30000, handler) {
      // Don't create multiple fallback intervals for the same channel
      if (this.pollingIntervals[channelName]) return;

      // Interval only — the immediate boot fetch moved to initializeChannel so it
      // happens regardless of whether legacy polling is enabled.
      this.pollingIntervals[channelName] = setInterval(() => {
        handler();
      }, interval);
    },
    
    stopFallbackForChannel(channelName) {
      if (this.pollingIntervals[channelName]) {
        clearInterval(this.pollingIntervals[channelName]);
        this.pollingIntervals[channelName] = null;
      }
    },

    cleanupChannelConnection(channelName) {
      if (this.subscriptions[channelName]) {
        this.subscriptions[channelName].unsubscribe();
        this.subscriptions[channelName] = null;
      }
      if (this.eventsChannels[channelName]) {
        this.eventsChannels[channelName].close();
        this.eventsChannels[channelName] = null;
      }
      this.stopFallbackForChannel(channelName);
      this.isRealTimeConnected[channelName] = false;
      this.channelSubscribedAt[channelName] = null;
    },
    
    cleanupConnection() {
      // Drop any armed deaf-confirmation: the channels are going away on purpose, and a
      // timer that fires afterwards would re-init them behind the teardown's back.
      this.clearDeafSuspicion();
      this.deafSince = null;
      this.deafVia = null;
      this.deafRule = null;
      this.deafRecoveryStartedAt = null;
      this.deafRecoverySocketSeq = null;
      this.deafRecoveryAttempts = 0;
      this.nextDeafRecoveryAt = 0;
      this.watchdogActAt = null;
      this.realtimeEpoch += 1;
      // Clean up all channel connections
      for (const channelName in this.eventsChannels) {
        this.cleanupChannelConnection(channelName);
      }
      this.stopResyncHeartbeat();
    },

    // ── Resync heartbeat ─────────────────────────────────────────────────────
    // Covers the kiosk hole: tablet always visible, never changes network, socket
    // terminally dead → none of the ledger's resync triggers (online/visible/gap/
    // reconnected) would fire, and nothing else re-establishes the socket (Amplify
    // gives up after its keep-alive timeout; reconnection used to happen only on
    // visibilitychange). Every 3 min, any channel we believe disconnected gets a
    // full re-init: reconnect the socket, boot fetch, seed/resync. While the
    // socket is healthy this does nothing at all.
    startResyncHeartbeat() {
      this.startSocketWatchdog();
      if (this.resyncHeartbeatInterval) return;
      this.resyncHeartbeatInterval = setInterval(() => {
        if (this.deafRecovering || this.reconnectingDead) return; // a rebuild is already doing this
        for (const config of this.getChannelConfigs()) {
          if (this.isRealTimeConnected[config.name]) continue;
          this.initializeChannel(config);
        }
      }, 180000);
    },

    stopResyncHeartbeat() {
      this.stopSocketWatchdog();
      if (this.resyncHeartbeatInterval) {
        clearInterval(this.resyncHeartbeatInterval);
        this.resyncHeartbeatInterval = null;
      }
    },
    
    async handleVisibilityChange() {
      this.perfVisChanges += 1;
      if (document.visibilityState === 'visible') {
        const backgroundDuration = Date.now() - (this.backgroundTimestamp || 0);
        logger.info(`App returned to foreground after ${Math.round(backgroundDuration/1000)}s`);
        // The socket's keep-alive age is stale right after a resume: do not judge ZOMBIE yet.
        this.zombieHoldoffUntil = Date.now() + ZOMBIE_RESUME_HOLDOFF_MS;

        if (Date.now() - this.lastForegroundSyncAt < FOREGROUND_SYNC_MIN_INTERVAL_MS) {
          logger.info('Foreground sync skipped: one ran less than 30 s ago');
          return;
        }
        this.lastForegroundSyncAt = Date.now();

        try {
          // Always fetch orders when app becomes active, even if no sockets were reconnected. Started
          // FIRST and not awaited: the reconnect check can wait up to 10 s (the dead-socket jitter) and
          // rebuild a socket, and the kitchen's list must not wait behind that.
          logger.info('Fetching orders after app became active');
          const fetching = this.fetchRawOrders();
          // Check if connections are still alive and reconnect if needed
          await this.checkAndReconnectDeadConnections();
          await fetching;
        } catch (err) {
          logger.error('Error during foreground recovery:', err);
        }
      } else {
        logger.info('App went to background');
        this.backgroundTimestamp = Date.now();
      }
    },
    
    async handleBackOnline() {
      this.perfOnlineChanges += 1;
      // Network is back: reconnect any dead channel NOW instead of waiting for the
      // 3-min heartbeat or a visibility change (kiosk tablets rarely produce one).
      // The eventLedger's own 'online' listener resyncs channels whose socket
      // survived; this covers the ones whose subscription terminally died offline.
      logger.info('Network back online, checking realtime connections');
      this.saveUnsavedRiderLeft();
      try {
        await this.checkAndReconnectDeadConnections();
      } catch (err) {
        logger.error('Error reconnecting channels after coming back online:', err);
      }
    },

    async checkAndReconnectDeadConnections() {
      // Foreground, online and the watchdog can all arrive together; two init loops racing on the
      // same subscriptions orphan some of them. One at a time: while a rebuild or another
      // reconnect runs, this one has nothing to add.
      if (this.deafRecovering || this.reconnectingDead) return;
      this.reconnectingDead = true;
      const epoch = this.realtimeEpoch;
      try {
        // The socket itself is gone (60 s dead, Amplify silent). Back online or in the foreground is
        // exactly when a whole site's tablets find out together, so the rebuild waits a random
        // 0-10 s, looks again (Amplify may have got there first) and counts against the day's cap.
        const plan = await planOnlineReturnRebuild({
          getProbe: () => getSocketProbe(),
          sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
          spend: spendRecovery,
          throttled: () => this.deafBackoffRemainingMs() > 0,
          onLine: () => navigator.onLine,
          isCancelled: () => epoch !== this.realtimeEpoch,
        });
        if (plan.action === 'cancelled' || plan.action === 'throttled' || plan.action === 'offline') return;
        if (plan.action === 'capped') {
          this.reportWatchdogCapped('dead', plan.probe);
          return;
        }
        if (plan.action === 'rebuild') {
          // One rebuild, which closes the socket first, instead of re-initialising channels over a
          // socket Amplify may still call READY.
          if (this.deafSince === null) {
            this.openDeafEpisode({ via: 'foreground', rule: 'dead', state: plan.probe.hubState, probe: plan.probe });
          }
          await this.recoverDeafChannels(plan.probe.hubState, { via: 'foreground', rule: 'dead' });
          return;
        }
        // 'none' and 'recovered_meanwhile' fall through: the per-channel check below still finds any
        // channel whose own subscription died.
        for (const config of this.getChannelConfigs()) {
          if (epoch !== this.realtimeEpoch) return;
          if (!this.isConnectionAlive(config.name)) {
            logger.warning(`Connection dead for channel: ${config.name}, reconnecting`);
            this.cleanupChannelConnection(config.name);
            await this.initializeChannel(config);
          } else {
            logger.info(`Connection alive for channel: ${config.name}`);
          }
        }
      } finally {
        this.reconnectingDead = false;
      }
    },
    
    isConnectionAlive(channelName) {
      // Check if we think the connection is connected
      if (!this.isRealTimeConnected[channelName]) {
        return false;
      }
      
      // Check if the channel and subscription exist
      const channel = this.eventsChannels[channelName];
      const subscription = this.subscriptions[channelName];
      
      if (!channel || !subscription) {
        return false;
      }
      
      // Check if subscription is closed
      if (subscription.closed) {
        return false;
      }

      // A silently-dead socket never closes the subscription: on a network blackhole
      // (half-open TCP, Wi-Fi roam) Amplify's `_errorDisconnect` closes the WebSocket
      // without erroring or completing the per-channel Observables, so `closed` stays
      // false and `isRealTimeConnected` stays true forever. Amplify's own watchdog is
      // what notices — it reports the socket unhealthy on the Hub — so the socket state
      // is the authority here, not the subscription object.
      if (this.deafSince !== null) {
        return false;
      }

      // The socket says it is dead and has said so for 60 s: Amplify is not coming back on its
      // own (see socketWatchdog.js). Not instantaneous on purpose — a reconnect in progress
      // reads "not ready" for a few seconds and must not cost a five-channel rebuild.
      if (isProbeDead(getSocketProbe(), Date.now())) {
        return false;
      }

      return true;
    },

    // Socket-health transitions from Amplify's Hub. Cheap by construction: the only
    // actionable state is DEAF_SOCKET_STATE, and even that one has to survive
    // DEAF_CONFIRM_MS before it costs a single request.
    handleSocketState({ state }) {
      this.socketState = state;

      if (state !== DEAF_SOCKET_STATE) {
        // Any other state clears a pending suspicion: a late `ka` (back to 'Connected') or
        // Amplify moving on to its own disconnect/reconnect cycle, which it handles itself.
        // An OPEN episode is deliberately NOT closed here. Our own reconnect drives the
        // socket through Connecting → Connected, and a reconnect that lands on another
        // dead socket reports 'Connected' just the same. Closing on that would declare a
        // false recovery, reset the backoff, and leave the tablet looping every ~95 s with
        // a telemetry trail of episodes that were never actually fixed. The episode closes
        // on evidence instead — see noteSocketRecovered, called when a frame arrives.
        this.clearDeafSuspicion();
        return;
      }

      // Nothing to recover before the restaurant (and therefore the channels) is known,
      // nothing to recover while the OS interface is down, and nothing to add while an
      // episode is already open or a confirmation is already armed.
      if (!this.restaurant?.id || !this.isOnline) return;
      // Re-arm even with an episode already open: our reconnect can itself land on a dead
      // socket, and Amplify will say so again in another 65 s. Without this the very first
      // failed attempt would leave the tablet deaf for the rest of the session — the exact
      // bug being fixed. Cost is bounded by the backoff in recoverDeafChannels.
      if (this.deafConfirmTimer) return;

      this.deafSuspectedAt = Date.now();
      this.deafConfirmTimer = setTimeout(() => {
        this.deafConfirmTimer = null;
        this.confirmDeafChannels(state);
      }, DEAF_CONFIRM_MS);
    },

    // The socket is delivering again: evidence arrived while an episode was open.
    //  - 'frame_received': a DATA frame reached a channel subscriber (immediate, but only busy
    //    restaurants produce it).
    //  - 'keepalive': checkSocketTruth saw the rebuilt socket's own keepAliveTimestamp move past
    //    the start of the recovery. AppSync sends a `ka` about every 60 s, so this closes the
    //    episode on a restaurant with no orders at all, which `frame_received` never could.
    // `via` is the closing evidence; `episode_via` / `rule` say what opened the episode.
    noteSocketRecovered(channelName, via = 'frame_received') {
      reportForensicEvent('conn_deaf_recovered', {
        socket_state: this.socketState,
        channel: channelName,
        deaf_ms: Date.now() - this.deafSince,
        recovery_attempts: this.deafRecoveryAttempts,
        via,
        episode_via: this.deafVia,
        rule: this.deafRule,
      });
      this.deafSince = null;
      this.deafVia = null;
      this.deafRule = null;
      this.deafRecoveryStartedAt = null;
      this.deafRecoverySocketSeq = null;
      this.deafRecoveryAttempts = 0;
      this.clearDeafSuspicion();
    },

    clearDeafSuspicion() {
      if (this.deafConfirmTimer) {
        clearTimeout(this.deafConfirmTimer);
        this.deafConfirmTimer = null;
      }
      this.deafSuspectedAt = null;
    },

    // THE event this whole path exists for: a tablet that is powered, online and sending
    // heartbeats while receiving no orders at all. Without it the episode is invisible in
    // telemetry — it leaves no trace of its own, only the absence of the fx_order_open events
    // that should have followed. Opened once per episode by the Hub-driven and watchdog paths
    // (the retries that follow are conn_deaf_reconnect{attempt}); a manual duplicate emits its
    // own, because it is a person asking, not a detector.
    reportDeafEpisode({ via, rule = null, state, channels, confirmMs, probe }) {
      const now = Date.now();
      reportForensicEvent('conn_deaf', {
        ...(probe ? socketHealthFields(probe, now) : {}),
        socket_state: state,
        channels,
        // Since the last frame of any kind, keep-alives included (the socket's own timestamp).
        silent_ms: probe?.kaAgeMs ?? (this.lastSocketMessageAt ? now - this.lastSocketMessageAt : null),
        confirm_ms: confirmMs,
        polling_enabled: localStorage.getItem('bronze_enable_order_polling') === '1',
        via,
        rule,
      });
    },

    // The debounce survived: the socket has now been silent for ~65 s + DEAF_CONFIRM_MS
    // while Amplify still calls it connected. That is the zombie, not a late keep-alive.
    confirmDeafChannels(state) {
      if (this.socketState !== DEAF_SOCKET_STATE) return; // moved on while we waited
      if (!this.restaurant?.id || !this.isOnline) return;

      const deafChannels = this.getChannelConfigs()
        .filter((config) => this.isRealTimeConnected[config.name])
        .map((config) => config.name);
      if (deafChannels.length === 0) return;

      const firstConfirmation = this.deafSince === null;
      if (firstConfirmation) {
        this.deafSince = this.deafSuspectedAt || Date.now();
        this.deafVia = 'hub_state';
        this.deafRule = null;
        this.reportDeafEpisode({
          via: 'hub_state', state, channels: deafChannels, confirmMs: DEAF_CONFIRM_MS, probe: getSocketProbe(),
        });
        logger.warning(`Events socket deaf (${state}) — reconnecting ${deafChannels.length} channel(s)`);
      }
      this.deafSuspectedAt = null;
      this.recoverDeafChannels(state);
    },

    // ── Local socket watchdog ────────────────────────────────────────────────────────────
    // Amplify can stop reconnecting while the Hub shows nothing actionable (stale handshake
    // timer, halted ReconnectionMonitor; see socketWatchdog.js), so every 30 s the app reads the
    // socket itself. Local only: no request. Its rules never touch `isOnline`.
    startSocketWatchdog() {
      if (this.socketWatchdogTimer) return;
      this.socketWatchdogTimer = setInterval(() => this.checkSocketTruth(), WATCHDOG_TICK_MS);
    },

    stopSocketWatchdog() {
      if (this.socketWatchdogTimer) {
        clearInterval(this.socketWatchdogTimer);
        this.socketWatchdogTimer = null;
      }
    },

    // "Quiet" for the BOMB rule, whose socket still delivers so there is no hurry: nobody is
    // building an order and nothing the operator is waiting on is in flight. Concretely, none of:
    //  - isPhotographing / openCameraOrderId: an order is open in the capture screen (this also
    //    covers the capture and the analysis popup, which only exist inside an open order);
    //  - currentNotification: a blocking notification is up;
    //  - helpFlow: the guided help modal is open;
    //  - an upload cycle running (backgroundUploader.isUploadInProgress()).
    // The rebuild refetches the orders and re-renders the list, which is what must not happen
    // under the operator's hands.
    isQuietForSocketRecovery() {
      return !this.isPhotographing
        && !this.openCameraOrderId
        && !this.currentNotification
        && !this.helpFlow
        && !isUploadInProgress();
    },

    // Milliseconds until the next recovery is allowed (0 = now). One backoff for every path, with
    // jitter fixed when the attempt is made (recoverDeafChannels) so it is stable across ticks.
    deafBackoffRemainingMs() {
      return Math.max(0, (this.nextDeafRecoveryAt || 0) - Date.now());
    },

    // Open a deaf episode: the timestamps the rest of the machinery keys on, and the one
    // fx_conn_deaf per episode. Used by every path that decides, on its own evidence, that the
    // socket is not delivering.
    openDeafEpisode({ via, rule = null, state, probe, confirmMs = null }) {
      this.deafSince = Date.now();
      this.deafVia = via;
      this.deafRule = rule;
      this.reportDeafEpisode({
        via, rule, state, confirmMs, probe,
        channels: this.getChannelConfigs().map((config) => config.name),
      });
    },

    // Cap reached: stay silent but say so once per day, otherwise a capped tablet looks like the
    // watchdog never ran.
    reportWatchdogCapped(rule, probe) {
      const day = new Date().toDateString();
      if (this.watchdogCappedDay === day) return;
      this.watchdogCappedDay = day;
      reportForensicEvent('conn_watchdog_capped', { rule, cap: DAILY_RECOVERY_CAP, socket_state: probe?.hubState ?? null });
    },

    // The server's count of the orders channel against this tablet's cursor, from the syncDevice the
    // health check already makes every 10 min (see seqHint.js). No request of its own. A server ahead
    // for over two minutes always gets a resync; only an event that happened while the subscription
    // was up, with no data frame since, is read as a deaf socket and rebuilt, through the same
    // single flight, backoff and daily cap as the watchdog.
    handleDeviceSynced(device) {
      try {
        const now = Date.now();
        const restaurantId = this.restaurant?.id;
        const path = `orders/${restaurantId}`;
        const cur = getCursor(path);
        const subscribedAt = this.channelSubscribedAt.orders;
        const upS = subscribedAt == null ? null : (now - subscribedAt) / 1000;
        const quietS = this.lastSocketMessageAt == null ? null : (now - this.lastSocketMessageAt) / 1000;
        const action = decideSeqHint({
          srv: device?.ordersMaxSeq,
          age: device?.ordersMaxSeqAgeSeconds,
          restaurantId: device?.restaurantId,
          currentRestaurantId: restaurantId,
          cur,
          upS,
          quietS,
          inDeafEpisode: this.deafSince !== null,
        });
        if (action === 'ignore') return;

        resyncChannel(path, 'seq_hint');
        const props = {
          srv: device.ordersMaxSeq, cur, age_s: device.ordersMaxSeqAgeSeconds,
          quiet_s: quietS == null ? null : Math.round(quietS), up_s: upS == null ? null : Math.round(upS),
          action, recover: null,
        };
        if (action === 'resync+recover') {
          if (this.deafRecovering) props.recover = 'in_flight';
          else if (this.deafBackoffRemainingMs() > 0) props.recover = 'backoff';
          else if (!spendRecovery(now)) {
            props.recover = 'capped';
            this.reportWatchdogCapped('seq_hint', getSocketProbe(now));
          } else {
            props.recover = 'started';
            const probe = getSocketProbe(now);
            this.openDeafEpisode({ via: 'seq_hint', state: probe.hubState, probe });
            logger.warning(`Server is ahead of the orders cursor (${device.ordersMaxSeq} > ${cur}) with no frame since — rebuilding ${registeredChannelCount()} channel(s)`);
            this.recoverDeafChannels(probe.hubState, { via: 'seq_hint' });
          }
        }
        reportForensicEvent('conn_seq_hint_resync', props);
      } catch (error) {
        logger.console('handleDeviceSynced failed', error);
      }
    },

    checkSocketTruth() {
      try {
        if (!this.restaurant?.id) return;
        const now = Date.now();
        // A hidden page, or a tick that comes much later than it should (the device slept, the page
        // was frozen), has a stale keep-alive age rather than a dead socket (see resumePlan).
        const gapMs = this.lastWatchdogTickAt ? now - this.lastWatchdogTickAt : 0;
        this.lastWatchdogTickAt = now;
        const plan = resumePlan({
          hidden: document.visibilityState === 'hidden', gapMs, now, zombieHoldoffUntil: this.zombieHoldoffUntil,
        });
        this.zombieHoldoffUntil = plan.zombieHoldoffUntil;
        if (plan.skip) return;

        const probe = getSocketProbe(now);

        // Close an open episode on evidence: a frame of any kind (ka or data) arrived on a socket
        // that is NEWER than the one the recovery started from. Identity, not time: during the
        // close wait the old socket can still deliver one last frame, which proves nothing.
        if (
          this.deafSince !== null && !this.deafRecovering && this.deafRecoverySocketSeq != null
          && isSocketOpen(probe) && probe.socketSeq > this.deafRecoverySocketSeq
          && probe.kaTimestamp != null && probe.socketOpenedAt != null && probe.kaTimestamp > probe.socketOpenedAt
        ) {
          this.noteSocketRecovered(null, 'keepalive');
        }

        const decision = evaluateSocketRules({
          probe,
          now,
          onLine: navigator.onLine,
          channelCount: registeredChannelCount(),
          quiet: this.isQuietForSocketRecovery(),
          zombieHoldoffUntil: this.zombieHoldoffUntil,
        });
        if (!decision) {
          this.watchdogActAt = null; // whatever it saw has passed: the next sighting jitters afresh
          return;
        }
        if (this.deafRecovering) return;
        if (this.deafBackoffRemainingMs() > 0) return;

        // The FIRST action of an episode waits a random 0-60 s: a fleet whose sockets died together
        // must not act together. (An episode already open is a retry, governed by the backoff.)
        if (this.deafSince === null) {
          if (this.watchdogActAt == null) this.watchdogActAt = now + firstActionDelayMs();
          if (now < this.watchdogActAt) return;
        }

        if (!spendRecovery(now)) {
          this.reportWatchdogCapped(decision.rule, probe);
          return;
        }

        this.watchdogActAt = null;
        if (this.deafSince === null) {
          this.openDeafEpisode({ via: 'watchdog', rule: decision.rule, state: probe.hubState, probe, confirmMs: decision.confirmMs });
        }
        logger.warning(`Events socket watchdog: ${decision.rule} — rebuilding ${registeredChannelCount()} channel(s)`);
        this.recoverDeafChannels(probe.hubState, { via: 'watchdog', rule: decision.rule, skipThrottle: true });
      } catch (error) {
        logger.console('checkSocketTruth failed', error);
      }
    },

    // The recovery primitive: close the WebSocket FIRST, wait until Amplify registers the
    // close, rebuild the channels on a fresh socket, resync. Re-subscribing over a dead-but-READY
    // socket reuses it and the subscribe ack then times out, which clears Amplify's keep-alive
    // interval for good (see closeEventsSocket). Never events.closeAll().
    rebuildEventsSocket() {
      const epoch = this.realtimeEpoch;
      return recoverEventsSocket({
        channels: this.getChannelConfigs(),
        isCancelled: () => epoch !== this.realtimeEpoch,
        reinitChannel: async (config) => {
          this.cleanupChannelConnection(config.name);
          await this.initializeChannel(config, { resyncOnConnect: false });
        },
      });
    },

    // Re-init every channel we still believe connected. Serialised through
    // `deafRecovering` because Amplify re-reports an unhealthy socket every 5s while it
    // stays unhealthy, and concurrent initializeChannel runs would race on
    // this.subscriptions / this.eventsChannels and orphan a live subscription.
    // `skipThrottle`: the caller has already applied the backoff (the watchdog) or is
    // deliberately bypassing it once (a manual duplicate).
    async recoverDeafChannels(state, { via = 'hub_state', rule = null, skipThrottle = false, deafTelemetry = true } = {}) {
      if (this.deafRecovering) return;
      // Amplify dedupes identical consecutive ConnectionStates, so a socket that simply
      // stays unhealthy reports once. A socket that FLAPS (a late `ka` flips it healthy,
      // the next miss flips it back) reports on every flip, and each report would tear
      // down and rebuild all five channels. Floor the attempts so a flapping socket costs
      // one reconnect per interval instead of one per flip.
      if (!skipThrottle && this.deafBackoffRemainingMs() > 0) {
        // Record the skip, but only when no attempt has run in this episode yet: otherwise the
        // lane would show an fx_conn_deaf with no reconnect and read as "the path never ran".
        // Once an attempt exists, conn_deaf_reconnect{attempt} already tells the story and a
        // skip per backoff tick is pure noise.
        if (this.deafRecoveryAttempts === 0) {
          reportForensicEvent('conn_deaf_reconnect_skipped', {
            socket_state: state,
            ms_since_last_attempt: this.lastDeafRecoveryAt ? Date.now() - this.lastDeafRecoveryAt : null,
            backoff_ms: this.deafBackoffRemainingMs(),
          });
        }
        return;
      }
      // Only a deaf-episode attempt moves the backoff (and the episode's own bookkeeping): a manual
      // duplicate rebuild on a healthy socket (deafTelemetry false) must not leave a real DEAD waiting.
      const now = Date.now();
      const attempt = planRecoveryAttempt({ attempts: this.deafRecoveryAttempts, now, countsTowardBackoff: deafTelemetry });
      if (deafTelemetry) {
        this.lastDeafRecoveryAt = now;
        this.deafRecoveryStartedAt = now;
        this.deafRecoverySocketSeq = getSocketProbe().socketSeq;
        this.deafRecoveryAttempts = attempt.attempts;
        // Fixed now (30 s, doubling to 10 min, each step +-25%) so every path sees the same wait.
        this.nextDeafRecoveryAt = attempt.nextAt;
      }
      this.deafRecovering = true;
      try {
        const result = await this.rebuildEventsSocket();
        if (result.cancelled) return; // a logout or session expiry tore everything down meanwhile
        if (result.timedOut) {
          // A connect hung: the slot is already free (deafRecovering is released below). Bump the epoch
          // so the hung init, when it finally finishes, closes its channel instead of subscribing
          // behind the next rebuild.
          this.realtimeEpoch += 1;
          if (deafTelemetry) reportForensicEvent('conn_deaf_reconnect_failed', { socket_state: state, error: 'rebuild_timeout', via, rule });
          else reportForensicEvent('manual_duplicate_rebuild', { rebuilt: false, error: 'rebuild_timeout' });
          return;
        }
        if (!deafTelemetry) {
          // A person-triggered rebuild on a socket that looked healthy is not a deaf episode: keep
          // fx_conn_deaf* a clean signal and say what happened under the manual-duplicate name.
          reportForensicEvent('manual_duplicate_rebuild', { rebuilt: result.rebuilt, error: result.rebuilt ? null : 'socket_not_closed' });
          return;
        }
        if (!result.rebuilt) {
          // Amplify still calls the socket READY 2 s after close(): re-subscribing now would
          // reuse it. The backoff schedules the next attempt, by which time the closing
          // handshake has finished.
          reportForensicEvent('conn_deaf_reconnect_failed', {
            socket_state: state, error: 'socket_not_closed', via, rule, ws_ready_before: result.readyStateBefore,
          });
          return;
        }
        reportForensicEvent('conn_deaf_reconnect', {
          socket_state: state,
          deaf_ms: this.deafSince ? Date.now() - this.deafSince : null,
          attempt: this.deafRecoveryAttempts,
          connected: this.getChannelConfigs().filter((c) => this.isRealTimeConnected[c.name]).length,
          via,
          rule,
          ws_ready_before: result.readyStateBefore,
        });
      } catch (error) {
        logger.error('Failed to recover deaf channels', { error });
        reportForensicEvent('conn_deaf_reconnect_failed', { socket_state: state, error: String(error), via, rule });
      } finally {
        this.deafRecovering = false;
      }
    },

    // Staff typed an order by hand and the backend says it already exists. The usual reason is a
    // tablet that missed it (a deaf socket), so the answer is to FIND the order, not to say "try
    // again". Steps and their order live in resolveManualDuplicate (unit-tested); this wires them
    // to the real lookups. Returns {outcome, order, transient, platforms}; CameraScreen opens the
    // order or says why it did not.
    //  - `visibleOrders` is what CameraScreen is painting, manual orders created this session
    //    included (App does not hold those until the backend returns them);
    //  - `createdAt` is the new 400 body's created_at: an order older than the list's 60 minutes is
    //    not in the list whatever the socket did, so the rebuild is skipped for it.
    handleManualDuplicate({ platform, deliveryId, orderId = null, createdAt = null, visibleOrders = [] } = {}) {
      const target = { platform, deliveryId, orderId, createdAt };
      return resolveManualDuplicate({
        target,
        findLocal: () => lookupDuplicate([visibleOrders, this.processedOrders, this.rawOrders], target),
        recoverSocket: () => this.recoverForManualDuplicate(),
        refreshOrders: () => this.fetchRawOrders(),
        fetchWide: () => this.fetchOrderFromWideWindow(target),
        report: (event, props) => reportForensicEvent(event, props),
      });
    },

    // Rebuild the socket once for a manual duplicate, bypassing the backoff (a person is waiting;
    // the daily cap does not count it either). A recovery that is already running is awaited, not
    // raced: concurrent initializeChannel runs would orphan subscriptions.
    //  - fx_conn_deaf only when no episode is open (the open one already said it);
    //  - an episode is opened (deafSince) only when the probe says the socket really looks unwell:
    //    on a healthy tablet the order was simply not on the list, and marking a healthy socket
    //    dead would make every foreground/online check rebuild it until the next keep-alive.
    async recoverForManualDuplicate() {
      const deadline = Date.now() + 20000;
      while (this.deafRecovering && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      const probe = getSocketProbe();
      const looksHealthy = isSocketOpen(probe) && probe.kaAgeMs != null && probe.kaAgeMs <= ZOMBIE_KA_AGE_MS;
      const episodeOpen = this.deafSince !== null;
      // fx_conn_deaf (and the reconnect events that follow it) are a deaf signal: they are emitted only
      // when the socket really looks unwell. A healthy tablet whose list simply lacked the order is
      // reported by fx_manual_duplicate_* alone.
      if (!episodeOpen && !looksHealthy) {
        this.openDeafEpisode({ via: 'manual_duplicate', state: probe.hubState ?? this.socketState, probe });
      }
      await this.recoverDeafChannels(probe.hubState, {
        via: 'manual_duplicate', skipThrottle: true, deafTelemetry: episodeOpen || !looksHealthy,
      });
    },

    // One-off lookup with the windows widened (240 min instead of 60): the order exists but the list
    // never shows it. NOT added to the lists: it is opened as a transient selection, and the regular
    // fetch and polls are untouched.
    //  1. the backend named the order (new body): fetch exactly that one by id, however old;
    //  2. otherwise (or when that was not it) the wide list, matched on platform AND delivery id.
    // Same result shape as lookupDuplicate, with the matched order already processed.
    async fetchOrderFromWideWindow(target) {
      const lists = [];
      if (target.orderId) {
        try {
          const data = await executeGraphQLQuery(fetchOrderForDuplicateDoc, { id: target.orderId });
          const row = rawRowFromOrder(data?.order);
          if (row) lists.push([row]);
        } catch (error) {
          logger.warning('Manual duplicate: order by id failed, trying the wide list', { error });
        }
      }
      let found = lookupDuplicate(lists, target);
      if (found.status !== 'match') {
        const result = await executeGraphQLQuery(fetchRawOrdersWideDoc);
        lists.push(result?.getRawOrdersRealtime || []);
        found = lookupDuplicate(lists, target);
      }
      if (found.status !== 'match') return found;
      return { ...found, order: await this.toProcessedOrder(found.order, this.db) };
    },

    async forceReconnectAllChannels() {
      logger.info('Force reconnecting all channels');
      const channelConfigs = this.getChannelConfigs();
      
      for (const config of channelConfigs) {
        this.cleanupChannelConnection(config.name);
        await this.initializeChannel(config);
      }
    },
    
    getChannelConfigs() {
      // Return the same channel configurations used in initializeRealtimeHandling
      if (!this.restaurant?.id) {
        logger.warning('getChannelConfigs called but restaurant is not available yet');
        return [];
      }
      return [
        {
          name: 'orders',
          path: `orders/${this.restaurant.id}`,
          // The ledger's coalesced resync ACKs only after this refetch, so a failure must reach it.
          handler: (event) => this.fetchRawOrders(event, { rethrow: true }),
          fallback: {
            // Legacy 30s full-refetch polling, now OFF by default: recovery while
            // disconnected is the resync heartbeat (targeted ledger query instead of
            // refetching the whole order state every 30s even when nothing happened).
            // Emergency per-device opt-in without a deploy:
            // localStorage.bronze_enable_order_polling='1' + reload.
            enabled: localStorage.getItem('bronze_enable_order_polling') === '1',
            interval: 30000,
            handler: this.fetchRawOrders
          }
        },
        {
          name: 'notifications_global',
          path: `notifications/global`,
          handler: this.handleNotification,
          fallback: {
            enabled: false
          }
        },
        {
          name: 'notifications_client',
          path: `notifications/${this.restaurant.client}`,
          handler: this.handleNotification,
          fallback: {
            enabled: false
          }
        },
        {
          name: 'notifications_restaurant',
          path: `notifications/${this.restaurant.id}`,
          handler: this.handleNotification,
          fallback: {
            enabled: false
          }
        },
        {
          name: 'actions_device',
          path: `actions/${this.deviceId}`,
          handler: this.handleAction,
          fallback: {
            enabled: false
          }
        }
      ];
    },
    
    handleNotification(data) {
      logger.info('Notification received:', data);
      
      if (data.event && data.event.type === 'show_modal') {
        logger.info('handleNotification - Notification event received');
                
          // Process notification data
          const notification = {
            id: data.event.id,
            source: data.event.source,
            timeout: data.event.timeout,
            title: data.event.title,
            body: data.event.body,
            options: data.event.options,
            option_ids: data.event.option_ids,
            image: data.event.image,
            endpoint: data.event.endpoint,
            type: data.event.type,
          };
          
          // Add notification to queue with a unique identifier
          notification.queueId = Date.now() + '-' + Math.random().toString(36).substr(2, 9);
          this.notificationQueue.push(notification);
          logger.info(`Notification added to queue. ID: ${notification.id}, QueueID: ${notification.queueId}, Queue length: ${this.notificationQueue.length}`);
          
          // Log the current queue state for debugging
          this.logQueueState();
          
          // If no notification is currently displayed, show this one
          if (!this.currentNotification) {
            this.showNextNotification();
          }
      }
    },
    
    handleNotificationClosed(notificationId) {
      logger.console(`handleNotificationClosed - Notification closed: ${notificationId}`);
      
      // Log before clearing the current notification
      if (this.currentNotification) {
        logger.info(`Closing notification: ${this.currentNotification.id}, QueueID: ${this.currentNotification.queueId}`);
      }
      
      this.currentNotification = null;
      
      // Log the queue state
      this.logQueueState();
      
      // Show next notification if available
      this.showNextNotification();
    },

    handleNotificationOption(data) {
      // When an option is selected, we need to close the notification
      // The data can be either {notificationId, option} or just notificationId for backward compatibility
      const notificationId = data.notificationId || data;
      const option = data.option || 'unknown';
      
      logger.console(`handleNotificationOption - Option '${option}' selected for notification: ${notificationId}`);
      
      // Log before clearing the current notification
      if (this.currentNotification) {
        logger.info(`Closing notification after option selection: ${this.currentNotification.id}, QueueID: ${this.currentNotification.queueId}`);
      }
      
      // Clear the current notification immediately
      this.currentNotification = null;
      
      // Log the queue state
      this.logQueueState();
      
      // Show next notification if available
      this.showNextNotification();
      
      // We don't call handleNotificationClosed here because the NotificationComponent no longer emits that event for option clicks
    },
    
    showNextNotification() {
      // If there are notifications in the queue, show the next one
      if (this.notificationQueue.length > 0) {
        // Get the next notification without removing it yet
        const nextNotification = this.notificationQueue[0];
        
        logger.info(`Showing next notification. ID: ${nextNotification.id}, QueueID: ${nextNotification.queueId}, Remaining in queue: ${this.notificationQueue.length}`);
        
        // Remove it from the queue and set as current
        this.currentNotification = this.notificationQueue.shift();
        
        // Log the updated queue state
        this.logQueueState();
      } else {
        logger.info('No more notifications in queue');
      }
    },
    
    logQueueState() {
      // Log the current state of the notification queue for debugging
      if (this.notificationQueue.length > 0) {
        logger.info('Current notification queue:');
        this.notificationQueue.forEach((notification, index) => {
          logger.info(`  [${index}] ID: ${notification.id}, QueueID: ${notification.queueId}, Title: ${notification.title}`);
        });
      } else {
        logger.info('Notification queue is empty');
      }
    },

    handleAction(data) {
      logger.console(`handleAction - Action received in device channel: ${data._channelPath}`);
      const actionType = data.event?.action_type;

      if (actionType === 'refresh') {
        logger.warning('handleAction - Event received to refresh the app');
        // Full refresh of the app. Deferred so the ledger persists this event's bookkeeping first.
        setTimeout(() => window.location.reload(), 0);
        return;
      }

      if (actionType === 'reconfigure') {
        // Backend pushed a config change. The parameter/value in the event are only a hint: the DB
        // is the source of truth (the trigger fires async and non-transactionally), so we re-read
        // and hot-apply without reloading — keeping the camera session alive.
        logger.info('handleAction - Reconfigure event received, re-reading device settings', {
          parameter: data.event?.parameter,
          value: data.event?.value,
        });
        this.refetchAndApplyDeviceSettings();
        return;
      }
    },

    startFallbackOrders() {
      // Call the new method with the appropriate channel name
      this.startFallbackForChannel('orders', 30000, this.fetchRawOrders);
    },
    
    stopFallback() {
      // Call the new method with the appropriate channel name
      this.stopFallbackForChannel('orders');
    },

    async handleSessionExpired() {
        if (this.sessionExpiredHandled) return;
        this.sessionExpiredHandled = true;

        // Stop every background loop BEFORE switching screens. Leaving them running is
        // what used to turn an expired session into an infinite error loop: the 30s
        // order polling and the 15s uploader kept hammering GraphQL/S3 with no session,
        // emitting the same error forever while the tablet sat on the login screen.
        this.cleanupConnection();
        if (this.stopBackgroundUploader) {
            this.stopBackgroundUploader();
            this.stopBackgroundUploader = null;
        }
        stopAckQueue();
        stopDeviceEvents();
        this.stopPerfTelemetry();
        stopForensic();
        this.realtimeReady = false;
        stopEventLedger();
        stopAnalysisQueue();

        // Single, low-volume product signal: "this tablet needs a re-login". Replaces
        // the thousands of per-cycle errors the loops used to emit in this state.
        try {
            posthog.capture('session_expired_relogin', {
                deviceId: this.deviceId,
                restaurant: this.restaurant?.name || null,
                client: this.restaurant?.client || null,
            });
        } catch (_) { /* never let telemetry throw */ }

        logger.warning(this.$t('message.sessionExpired'));
        await this.clearDatabase();
        this.resetCoverageState();
        this.resetHelpState();
        this.currentComponent = 'LoginScreen';
    },
    async handleSwUpdate() {
      // A new service worker is available, we need to refresh the page
      logger.info(this.$t('message.newServiceWorker'));
      await this.clearDatabase();
      this.refreshApp();
    },
    refreshApp() {
      // Clear browser cache and reload the page
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
      window.location.reload(true);
    },
    // Loads the cached settings from IndexedDB as an offline-safe baseline. The backend is the
    // source of truth and overrides this right after, via applyDeviceSettingsFromRows() in
    // fetchRestaurantData(); the cache only matters when the GraphQL read fails (offline arrival).
    // Returns a promise so login can await the read before applying remote settings (the raw
    // IndexedDB request is callback-based and would otherwise race the remote apply).
    loadSettings() {
      const fallback = { key: 'settings', ...reduceDeviceSettings([]) };
      return new Promise((resolve) => {
        try {
          const transaction = this.db.transaction('settings', 'readonly');
          const store = transaction.objectStore('settings');
          const request = store.get('settings');

          request.onsuccess = (event) => {
            this.settings = event.target.result || fallback;
            this.deviceSettingsBaseline = { ...this.settings };
            resolve();
          };

          request.onerror = (event) => {
            logger.error(`${this.$t('message.errorLoadingSettings')}`, { error: event.target.error });
            this.settings = fallback;
            this.deviceSettingsBaseline = { ...this.settings };
            resolve();
          };
        } catch (error) {
          logger.error('Error reading cached settings', { error });
          this.settings = fallback;
          this.deviceSettingsBaseline = { ...this.settings };
          resolve();
        }
      });
    },
    // Applies settings locally and persists them to the IndexedDB cache WITHOUT pushing back to the
    // backend. Used for backend-originated config (login + reconfigure) so we never echo.
    updateLocalSettings(newSettings) {
      this.settings = newSettings;
      try {
        const transaction = this.db.transaction('settings', 'readwrite');
        const store = transaction.objectStore('settings');
        store.put(newSettings); // newSettings must carry the 'settings' keyPath
      } catch (error) {
        logger.error('Error persisting settings to cache', { error });
      }
    },
    // Reduce backend device_setting rows and merge them over the current settings (backend wins).
    // Replacing the object lets the camera components' watchers hot-apply the change. A non-array
    // (offline / missing) leaves the cached settings untouched.
    applyDeviceSettingsFromRows(rows) {
      if (!Array.isArray(rows)) return;
      const remote = reduceDeviceSettings(rows);
      this.updateLocalSettings({ ...this.settings, key: 'settings', ...remote });
      // The backend is now the known baseline; later user edits diff against it.
      this.deviceSettingsBaseline = { ...this.settings };
    },
    // On login, reconcile local config with the backend. The first run migrates the tablet's local
    // config up to the backend (so nothing is lost); afterwards the backend is the source of truth.
    async reconcileDeviceSettingsOnLogin(rows) {
      if (!Array.isArray(rows)) return; // offline / missing -> keep cache, retry next login
      if (this.isDeviceSettingsMigrationNeeded()) {
        await this.migrateLocalSettingsToBackend(rows);
      } else {
        this.applyDeviceSettingsFromRows(rows);
      }
    },
    isDeviceSettingsMigrationNeeded() {
      if (!this.deviceId) return false;
      try {
        return !localStorage.getItem(`bronze:settingsMigrated:${this.deviceId}`);
      } catch (e) {
        return false; // storage unavailable -> skip rather than risk re-running every login
      }
    },
    markDeviceSettingsMigrated() {
      try {
        localStorage.setItem(`bronze:settingsMigrated:${this.deviceId}`, '1');
      } catch (e) { /* private mode / disabled storage: best effort */ }
    },
    // Upload the tablet's local config to the backend for params the backend hasn't been set on, so
    // no device loses its existing configuration on rollout. One-time per device (flag-guarded).
    async migrateLocalSettingsToBackend(rows) {
      const { merged, writes } = planFirstRunMigration(this.settings, rows);
      // Adopt the reconciled config locally (local where backend was untouched, backend otherwise).
      this.updateLocalSettings({ ...this.settings, key: 'settings', ...merged });
      this.deviceSettingsBaseline = { ...this.settings };

      if (!writes.length) {
        this.markDeviceSettingsMigrated();
        logger.info('First-run device-settings migration: nothing to migrate');
        return;
      }

      logger.info(`First-run device-settings migration: uploading ${writes.length} local value(s)`, { writes });
      const results = await Promise.allSettled(
        writes.map(({ parameter, value }) => setDeviceSetting(parameter, value))
      );
      const allOk = results.every((r) => r.status === 'fulfilled' && r.value?.setDeviceSetting);
      if (allOk) {
        this.markDeviceSettingsMigrated();
        logger.success('First-run device-settings migration complete');
      } else {
        // Leave the flag unset so it retries next login. Re-running is safe: params that DID persist
        // are no longer at default, so they won't be re-migrated/clobbered.
        logger.warning('First-run device-settings migration partially failed; will retry next login', { results });
      }
    },
    // Re-reads the device config from the backend and hot-applies it. Triggered by a `reconfigure`
    // push (and reused as a manual resync). Best-effort: a failure keeps the current settings.
    async refetchAndApplyDeviceSettings() {
      try {
        const result = await executeGraphQLQuery(fetchDeviceSettingsDoc);
        const rows = result?.currentDevice?.deviceSettings;
        this.applyDeviceSettingsFromRows(rows);
        logger.info('Device settings re-applied after reconfigure', { count: rows?.length ?? 0 });
      } catch (error) {
        logger.error('Failed to re-read device settings on reconfigure', { error });
      }
    },
    // User changed a setting on the tablet: apply + cache locally immediately (keeps the camera
    // preview reactive), then debounce the backend push. The exposure slider fires on every drag
    // step, so a trailing debounce coalesces a drag into a single UPDATE and avoids out-of-order
    // writes to the same (device, parameter) row.
    updateSettings(newSettings) {
      this.updateLocalSettings(newSettings);
      this.queueDeviceSettingsSync();
    },
    queueDeviceSettingsSync() {
      clearTimeout(this.deviceSettingsSyncTimer);
      this.deviceSettingsSyncTimer = setTimeout(() => this.flushDeviceSettingsSync(), 400);
    },
    // Push the params that changed vs. the last known backend state. updatedFromDevice = true is the
    // echo guard (the trigger only fires on backend writes, so our own write never bounces back).
    flushDeviceSettingsSync() {
      if (!this.deviceId) return;
      const writes = diffSettingsToParameterWrites(this.deviceSettingsBaseline, this.settings);
      if (!writes.length) return;
      for (const { parameter, value } of writes) {
        reportForensicEvent('config_change', { parameter, value, source: 'local' });
        setDeviceSetting(parameter, value).catch((error) => {
          logger.error(`Failed to push device setting '${parameter}'`, { error, parameter, value });
        });
      }
      // Advance the baseline so a redundant flush is a no-op and later edits only push the next delta.
      this.deviceSettingsBaseline = { ...this.settings };
    },
    handleStaleCameraId(staleId) {
      if (this.settings.selectedCameraId === staleId) {
        this.updateSettings({ ...this.settings, selectedCameraId: null });
      }
    },
    async handleLoginSuccess(user) {
      if (__BRONZE_DEMO__) {
        // Demo mode: populate a network-free session from mock data only.
        const { setupDemoSession } = await import('./demo');
        await setupDemoSession(this);
        return;
      }
      const startedAt = Date.now();
      this.startupStatus = 'fetching';
      try {
        this.deviceId = user.username;
        this.db = await openDatabase('BronzeTrackerDB', 3);
        // Load the cached config first (offline baseline), then fetch the restaurant — which also
        // pulls the device settings and overrides the cache with the backend source of truth.
        await this.loadSettings();
        const deviceOutcome = await this.fetchRestaurantData();

        if (this.restaurant) {
          // Live sales demo (src/liveDemo/): «Modo demo» is for Bronze's own client only. A flag that is still
          // 'on' here (the tablet moved to another client, stale storage) is turned off and the app restarts
          // once, so PostHog and the real order sources run as they should. First, before anything else of
          // this boot reads the flag; the restart cannot loop (flag.js reloads only when the write held).
          if (enforceLiveDemoGate(this.restaurant)) {
            window.location.reload();
            return;
          }
          // ui_locale here is already fetchRestaurantData's post-login value (it runs first,
          // above) — segments PostHog persons by the locale actually running, not just
          // $browser_language (navigator.language).
          posthog.setPersonProperties({'restaurant':this.restaurant, 'client':this.restaurant.client, 'ui_locale': this.i18nLocale})
          // restaurant_id lets the coverage experiment assign `photo-coverage-alerts` variants
          // per STORE (both tablets of a kitchen must land in the same arm). Same call as
          // before, so it costs no extra flag request.
          posthog.setPersonPropertiesForFlags({'client': this.restaurant.client, 'restaurant_id': this.restaurant.id})
          window.Intercom('update', { user_id: this.deviceId, Restaurant: this.restaurant.name, language_override: this.restaurant.clientByClient.language });

          // Is this the first login of THIS tablet? Decided from the last-login record as it was
          // BEFORE this login refreshes it (below), then remembered per device; only tablets are
          // offered the periscope tutorial (the published demo returned above, so it never is), and
          // only on a FRESH install: the origin recorded at startup (src/bootInstallOrigin.js) is read
          // inside, so a tablet that merely updated is never offered it.
          const periscopeOffer = periscopeTutorialOnLogin({
            storage: window.localStorage,
            deviceId: this.deviceId,
            lastLogin: readLastLogin(window.localStorage),
            isTablet: window.innerWidth >= 768,
          }).offer;

          // Persist this good login so the LoginScreen help button can still name the restaurant
          // after a session expires. Deliberately NOT the 'device_id' key (LoginScreen clears that
          // on an auth failure); this one outlives the failure — that is the whole point.
          rememberLastLogin(window.localStorage, { deviceId: this.deviceId, restaurant: this.restaurant.name });

          // Initialize and start background uploader only once with all required data.
          // Keep the cleanup fn so handleSessionExpired can stop the loop, and stop any
          // previous instance first so a re-login never stacks a second 15s loop.
          setupBackgroundUploader(this.db);
          if (this.stopBackgroundUploader) this.stopBackgroundUploader();
          this.stopBackgroundUploader = startBackgroundUploader(this.restaurant.id, this.deviceId);
          this.sessionExpiredHandled = false;

          // Upstream realtime channels: event ACKs + operational device events.
          initAckQueue({ deviceId: this.deviceId, restaurantId: this.restaurant.id });
          initDeviceEvents({ deviceId: this.deviceId, restaurantId: this.restaurant.id });
          initForensic({ deviceId: this.deviceId, restaurantId: this.restaurant.id });
          // Performance telemetry needs the forensic channel open (it has no buffer of its own),
          // so it starts here and not in mounted().
          this.startPerfTelemetry();
          // Restore the alert budget spent before this reload (see loadCoverageHistory).
          this.coverageHistory = loadCoverageHistory(this.restaurant.id);
          // Event ledger (cursor + missed-event recovery). Must init before the channels
          // register (below), or they seed a ledger that has no device id yet.
          initEventLedger({
            deviceId: this.deviceId,
            // Single source of truth for connectivity, derived from the Events socket state
            // (+ navigator.onLine floor). Consumed by the offline UX flows via provide/inject.
            onConnectivity: (online) => {
              if (online !== this.isOnline) reportForensicEvent(online ? 'conn_online' : 'conn_offline', {});
              this.isOnline = online;
            },
            // Socket-health transitions. Amplify's keep-alive watchdog is the only thing
            // that notices a silently-dead WebSocket; every other net in this app (the
            // 'online' hook, visibilitychange, the 3-min resync heartbeat) reads
            // isConnectionAlive, which cannot see it on its own.
            onSocketState: (info) => this.handleSocketState(info),
          });
          // Analyses attempted offline queue themselves and relaunch on reconnection.
          initAnalysisQueue({
            deviceId: this.deviceId,
            restaurantId: this.restaurant.id,
            getOrder: (orderId) =>
              this.processedOrders?.find((o) => o.orderId === orderId) ||
              this.rawOrders?.find((o) => o.orderId === orderId) || null,
            hasPendingPhotos: async (orderId) => (await getPhotosByOrderId(this.db, orderId)).length > 0,
            onVerdict: (orderId, result) => this.handleQueuedVerdict(orderId, result),
          });

          // Realtime starts here, after the ACK queue and ledger are ready (not awaited: it
          // subscribes channel by channel). The `restaurant` watcher no longer does it on login.
          this.realtimeReady = true;
          this.initializeRealtimeHandling();

          // Startup succeeded: clear the watchdog's auto-reload guard so a future
          // bad session starts its retry budget from scratch.
          this.startupStatus = 'ok';
          try { sessionStorage.removeItem('startup_reload_count'); } catch (_) { /* ignore */ }
          this.trackStartup('success', Date.now() - startedAt);
          // Live sales demo (src/liveDemo/): «Modo demo» was switched on (or off) in Settings before this
          // reload. After the normal login and fetch, and BEFORE the service screen mounts and merges its
          // list with the IndexedDB, the three demo orders replace the list (on) or the demo records are
          // removed (off). The real fetch is muted while on (fetchRawOrders, processEvent, processQuery).
          if (liveDemoMode()) {
            try {
              const { setupLiveSession } = await import('./liveDemo/live');
              await setupLiveSession(this, liveDemoMode());
            } catch (error) {
              logger.error('Live demo setup failed', { error });
            }
          }
          // A tutorial link (opened before the session was up) also lands on its tutorial; a first
          // login keeps its periscope welcome.
          const tutorialLink = this.tutorialLinkPending;
          this.tutorialLinkPending = null;
          if (periscopeOffer || tutorialLink === TUTORIAL_LINKS.PERISCOPE) {
            this.openPeriscopeTutorial(periscopeOffer ? 'first_login' : 'link');
          } else if (tutorialLink === TUTORIAL_LINKS.PHOTO_VIDEO && this.photoVideo) {
            this.openPhotoVideoTutorial('link');
          } else if (this.showQrGunTutorial && readQrGunResume(window.localStorage, Date.now())) {
            // Android closed the app while the person was in the tablet's Bluetooth settings (pairing the
            // gun): pick the guide up at «Vuelve a Bronze».
            this.openQrGunTutorial('resume', 'back');
          } else {
            this.currentComponent = 'CameraScreen';
          }
          // A help remedy reloaded the page: come back to its "did that fix it?" once the
          // kitchen screen is up (no-op when nothing is pending).
          this.resumeHelpFollowUp();

          // Warm the manual-order caches (platforms + own-delivery presets) in the background, off
          // the critical path, so the first tap on "add manual order" is instant. Best-effort only.
          this.warmManualOrderCache();
        } else if (deviceOutcome === 'device_missing') {
          // The backend answered, and knows no device by this username: the id was renamed or deleted
          // while this tablet kept its cached token (valid up to a day). Reloading cannot fix it, so
          // LoginScreen signs out and shows the form, and the local order data of the old id goes.
          this.trackStartup('device_missing', Date.now() - startedAt);
          await this.clearDatabase();
          this.startupStatus = 'device-gone';
        } else {
          // currentDevice resolved but has no restaurant attached (config issue).
          this.startupStatus = 'failed';
          this.trackStartup('no_restaurant', Date.now() - startedAt);
          logger.error(`Failed to fetch restaurant data for user ${user.username}. Is the device associated to a restaurant?`);
        }
      } catch (error) {
        // fetchRestaurantData exhausted its retries (slow/failed AppSync). Surface
        // a retry affordance instead of leaving LoginScreen stuck on the spinner.
        this.startupStatus = 'failed';
        this.trackStartup('error', Date.now() - startedAt);
        logger.error(`Error during login success handling`, {error});
      }
    },
    warmManualOrderCache() {
      // Prefetch the manual-order platform list + own-delivery presets so the modal opens instantly
      // the first time. Strictly best-effort and OFF the startup critical path: deferred to idle
      // time, never awaited, errors swallowed. It only populates the module-level TTL caches in
      // graphql.js (keyed by the same device_id the modal uses); if the TTL later expires, the modal
      // still revalidates on open (stale-while-revalidate), exactly as before — so this never blocks.
      const run = () => {
        Promise.allSettled([
          fetchDeliveryPlatformsForCurrentDevice(),
          fetchOwnDeliveryCodePresets(),
        ]);
      };
      try {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(run, { timeout: 4000 });
        } else {
          setTimeout(run, 1500);
        }
      } catch (_) {
        setTimeout(run, 1500);
      }
    },
    handleRetryStartup() {
      // Manual retry from the LoginScreen watchdog: a full reload is the most
      // reliable reset (re-runs Amplify config, auth check and the fetch chain).
      // A deliberate tap also resets the auto-reload budget so the next load can
      // attempt to self-heal again.
      logger.info('Manual startup retry requested, reloading app');
      try { sessionStorage.removeItem('startup_reload_count'); } catch (_) { /* ignore */ }
      window.location.reload();
    },
    /**
     * Performance telemetry for the session. Three things, all passive:
     *  - the LoAF observer, the only signal that names WHO blocked the main thread;
     *  - one device profile, because today we have no idea what hardware the fleet runs on;
     *  - a heartbeat that also leaves a synchronous tombstone, which is how a renderer killed for
     *    memory gets counted at all — that death runs no handler and flushes no queue.
     */
    startPerfTelemetry() {
      try {
        startLoafObserver();
        reportForensicEvent('app_start', appStartProps());
        // The profile carries the device's COLD baseline for the CPU probe. Without it a later
        // `cpu_bench_ms: 800` is unreadable — 800 could be a throttled fast tablet or a healthy
        // slow one. With it, every heartbeat is a ratio against this same device at startup,
        // which is the comparison that actually detects a shift going bad.
        collectDeviceProfile()
          .then(async (profile) => reportForensicEvent('device_profile', {
            ...profile,
            cpu_bench_ms: await cpuBenchMs(),
          }))
          .catch(() => {});
        this.perfHeartbeatSince = performance.now();
        this.perfLastActivityPhotos = -1;
        this.perfHeartbeat = setInterval(() => this.emitSessionHealth(), PERF_HEARTBEAT_MS);
        // A hide is the last moment we are guaranteed to run. pagehide is NOT: the kill we care
        // about most does not fire it.
        this.perfOnHide = () => { if (document.visibilityState === 'hidden') this.writePerfTombstone(true); };
        document.addEventListener('visibilitychange', this.perfOnHide);
      } catch (error) {
        logger.console('perf telemetry failed to start', error);
      }
    },
    stopPerfTelemetry() {
      clearInterval(this.perfHeartbeat);
      this.perfHeartbeat = null;
      if (this.perfOnHide) document.removeEventListener('visibilitychange', this.perfOnHide);
      this.perfOnHide = null;
      stopLoafObserver();
    },
    /** Synchronous, tiny, and written often: it is a crash detector, not a report. */
    writePerfTombstone(clean) {
      writeTombstone({
        page_id: getPageId(),
        since_load_ms: Math.round(performance.now()),
        photos: getPhotosSinceLoad(),
        ts: Date.now(),
        clean: !!clean,
      });
    },
    async emitSessionHealth() {
      try {
        // Always refresh the tombstone, even on a quiet tick: it is what turns a kill into data.
        this.writePerfTombstone(false);
        const photos = getPhotosSinceLoad();
        const idle = photos === this.perfLastActivityPhotos;
        // These tablets are on 24/7 and two thirds of the day nothing happens. A flat 5-minute
        // beat would be mostly night-time noise, so a quiet session reports six times less often.
        if (idle && performance.now() - (this.perfLastBeatAt || 0) < PERF_IDLE_HEARTBEAT_MS) return;
        this.perfLastBeatAt = performance.now();
        this.perfLastActivityPhotos = photos;
        const since = this.perfHeartbeatSince;
        this.perfHeartbeatSince = performance.now();
        // Churn since the last beat, read and reset together: a flapping tab or link shows here.
        const visChanges = this.perfVisChanges;
        const onlineChanges = this.perfOnlineChanges;
        this.perfVisChanges = 0;
        this.perfOnlineChanges = 0;
        // Close the LoAF window BEFORE running the CPU probe. The probe spawns and tears down a
        // worker, and that touches the main thread — inside the window it would be attributing
        // our own measurement to the app, which is the one thing this module promises not to do.
        const loaf = loafSince(since);
        // The throttling probe, and the only signal here that would move when a tablet is
        // thermally capped: measured on a Lenovo TB336FU a capture then costs 3.5x more while the
        // heap is flat, memory is free, the network is fine and the main thread is not blocked.
        const cpuBench = await cpuBenchMs();
        reportForensicEvent('session_health', {
          uptime_ms: Math.round(performance.now()),
          orders_count: this.processedOrders?.length ?? null,
          storage_usage_mb: await storageUsageMb(),
          heap_mb: heapMb(),
          dom_nodes: domNodes(),
          cpu_bench_ms: cpuBench,
          visible: document.visibilityState === 'visible',
          online: this.isOnline,
          vis_changes: visChanges,
          online_changes: onlineChanges,
          // The socket as the socket reports it, read locally (no request): whether it is open,
          // how long since any frame, how long this one has lived. What the Hub says is not
          // enough, it was wrong for hours in the 19.8 h deaf episode.
          ...socketHealthFields(getSocketProbe(), Date.now()),
          ...(loaf || {}),
        });
      } catch (error) {
        logger.console('session_health failed', error);
      }
    },
    trackStartup(outcome, totalMs) {
      // Single, low-volume event fired once per app open — this is the signal we
      // use to detect slow/stuck startups in PostHog. Never let telemetry throw.
      try {
        posthog.capture('app_startup', {
          outcome,
          totalMs,
          deviceId: this.deviceId,
          restaurant: this.restaurant?.name || null,
          client: this.restaurant?.client || null,
          appVersion: this.appVersion,
          // The vue-i18n locale actually running, vs. the restaurant's configured
          // client.language and the browser's own languages — lets PostHog segment
          // startups by what locale really rendered, not just navigator.language.
          ui_locale: this.i18nLocale,
          html_lang: (typeof document !== 'undefined' && document.documentElement.getAttribute('lang')) || null,
          browser_languages: (typeof navigator !== 'undefined' && Array.isArray(navigator.languages))
              ? navigator.languages.slice(0, 5).join(',')
              : null,
          client_language: this.restaurant?.clientByClient?.language || null,
          // What PrimeVue costs at boot and whether its licence holds on this device's clock:
          // totalMs starts after both, so it cannot see them (src/ui/install.js).
          ...uiStartupTelemetry(),
        });
      } catch (_) { /* ignore */ }
      if (outcome !== 'success') {
        logger.warning(`App startup outcome: ${outcome} (${totalMs}ms)`, { outcome, totalMs });
      } else if (totalMs > SLOW_STARTUP_MS) {
        logger.warning(`Slow app startup (${totalMs}ms)`, { totalMs });
      }
    },
    openQueueScreen() {
      this.previousComponent = this.currentComponent;
      this.currentComponent = 'QueueScreen';
    },
    goBack() {
      if (this.currentComponent === 'QueueScreen' && this.previousComponent) {
        this.currentComponent = this.previousComponent;
        this.previousComponent = null;
      } else {
        this.currentComponent = 'CameraScreen';
      }
    },
    async processOrders() {
      this.processedOrders = this.rawOrders.map(order => ({
        ...order,
        photoCount: order.order?.photos?.length || 0,
        analysisResult: order.order?.orderAnalyses?.[0] 
          ? { isCorrect: order.order.orderAnalyses[0].isCorrect }
          : null
      }));
    },
    // Resolves to classifyCurrentDevice()'s verdict on the backend's answer ('ok' | 'device_missing' |
    // 'no_restaurant'); a call that never gets an answer throws instead.
    async fetchRestaurantData() {
      const startedAt = Date.now();
      let result;
      try {
        // Timeout each attempt and retry a few times so a transient AppSync blip
        // self-heals instead of permanently freezing the app on "Cargando...".
        const ask = () => retry(
          () => withTimeout(
            executeGraphQLQuery(fetchRestaurantDoc),
            RESTAURANT_FETCH_TIMEOUT_MS,
            'fetchRestaurantData'
          ),
          RESTAURANT_FETCH_ATTEMPTS,
          RESTAURANT_FETCH_RETRY_DELAY_MS
        );
        result = await ask();
        if (classifyCurrentDevice(result) === 'device_missing') {
          await new Promise((resolve) => setTimeout(resolve, DEVICE_MISSING_RECHECK_MS));
          result = await ask();
        }
      } catch (error) {
        const elapsed = Date.now() - startedAt;
        logger.error(`Error while fetching restaurant data after ${RESTAURANT_FETCH_ATTEMPTS} attempts`, { error, method: 'fetchRestaurantData', errorType: 'graphql', elapsed });
        this.restaurant = null;
        throw error; // let handleLoginSuccess surface the retry affordance
      }

      const elapsed = Date.now() - startedAt;
      const outcome = classifyCurrentDevice(result);
      this.restaurant = result?.currentDevice?.restaurant || null;

      if (outcome === 'device_missing') {
        logger.warning(`The backend knows no device by the signed-in id: ${this.deviceId}`, { method: 'fetchRestaurantData', errorType: 'device-missing' });
      } else if (!this.restaurant) {
        logger.error(`No restaurant assigned to deviceID: ${this.deviceId}`, { method: 'fetchRestaurantData', errorType: 'db-config' });
      } else {
        this.restaurant.deviceTag = result.currentDevice.tag;
        if (elapsed > SLOW_STARTUP_MS) {
          logger.warning(`Slow restaurant data fetch (${elapsed}ms)`, { method: 'fetchRestaurantData', elapsed });
        }
        logger.console(`Restaurant data found for deviceID: ${this.deviceId}: ${JSON.stringify(this.restaurant)}`);

        // Reconcile the camera config with the backend. The first login after this feature shipped
        // runs a one-time local->backend migration so no tablet loses its existing config; after
        // that the backend is the source of truth.
        await this.reconcileDeviceSettingsOnLogin(result.currentDevice.deviceSettings);

        // Update i18n locale based on clientByClient.language
        if (this.restaurant.clientByClient && this.restaurant.clientByClient.language) {
          const language = this.restaurant.clientByClient.language;
          // Check if the language is supported in our i18n messages
          if (this.i18nAvailableLocales.includes(language)) {
            logger.console(`Setting language to: ${language}`);
            this.i18nLocale = language;
            // Keep <html lang> in sync with the restaurant's language (accessibility)
            document.documentElement.setAttribute('lang', language);
            // Keep the ui_locale person property current too, so a locale that changes
            // after the initial login (e.g. a later re-fetch) still segments correctly.
            try { posthog.setPersonProperties({ ui_locale: language }); } catch (_) { /* ignore */ }
          } else {
            logger.warning(`Language ${language} not available, using default locale`);
          }
        }
      }
      this.$emit('set-restaurant', this.restaurant);
      return outcome;
    },
    async fetchRawOrders(event = null, { rethrow = false } = {}) {
      /**
       * This function implements the order fetching and processing. The function is called due to a realtime event or direct function call.
       * If event is provided, it processes the event to get order details.
       * If no event is provided, it fetches the orders from the database using AppSync.
       */

      // Live sales demo on: the list is the three demo orders; the real source (initial fetch, polling,
      // realtime, resync) neither adds nor updates nor removes anything. Only events the demo injects pass.
      if (liveDemoOn() && !event?.event?._liveDemo) return;

      try {
        const dbInstance = this.db;
        if (!dbInstance) {
          throw new Error('Database instance is not available');
        }
        if (event) {
          await this.processEvent(event, dbInstance)
        } else {
          await this.processQuery(dbInstance);
        }
        logger.console('fetchRawOrders - Fetched orders')
        logger.console(this.rawOrders)
        logger.console("fetchRawOrders - Processed orders")
        logger.console(this.processedOrders)
      } catch (error) {
        logger.error('fetchRawOrders - Error fetching and processing orders:', {error});
        if (rethrow) throw error;
      }
    },
    async processEvent(data, dbInstance) {
      /**
       * This function processes the event to get order details.
       * The event is received from the realtime connection and it only contains information about the specific order.
       * There are three types of events: new_order, photo_created and order_analysis.
       */

      // Live sales demo on: no real event touches the list, toasts a verdict or a rider (see fetchRawOrders).
      if (liveDemoOn() && !data?.event?._liveDemo) return;

      // Noted BEFORE the list changes: a full fetch in flight may hold a snapshot that predates this event,
      // and one that settles while the change is still being written must fetch again too. A spare refetch
      // (the event fails, or the fetch already had it) is the cheap side of that trade.
      this.ordersQueryGuard.noteEvent();

      logger.info('processEvent - Event of type: ' + data.event.type + ' received');
      logger.console('processEvent - EVENT')
      logger.console(data)
      
      // Check if the order is in the database
      let localOrder = await getOrderFromIndexedDB(dbInstance, data.event.orderId);

      if (localOrder == null) {
        // If the event is a rider check-in for an unknown order, refresh all orders instead of creating a dummy entry
        const hasRiderEvent = data.event.order?.orderEvents?.some(e => e.event === 'RIDER_CHECK_IN');
        if (hasRiderEvent && data.event.type === EventTypes.ORDER_UPDATE) {
          logger.info('processEvent - Rider check-in for unknown order, refreshing all orders');
          await this.processQuery(dbInstance);
          return;
        }
        await this.createNewOrderEvent(data, localOrder, dbInstance);
      } else {
        await this.updateOrderEvent(data, localOrder, dbInstance);
      }

      // As we need to modify this.rawOrders and this.processedOrders for watches to work, we need to make a deep copy of them
      this.rawOrders = JSON.parse(JSON.stringify(this.rawOrders));
      this.processedOrders = JSON.parse(JSON.stringify(this.processedOrders));
    },
    async createNewOrderEvent(data, localOrder, dbInstance){
      /**
       * This function creates a new order when dealing with a realtime event.
       * The order is not in the database, so we need to create a new order object and save it to the database.
       * In addition, we need to add the order to the rawOrders and processedOrders arrays.
       */

      logger.console('processEvent - Order not found in DB. Creating new order with key: ' + data.event.orderId);

      // Get orderAnalysis ID from the event, else keep the one already stored locally, else mint
      // one. Never re-mint over a stored id: the photo is uploaded carrying it and
      // /item-comparison finds the photos by it (see resolveOrderAnalysisId).
      const orderAnalysis = resolveOrderAnalysisId(data.event.order.orderAnalyses, localOrder, this.restaurant.proActivated);

      // Preserve orderEvents or initialize empty
      data.event.order.orderEvents = data.event.order.orderEvents || [];

      // Casting the photos and orderAnalyses to arrays to avoid errors when saving to the database
      data.event.order.photos = new Array(0);
      data.event.order.orderAnalyses = {
        id: orderAnalysis,
        isCorrect: null,
        appResponse: null,
      };

      // Get ticket content
      let orderContent = {
        "hierarchical": [],
        "grouped": []
      };
      
      // Safely parse orderDigitalTicket if it exists
      if (data.event.order && data.event.order.orderDigitalTicket) {
        try {
          orderContent = {
            "hierarchical": data.event.order.orderDigitalTicket.hierarchical ? JSON.parse(data.event.order.orderDigitalTicket.hierarchical) : [],
            "grouped": data.event.order.orderDigitalTicket.grouped ? JSON.parse(data.event.order.orderDigitalTicket.grouped) : []
          };
        } catch (error) {
          logger.console('Error parsing orderDigitalTicket', {error});
        }
      }

      // Create a new order object
      localOrder = {
        ...data.event,
        key: data.event.orderId,
        orderAnalysis: orderAnalysis,
        photoCount: data.event.order.photos.length || 0,
        analysisResult: localOrder ? JSON.parse(localOrder.analysisResult || 'null') : null,
        orderContent: orderContent,
      };

      // Save the order to the database
      saveOrderToIndexedDB(dbInstance, localOrder);

      // Add the order to the rawOrders and processedOrders arrays. This variables are watched by the OrderSelection component.
      this.rawOrders = [...this.rawOrders, data.event];
      this.processedOrders = [...this.processedOrders, localOrder];
    },
    async updateOrderEvent(data, localOrder, dbInstance){
      /**
       * This function updates an order when dealing with a realtime event.
       * The order is already in the database, so we need to update the order object and save it to the database.
       * In addition, we need to update the rawOrders and processedOrders arrays.
       */

      logger.console('processEvent - Order found in DB. Updating order with key: ' + data.event.orderId);


      // Update the photos if the event is a photo_created event.
      if (data.event.type === EventTypes.PHOTO_CREATED) {
        logger.console('processEvent - Photo created. Adding to order photos');

        // We might be processing the event of an image that has been captured using the same device.
        // The process photo pipeline already saves the photo to the database, so we don't need to save it again.
        // This means that we have to check if the photo is already in the order using the photoKey.
        let photoKey = data.event.order.photos.url.split('/').pop().split('_')[0];
        let orderAnalysis = resolveOrderAnalysisId(data.event.order.orderAnalyses, localOrder, this.restaurant.proActivated);

        // The photos list is heterogeneous by design: a local capture stores the key as a
        // string, this handler stores `{ url }` for photos taken elsewhere. Comparing raw
        // entries only ever matched the string form, so after a full fetch (which replaces
        // the list with the server's `{url}` objects) a re-delivered photo_created — an
        // AppSync replay on reconnect, say — matched nothing and pushed a duplicate,
        // inflating photoCount permanently. Normalise both sides to the same key.
        const keyOfPhoto = (photo) => (typeof photo === 'string'
          ? photo
          : String(photo?.url || '').split('/').pop().split('_')[0]);

        // If the photo is not in the order, we add it.
        if (!localOrder.order.photos.some(photo => keyOfPhoto(photo) === photoKey)) {
          logger.console('processEvent - Photo ' + photoKey + ' not found in order. Adding to order photos');
          localOrder.order.photos.push({
            url: data.event.order.photos.url,
          });
        }
        localOrder.order.hasPhoto = true;
        localOrder.photoCount = localOrder.order.photos.length;

        // When a photo event comes it also has order analysis ID
        localOrder.orderAnalysis = orderAnalysis;
        if (data.event.order.orderAnalyses && data.event.order.orderAnalyses.length > 0) {
          if (!localOrder.order.orderAnalyses) {
            localOrder.order.orderAnalyses = {};
          }
          localOrder.order.orderAnalyses.id = data.event.order.orderAnalyses[0].id;
        }
        // Check if the analysisResult is a string and parse it if it is.
        if (typeof localOrder.analysisResult === 'string') {
          localOrder.analysisResult = JSON.parse(localOrder.analysisResult);
        }
        else {
          localOrder.analysisResult = null;
        }
      } 
      // Update the order with the new analysis if the event is an order_analysis event.
      else if (data.event.type === EventTypes.ORDER_ANALYSIS) {
        // Update the order with the new analysis
        logger.console('processEvent - Updating order with new analysis information');
        let orderAnalysis = resolveOrderAnalysisId(data.event.order.orderAnalyses, localOrder, this.restaurant.proActivated);
        localOrder.orderAnalysis = orderAnalysis;
        // Check if the analysisResult is a string and parse it if it is.
        if (typeof localOrder.analysisResult === 'string') {
          localOrder.analysisResult = JSON.parse(localOrder.analysisResult);
        }
        else {
          localOrder.analysisResult = null;
        }
        localOrder.order.orderAnalyses = data.event.order.orderAnalyses[0];
      }
      else if (data.event.type === EventTypes.ORDER_UPDATE) {
        //Update the order manualIsCorrect only if the incoming value is not null (rider events send null)
        if (data.event.order.manualIsCorrect !== null && data.event.order.manualIsCorrect !== undefined) {
          localOrder.order.manualIsCorrect = data.event.order.manualIsCorrect;
        }
        // RBE pilot: refresh the cached customer name / delivery address from the update event. Name:
        // guard against null. Address: truthiness — an empty "" must never clobber a good address, since
        // Burger King falls back to the name while the address is empty; only a non-empty value updates.
        if (data.event.recipientName != null) {
          localOrder.recipientName = data.event.recipientName;
        }
        if (data.event.recipientAddress) {
          localOrder.recipientAddress = data.event.recipientAddress;
        }
        if(data.event.order.orderDigitalTicket){
          // Update the order with the new order information about ticket content
          let orderContent = {
            "hierarchical": [],
            "grouped": []
          };
          
          // Safely parse orderDigitalTicket if it exists
          if (data.event.order && data.event.order.orderDigitalTicket) {
            try {
              orderContent = {
                "hierarchical": data.event.order.orderDigitalTicket.hierarchical ? JSON.parse(data.event.order.orderDigitalTicket.hierarchical) : [],
                "grouped": data.event.order.orderDigitalTicket.grouped ? JSON.parse(data.event.order.orderDigitalTicket.grouped) : []
              };
            } catch (error) {
              logger.console('Error parsing orderDigitalTicket in ORDER_UPDATE: ' + error);
            }
          }
          
          localOrder.orderContent = orderContent;
      }
      }
      else if (data.event.type === EventTypes.ITEM_CROSS_OUT) {
        // Update the crossing out state for the item
        localOrder.orderContent.hierarchical.forEach(item => {
          logger.console('processEvent - Updating item: ' + item.sell_id + ' with crossedOutBy: ' + data.event.sellId);
          if (item.sell_id === data.event.sellId) {
            logger.console('processEvent - Item found in hierarchical: ' + item.sell_id);
            if (!('crossedOutBy' in item)) {
              item.crossedOutBy = null;
            }
            item.crossedOutBy = data.event.crossedOutBy;
          }
          // Check if the item has items inside and update them recursively
          if (item.items) {
            item.items.forEach(subItem => {
              logger.console('processEvent - Updating subitem: ' + subItem.sell_id + ' with crossedOutBy: ' + data.event.sellId);
              if (subItem.sell_id === data.event.sellId) {
                logger.console('processEvent - Item found in hierarchical: ' + subItem.sell_id);
                if (!('crossedOutBy' in subItem)) {
                  subItem.crossedOutBy = null;
                }
                subItem.crossedOutBy = data.event.crossedOutBy;
              }
            });
          }
        });
      }

      // Merge orderEvents (union by event key) - applies to ALL event types
      if (data.event.order?.orderEvents && data.event.order.orderEvents.length > 0) {
        const existingEvents = localOrder.order.orderEvents || [];
        const existingEventTypes = new Set(existingEvents.map(e => e.event));
        const hadRiderCheckIn = existingEventTypes.has('RIDER_CHECK_IN');

        for (const incomingEvent of data.event.order.orderEvents) {
          if (!existingEventTypes.has(incomingEvent.event)) {
            existingEvents.push(incomingEvent);
            existingEventTypes.add(incomingEvent.event);
          }
        }
        localOrder.order.orderEvents = existingEvents;

        // Trigger rider notification if RIDER_CHECK_IN is new
        if (!hadRiderCheckIn && existingEventTypes.has('RIDER_CHECK_IN')) {
          this.handleRiderCheckIn(localOrder);
        }

        // Exit event arrived from another tablet or the platform → mirror the hide. The OWN hand-over
        // (operator's tap, relayed here) drops the card at once. The platform's EXTERNAL_ exit gets a
        // 1-min grace to match the backend fetch (get_raw_orders_realtime external_exit_grace_minutes),
        // so it is NOT tombstoned here — checkRiderAutoDelivery hides it once the event is 1 min old.
        // Both cases reuse the delivered tombstone that filteredOrders already honors; on reload the
        // backend agrees (own excluded at once, EXTERNAL_ excluded after 1 min).
        const hasOwnExit = existingEventTypes.has('RIDER_LEFT_THE_RESTAURANT') ||
                           existingEventTypes.has('ORDER_DELIVERED_TO_CUSTOMER');
        if (hasOwnExit && !this.riderDeliveredOrderIds[data.event.orderId]) {
          this.riderDeliveredOrderIds[data.event.orderId] = Date.now();
          delete this.riderReminderTracked[data.event.orderId];
        }
      }

      // Save the order to the database
      saveOrderToIndexedDB(dbInstance, localOrder);

      // As we only have the information of the specific order, we need to update this order in the rawOrders and processedOrders arrays.
      // We need to find the index of the order in the arrays to update it.
      let updatedOrderIndex = this.rawOrders.findIndex(o => o.orderId === data.event.orderId);

      if (updatedOrderIndex !== -1) {
        logger.console('processEvent - Updating order in raw orders');
        if (data.event.type === EventTypes.PHOTO_CREATED) {
          // Update photo related information if the event is a photo_created event.
          logger.console('processEvent - Updating order in raw orders: photo_created');
          this.rawOrders[updatedOrderIndex].order.photos = localOrder.order.photos
          this.rawOrders[updatedOrderIndex].photoCount = localOrder.photoCount;
          this.rawOrders[updatedOrderIndex].order.hasPhoto = localOrder.order.hasPhoto;
        }
        else if (data.event.type === EventTypes.ORDER_ANALYSIS) {
          // Update order analysis related information if the event is an order_analysis event.
          logger.console('processEvent - Updating order in raw orders: order_analysis');
          this.rawOrders[updatedOrderIndex].orderAnalysis = localOrder.orderAnalysis;
          this.rawOrders[updatedOrderIndex].analysisResult = localOrder.analysisResult;
          this.rawOrders[updatedOrderIndex].order.orderAnalyses = localOrder.order.orderAnalyses;
        }
        // Always sync orderEvents
        if (localOrder.order.orderEvents) {
          this.rawOrders[updatedOrderIndex].order.orderEvents = localOrder.order.orderEvents;
        }
        // RBE pilot: sync the customer name / delivery address when the event carries it. order_update
        // includes them top-level; order_analysis/photo_created send null, so the guard prevents
        // wiping the value set at order creation.
        if (data.event.recipientName != null) {
          this.rawOrders[updatedOrderIndex].recipientName = data.event.recipientName;
        }
        if (data.event.recipientAddress) {
          this.rawOrders[updatedOrderIndex].recipientAddress = data.event.recipientAddress;
        }
      }
      updatedOrderIndex = this.processedOrders.findIndex(o => o.orderId === data.event.orderId);
      if (updatedOrderIndex !== -1) {
        logger.console('processEvent - Updating order in processed orders');
        if (data.event.type === EventTypes.PHOTO_CREATED) {
          // Update photo related information if the event is a photo_created event.
          logger.console('processEvent - Updating order in processed orders: photo_created');
          this.processedOrders[updatedOrderIndex].order.photos = localOrder.order.photos;
          this.processedOrders[updatedOrderIndex].photoCount = localOrder.photoCount;
          this.processedOrders[updatedOrderIndex].order.hasPhoto = localOrder.order.hasPhoto;
        }
        else if (data.event.type === EventTypes.ORDER_ANALYSIS) {
          // Update order analysis related information if the event is an order_analysis event.
          logger.console('processEvent - Updating order in processed orders: order_analysis');
          // Capture whether this order already had a verdict BEFORE the in-place mutation, so
          // we only toast a genuine pending→decided transition (not a re-emit of the same one).
          const wasDecided = this.hasTerminalVerdict(this.processedOrders[updatedOrderIndex]);
          this.processedOrders[updatedOrderIndex].orderAnalysis = localOrder.orderAnalysis
          this.processedOrders[updatedOrderIndex].analysisResult = localOrder.analysisResult
          this.processedOrders[updatedOrderIndex].order.orderAnalyses = localOrder.order.orderAnalyses;
          if (!wasDecided) {
            this.emitVerdictToastRealtime(this.processedOrders[updatedOrderIndex]);
          }
        }
        // Always sync orderEvents
        if (localOrder.order.orderEvents) {
          this.processedOrders[updatedOrderIndex].order.orderEvents = localOrder.order.orderEvents;
        }
        // RBE pilot: sync the customer name / delivery address when present (guard prevents null events wiping it).
        if (data.event.recipientName != null) {
          this.processedOrders[updatedOrderIndex].recipientName = data.event.recipientName;
        }
        if (data.event.recipientAddress) {
          this.processedOrders[updatedOrderIndex].recipientAddress = data.event.recipientAddress;
        }
      }
    },
    // One getRawOrdersRealtime row → the order object the rest of the app renders and stores
    // (IndexedDB included). Shared by processQuery (the whole list) and the one-off wide lookup of
    // handleManualDuplicate, so a transient order has exactly the shape of a listed one.
    // `persist: false` skips the IndexedDB write: processQuery saves only once its guard accepts the snapshot.
    async toProcessedOrder(order, dbInstance, { persist = true } = {}) {

      // Get the order from the local database.
      let localOrder = await getOrderFromIndexedDB(dbInstance, order.orderId);

      // Get orderAnalysis ID from the order, else keep the one already stored locally. A full
      // refetch runs on every reconnect, and the server row does not exist until the first photo
      // is notified — minting a new id here would orphan the photos already uploaded under the
      // previous one.
      const orderAnalysis = resolveOrderAnalysisId(order.order.orderAnalyses, localOrder, this.restaurant.proActivated);

      // Check if order.order.orderAnalyses length is greater than 0. If it is, we need to convert it to an object.
      // If not we create a new object
      if (order.order.orderAnalyses.length > 0) {
        order.order.orderAnalyses = order.order.orderAnalyses[0];
      }
      else {
        order.order.orderAnalyses = {
          id: orderAnalysis,
          isCorrect: null,
          appResponse: null,
        };
      }
      
      // Get ticket content
      let orderContent = null;
      if (order.order.orderDigitalTicket) {
        orderContent = {
          "hierarchical": JSON.parse(order.order.orderDigitalTicket.hierarchical),
          "grouped": JSON.parse(order.order.orderDigitalTicket.grouped)
        }
      }

      // Get crossed out items
      if (order.order.orderDigitalTicket && order.order.orderDetail) {
      let crossedOutItems = order.order.orderDetail.rawOrderDetailsContentsByOrderId
        if (crossedOutItems.length > 0) {
          for (let item of orderContent.hierarchical) {
            for (let crossedOutItem of crossedOutItems) {
              crossedOutItem = crossedOutItem.rawOrderDetailsContentCrossesBySellId;
              if (crossedOutItem.length > 0) {
                if (item.sell_id === crossedOutItem[0].sellId) {
                  item.crossedOutBy = crossedOutItem[0].crossedOutBy;
                }
              }
            }
          }
        }
      }

      localOrder = {
        ...order, 
        key: order.orderId,
        orderAnalysis: orderAnalysis,
        photoCount: order.order.photos.length || 0,
        orderContent: orderContent,
        };
      if (typeof localOrder.analysisResult === 'string') {
        localOrder.analysisResult = JSON.parse(localOrder.analysisResult);
      }
      else {
        localOrder.analysisResult = null;
      }

      if (persist) await saveOrderToIndexedDB(dbInstance, localOrder);

      return localOrder
    },
    async processQuery(dbInstance, { followUp = false } = {}){
      /**
       * This function fetches the orders from the database using AppSync.
       * The query from AppSync returns all the orders from real_time_pos_raw_data table.
       * Full fetches overlap: ordersQueryGuard drops a snapshot older than one already applied, and asks
       * for one follow-up fetch when a realtime event changed the list while this one was in flight.
       * The snapshot is persisted to IndexedDB only once the guard accepts it (CameraScreen paints from there).
       */

      // Live sales demo on: the demo orders are the list (see fetchRawOrders).
      if (liveDemoOn()) return;

      const token = this.ordersQueryGuard.begin();
      logger.info('processQuery - Fetching orders from AppSync');

      // Fetch orders from the database
      const result = await executeGraphQLQuery(fetchRawOrdersDoc);
      let fetchedOrders = result.getRawOrdersRealtime;

      // Process the orders to build the processedOrders array.
      const processedOrders = await Promise.all(fetchedOrders.map((order) => this.toProcessedOrder(order, dbInstance, { persist: false })));

      // A fetch that started later was already applied: this snapshot is older. No toasts, no assignment.
      const verdict = this.ordersQueryGuard.settle(token);
      if (verdict === 'drop') {
        logger.info('processQuery - Dropping a stale orders snapshot (a later fetch was already applied)');
        return;
      }

      // Start the saves right here, with no await before the assignment below: an await would let a realtime
      // event land after settle() and then be overwritten without the guard's follow-up fetch. saveOrderToIndexedDB
      // opens its transaction synchronously, so these writes are ordered before any later IndexedDB read.
      const saves = processedOrders.map((order) => saveOrderToIndexedDB(dbInstance, order));

      // Detect new rider check-ins and analysis-verdict transitions (comparing old vs new state)
      const oldOrders = this.processedOrders;
      for (const newOrder of processedOrders) {
        const oldOrder = oldOrders.find(o => o.orderId === newOrder.orderId);
        const hadRiderCheckIn = oldOrder?.order?.orderEvents?.some(e => e.event === 'RIDER_CHECK_IN') || false;
        const hasRiderCheckIn = newOrder.order?.orderEvents?.some(e => e.event === 'RIDER_CHECK_IN') || false;
        if (hasRiderCheckIn && !hadRiderCheckIn) {
          this.handleRiderCheckIn(newOrder);
        }
        // Toast when a pending order reaches a verdict on its own (background/queued analysis,
        // or auto-green on reconnect) — the operator may not be looking at that tile.
        this.maybeToastVerdict(oldOrder, newOrder);
      }

      // Update the rawOrders and processedOrders arrays. This variables are watched by the OrderDisplay component.
      this.rawOrders = fetchedOrders;
      this.processedOrders = processedOrders;

      await Promise.all(saves);

      // A realtime event changed the list while this fetch was in flight and the snapshot may predate it:
      // fetch once more. One follow-up per call, so a busy restaurant cannot chain fetches.
      if (verdict === 'apply+refetch' && !followUp) {
        logger.info('processQuery - A realtime event landed mid-fetch, fetching once more');
        await this.processQuery(dbInstance, { followUp: true });
      }
    },
    async clearDatabase() {
      if (!this.db) {
        console.warn('IndexedDB not initialized, skipping clear operation');
        return;
      }

      const clearTable = (storeName) => {
        return new Promise((resolve, reject) => {
          const transaction = this.db.transaction(storeName, 'readwrite');
          const store = transaction.objectStore(storeName);
          const request = store.clear();

          request.onsuccess = () => resolve();
          request.onerror = (event) => reject(event.target.error);
        });
      };

      try {
        await clearTable('photos');
        await clearTable('orders');
        // Don't clear 'settings' to preserve user preferences
        console.log(this.$t('message.indexedDBCleared'));
      } catch (error) {
        console.error(`${this.$t('message.errorClearingIndexedDB')}:`, error);
      }
    },
    openSettings() {
      this.currentComponent = 'SettingsScreen';
    },
    // The periscope install guide: offered at the first login of a tablet and always reachable from
    // Ajustes, or by the link support sends (`source` 'first_login' | 'settings' | 'link'). See
    // src/periscopeTutorial.js and src/tutorialLink.js.
    openPeriscopeTutorial(source) {
      this.periscopeTutorialSource = source;
      this.currentComponent = 'PeriscopeTutorialScreen';
      trackPeriscope('shown', { source });
    },
    // A tap on a tutorial link inside the messenger (src/tutorialLink.js): the messenger closes and the
    // tutorial opens over whatever screen was up, or right after login if the session is not up yet.
    openTutorialLink(name) {
      if (window.Intercom) window.Intercom('hide');
      if (this.startupStatus !== 'ok' || this.currentComponent === 'LoginScreen') {
        this.tutorialLinkPending = name;
      } else if (name === TUTORIAL_LINKS.PERISCOPE) {
        this.openPeriscopeTutorial('link');
      } else if (name === TUTORIAL_LINKS.PHOTO_VIDEO) {
        this.openPhotoVideoTutorial('link');
      }
    },
    // Finishing or "ya está colocado" settles the per-device flag; leaving without choosing keeps
    // it (the welcome comes back at the next login). Back to Ajustes if it was opened from there.
    closePeriscopeTutorial({ outcome } = {}) {
      const next = stateAfterClose(outcome, readPeriscopeTutorialState(window.localStorage, this.deviceId));
      if (next) writePeriscopeTutorialState(window.localStorage, this.deviceId, next);
      const source = this.periscopeTutorialSource;
      this.periscopeTutorialSource = null;
      this.currentComponent = source === 'settings' ? 'SettingsScreen' : 'CameraScreen';
    },
    // The QR gun guide: only from Ajustes (Deleito's clients), or picked up again at login after Android closed
    // the app in the Bluetooth settings. `source` is 'settings' | 'resume'. See src/qrGunTutorial.js.
    openQrGunTutorial(source, step = 'power') {
      this.qrGunTutorialSource = source;
      this.qrGunTutorialStep = step;
      this.currentComponent = 'QrGunTutorialScreen';
      trackQrGun('shown', { source });
    },
    // «Cómo hacer buenas fotos», from Ajustes or by the link support sends (`source` 'settings' | 'link');
    // the screen reports its own telemetry (src/photoVideoTutorial.js). Closing goes back to Ajustes if it
    // was opened from there, to the camera otherwise.
    openPhotoVideoTutorial(source) {
      // Already playing (the link tapped over the player): it stays as it was opened.
      if (!this.photoVideo || this.currentComponent === 'PhotoVideoTutorialScreen') return;
      this.photoVideoSource = source;
      this.currentComponent = 'PhotoVideoTutorialScreen';
    },
    closePhotoVideoTutorial() {
      const source = this.photoVideoSource;
      this.photoVideoSource = null;
      this.currentComponent = source === 'settings' ? 'SettingsScreen' : 'CameraScreen';
    },
    // Back to Ajustes (or the camera, when it was picked up at login). Leaving settles the resume mark.
    closeQrGunTutorial({ outcome, step } = {}) {
      clearQrGunResume(window.localStorage);
      const source = this.qrGunTutorialSource;
      if (outcome === 'completed') trackQrGun('completed', { source });
      else trackQrGun('dismissed', { source, step });
      this.qrGunTutorialSource = null;
      this.qrGunTutorialStep = null;
      this.currentComponent = source === 'resume' ? 'CameraScreen' : 'SettingsScreen';
    },
    // The changelog is morning reading. It auto-opens only when the tablet has a session
    // and the kitchen is idle: any order still waiting for its photo — with no grace
    // period, pending is pending — or an active coverage alert keeps the gate shut. The
    // 30 s interval re-evaluates, so the edition appears on the morning boot (empty
    // list) and never interrupts a shift. Ajustes → "Novedades" bypasses this gate.
    updateChangelogGate() {
      this.changelogGateOpen =
        this.startupStatus === 'ok' &&
        !this.coverageAlert.visible &&
        this.countOverduePhotoCoverage(0) === 0;
    },
    openChangelog() {
      this.changelogOpenRequests += 1;
    },
    // ---- Guided help flow ------------------------------------------------------------------
    // The help button opens a one-tap topic modal instead of the bare composer (design and
    // numbers in docs/flujo-ayuda-guiado.md). The engine (guidedFlow.js) decides the steps and
    // the definition (helpFlow.js) supplies topics, context and the message; this component only
    // feeds them app state, runs the two effects (reload / open the messenger) and reports.
    openHelp(source = 'header') {
      // Already asking (a second tap, or the coverage banner's fallback landing here): keep the
      // flow where it is rather than reset it to the grid and double-count help_opened.
      if (this.helpFlow) return;
      // Hay un reinicio esperando veredicto: el botón lo adelanta en lugar de abrir la parrilla.
      if (this.takeHelpFollowUp(source)) return;
      const messengerReady = Boolean(window.Intercom);
      const store = this.restaurant?.name;
      const guided = Boolean(store) && this.isHelpGuidedEnabled();
      // The question the redesign exists to answer: is the button actually being pressed,
      // and does the tap reach the composer? A tap that lands nowhere (messenger_ready
      // false) is the blind spot — without this event a silent Intercom is invisible and
      // looks identical to nobody asking for help. `guided` is additive, so the existing
      // series stays comparable. Telemetry must never break the flow.
      this.reportHelp('help_opened', {
        source,
        surface: this.helpSurface,
        messenger_ready: messengerReady,          // legacy meaning (SDK initialised) — kept for the series
        messenger_loaded: isMessengerReady(window.Intercom), // the widget really arrived
        prefilled: messengerReady && Boolean(store),
        immersive: this.settings.immersiveViewport !== false,
        guided,
      });
      if (guided) {
        // Opens even with no messenger: the remedy for "no orders" is a reload, which is also
        // what brings a dead Intercom back; escalation parks on UNAVAILABLE with the reason.
        this.helpVoiceEnabled = this.isHelpVoiceEnabled();
        this.helpFlow = createFlow(HELP_FLOW_ID, { source });
        this.loadHelpPlatforms();
        return;
      }
      if (!messengerReady) return;
      if (!store) {
        // No session yet: nothing worth pre-filling, so just open the messenger.
        window.Intercom('show');
        return;
      }
      // Legacy path (kill-switch off): land in the composer with the opening line written.
      // (The SDK cannot send programmatically — showNewMessage only pre-populates.)
      window.Intercom('showNewMessage', this.$t('message.helpPrefill', { store }));
    },
    // Kill-switch, not a rollout gate: the guided modal is the default, and setting the PostHog
    // flag `help-guided-flow` to false (per client / restaurant) falls back to the old composer.
    // A method, not a computed: flags load asynchronously and posthog is not reactive.
    isHelpGuidedEnabled() {
      try {
        return posthog.getFeatureFlag('help-guided-flow') !== false;
      } catch (_) {
        return true;
      }
    },
    /**
     * ¿Enviar el mensaje de verdad, en vez de dejarlo escrito en el redactor? Sí, por defecto.
     *
     * `Intercom('startConversation', texto)` envía y abre la conversación, a diferencia de
     * `showNewMessage`, que solo pre-rellena y dejaba la mitad de los mensajes sin enviar. No
     * necesita backend ni token de API, y no hace falta subir la dependencia: el paquete es un
     * envoltorio de `window.Intercom(...)`, así que el comando viaja igual que `showNewMessage`.
     *
     * VERIFICADO EN PRODUCCIÓN (2026-09-09): enviado desde la app con el widget real, la
     * conversación aparece en la Inbox como `customer_initiated`, con el mensaje completo
     * atribuido al contacto del dispositivo, y Fin responde en el hilo. No es una suposición
     * a partir de la documentación.
     *
     * Freno, no puerta: el flag `help-send-direct` de PostHog a `false` devuelve el redactor
     * (por si un cliente prefiere revisar antes de enviar), y `bronze_help_send_direct` en
     * localStorage fuerza cualquiera de los dos comportamientos en una tablet concreta.
     */
    isHelpSendDirect() {
      try {
        const override = localStorage.getItem('bronze_help_send_direct');
        if (override === '1') return true;
        if (override === '0') return false;
        return posthog.getFeatureFlag('help-send-direct') !== false;
      } catch (_) {
        return true;
      }
    },
    /**
     * ¿La Ayuda lee pedidos del servidor? Sí, por defecto: "quiero ver las fotos de un pedido" las
     * enseña en la tablet, y los temas sobre un pedido que suele haber salido de la pantalla ("dice
     * que falta un producto y está", "salió sin foto") lo eligen de los últimos del servidor.
     *
     * Freno, no puerta, igual que `help-guided-flow`: el flag `help-order-photos` de PostHog a
     * `false` devuelve esos temas a lo de antes (elegir de la pantalla, y un mensaje a soporte), y
     * `bronze_help_order_photos` en localStorage ('1' / '0') fuerza cualquiera de los dos en una
     * tablet concreta. Se lee en el toque, no en una computed: los flags llegan asíncronos.
     */
    isHelpPhotosEnabled() {
      try {
        const override = localStorage.getItem('bronze_help_order_photos');
        if (override === '1') return true;
        if (override === '0') return false;
        return posthog.getFeatureFlag('help-order-photos') !== false;
      } catch (_) {
        return true;
      }
    },
    /**
     * ¿Sigue un escalado en la conversación del episodio, en vez de abrir otra? Sí, por defecto: hacer
     * el flujo varias veces abría varios chats en la bandeja de soporte (el cómo, y lo que se ha medido
     * del widget, en intercomConversation.js).
     *
     * Freno, no puerta, como los demás interruptores de la Ayuda: el flag `help-continue-conversation`
     * de PostHog a `false` vuelve a una conversación nueva por escalado, y
     * `bronze_help_continue_conversation` en localStorage ('1' / '0') fuerza cualquiera de los dos en
     * una tablet concreta. Escribir en el chat de Intercom depende de su DOM, que cambian cuando
     * quieren: esto es lo que se apaga si algún día lo hace mal.
     */
    isHelpContinueEnabled() {
      try {
        const override = localStorage.getItem('bronze_help_continue_conversation');
        if (override === '1') return true;
        if (override === '0') return false;
        return posthog.getFeatureFlag('help-continue-conversation') !== false;
      } catch (_) {
        return true;
      }
    },
    /**
     * ¿Se ofrece "Mandar una nota de voz"? Sí, si el navegador puede grabar: sin `MediaRecorder` ni
     * `getUserMedia` el micrófono de Intercom no funciona y la baldosa sería un botón muerto.
     * Freno por PostHog (`help-voice-note` a false) y override por tablet en localStorage
     * `bronze_help_voice_note` ('1' / '0'), como los demás interruptores del flujo.
     */
    isHelpVoiceEnabled() {
      try {
        const override = localStorage.getItem('bronze_help_voice_note');
        if (override === '1') return true;
        if (override === '0') return false;
        const canRecord = typeof window.MediaRecorder === 'function'
          && Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        if (!canRecord) return false;
        return posthog.getFeatureFlag('help-voice-note') !== false;
      } catch (_) {
        return false;
      }
    },
    /**
     * Después de abrir la conversación, pulsa el micrófono de Intercom por la persona y le dice qué
     * hacer. No lo pulsa hasta que el chat se asienta: la conversación se crea por la red y, al
     * crearse, Intercom rehace la vista y corta cualquier grabación arrancada antes (el reparto de
     * esa espera está en startIntercomVoiceNote). Si no encuentra el botón, el chat ya está
     * abierto: se le pide que toque el micro. Nada de esto bloquea ni lanza.
     */
    async startHelpVoiceNote({ sendDirect, acceptCurrent = false }) {
      let outcome = { result: 'not_found', waitMs: 0, remounts: 0 };
      try {
        outcome = await startIntercomVoiceNote({
          // Con envío directo, el mensaje de contexto tiene que verse antes de grabar: es la señal de
          // que la conversación ya va por delante. Sin envío directo no hay mensaje que esperar.
          expectText: sendDirect ? this.$t('message.helpFlowMsgVoiceNote') : null,
          // Siguiendo en la conversación del episodio no se crea ninguna: el micro de ahora vale.
          acceptCurrent,
          onPressed: () => this.showAppToast({
            text: this.$t('message.helpVoiceSpeakNow'), variant: 'info', duration: 8000,
          }),
        });
      } catch (_) { /* adorno sobre el DOM de un tercero */ }
      if (outcome.result === 'not_found') {
        this.showAppToast({ text: this.$t('message.helpVoiceTapMic'), variant: 'info', duration: 8000 });
      }
      // `repressed` y `remounts` dicen si la espera basta en la red de la cocina: si los cortes son
      // frecuentes, hay que esperar más; si nunca hay, se puede esperar menos.
      this.reportHelp('help_voice_note', {
        mic: outcome.result,
        wait_ms: outcome.waitMs,
        remounts: outcome.remounts,
        send_direct: sendDirect,
      });
    },
    reportHelp(event, props = {}) {
      try {
        reportForensicEvent(event, props);
      } catch (e) {
        logger.console(`help telemetry failed: ${e}`);
      }
    },
    // Everything the help flow reads about orders goes through here, never through
    // processedOrders directly: see the comment on helpOrdersOnScreen.
    /**
     * "Entregar sin corregir" con una razón (piloto de IA wrong). Manda el porqué a soporte sin
     * abrir el modal de ayuda: la persona ya ha contestado, no hay nada más que preguntarle.
     * Mejor esfuerzo de principio a fin — el pedido se entrega igual, pase lo que pase aquí.
     */
    onAiWrongReason({ reason, order, missingItems } = {}) {
      const definition = findAiWrongReason(reason);
      if (!definition) return;
      const items = missingItemNames(missingItems);
      let sent = false;
      try {
        const ctx = this.buildHelpContext();
        const subject = order
          ? describeOrder(order, (platform) => this.platformLabel(platform), { aiOff: this.restaurant?.proActivated === false })
          : null;
        const metadata = aiWrongEventMetadata({
          reason: definition, subject, items, ctx, t: (key, ...rest) => this.$t(key, ...rest),
        });
        if (metadata && isMessengerReady(window.Intercom)) {
          // `trackEvent`, NO `startConversation`: esto llega a Intercom SIN abrir el messenger.
          // El precio hay que tenerlo claro: un evento no aparece en la bandeja como una
          // conversación, aterriza en la ficha del contacto (timeline, segmentos, informes,
          // workflows). Es lo que se pidió — aquí nadie tiene que responder — y además evita
          // inundar la bandeja con el 9 % de los pedidos. Si algún día se quiere en la bandeja sin
          // abrir el widget, eso pide la API REST desde backend; desde el front no se puede.
          window.Intercom('trackEvent', AI_WRONG_EVENT, metadata);
          // Sin messenger que se abra, el toast es la ÚNICA señal de que se ha mandado.
          sent = true;
          this.showAppToast({ text: this.$t('message.aiWrongSentToast'), variant: 'success' });
        }
      } catch (error) {
        logger.console(`ai wrong reason failed to send: ${error}`);
      }
      // El evento va SIEMPRE, se haya podido mandar o no: la razón es el dato de producto que
      // faltaba (el 9 % de pedidos que pasan por aquí sin decir por qué), y no depende de que
      // Intercom estuviera cargado.
      this.reportHelp('ai_wrong_reason', {
        reason,
        order_id: order?.orderId ?? null,
        // La misma llave que `verdict_decision` y `analysis_verdict`: sin ella, cruzar el motivo
        // con su veredicto en Athena es un apaño por tiempo en vez de un join.
        order_analysis_id: order?.orderAnalysis || null,
        missing_items: items.length,
        sent,
      });
    },
    onCameraState(state) {
      this.helpCameraState = state || null;
      // `cameraPermissions` existía sin que nadie lo escribiera. Ahora dice la verdad, que es lo
      // que espera quien lo recibe como prop.
      this.cameraPermissions = state?.permission === 'granted' || state?.ready === true;
    },
    helpOrderSource() {
      return this.helpOrdersOnScreen?.length ? this.helpOrdersOnScreen : (this.processedOrders || []);
    },
    helpOpenOrder() {
      if (!this.openCameraOrderId) return null;
      return this.helpOrderSource().find((o) => o.orderId === this.openCameraOrderId) || null;
    },
    helpCandidateList(topic) {
      return helpCandidates(this.helpOrderSource(), topic, {
        riderDelivered: this.riderDeliveredOrderIds,
        platformLabel: (platform) => this.platformLabel(platform),
        aiOff: this.restaurant?.proActivated === false,
      });
    },
    // What the engine cannot know on its own, sampled at each transition.
    helpEnv(topic = this.helpFlow?.topic) {
      const openOrder = this.helpOpenOrder();
      const wantsPlatform = topic?.subject === 'platform';
      // The order open in the camera IS the subject when the topic is about one — handed to
      // the engine here so the message never has to re-derive it. Nunca para un tema de lista
      // ("¿cuáles no deberían estar?" no se contesta con el pedido que tienes abierto) ni para
      // uno de agregadora, que la persona tiene que elegir.
      const subject = (!wantsPlatform && !topic?.multi && openOrder)
        ? describeOrder(openOrder, (platform) => this.platformLabel(platform), { aiOff: this.restaurant?.proActivated === false })
        : null;
      // Para mirar fotos, el pedido abierto solo vale si ya tiene alguna: abrir uno sin fotos es
      // enseñar un hueco, y lo que la persona busca entonces es otro pedido de la lista.
      const viewable = !topic?.view || (subject && subject.photoCount > 0);
      return {
        canEscalate: isMessengerReady(window.Intercom),
        online: this.isOnline,
        subject: viewable ? subject : null,
        candidates: wantsPlatform ? this.helpPlatforms.length : this.helpCandidateList(topic).length,
      };
    },
    // Las agregadoras del restaurante. Mejor esfuerzo y con la caché compartida con el pedido
    // manual: si falla, el motor ve 0 candidatos, no pregunta, y el mensaje sale sin agregadora.
    loadHelpPlatforms() {
      if (this.helpPlatforms.length) return;
      fetchDeliveryPlatformsForCurrentDevice()
        .then((rows) => {
          const ids = (rows || [])
            .map((r) => r?.deliveryPlatform || r?.platform || r?.id)
            .filter(Boolean);
          this.helpPlatforms = [...new Set(ids)];
        })
        .catch(() => { /* sin lista no se pregunta */ });
    },
    buildHelpContext() {
      return snapshotHelpContext({
        store: this.restaurant?.name,
        deviceId: this.deviceId,
        appVersion: this.appVersion,
        online: this.isOnline,
        socketDeaf: this.deafSince !== null,
        orders: this.helpOrderSource(),
        openOrderId: this.openCameraOrderId,
        riderDelivered: this.riderDeliveredOrderIds,
        camera: {
          ready: this.helpCameraState?.ready === true,
          permission: this.helpCameraState?.permission || 'unknown',
          cameras: this.availableCameras?.length || 0,
          fault: this.helpCameraState?.fault || null,
          code: this.helpCameraState?.code || null,
          stream: this.streamInfo,
          resolution: this.settings?.resolution ?? null,
          selectedCameraId: this.settings?.selectedCameraId || null,
        },
        platformLabel: (platform) => this.platformLabel(platform),
        aiOff: this.restaurant?.proActivated === false,
      });
    },
    // The before/after pair a remedy is judged by (goes to telemetry, not to the kitchen).
    helpSnapshotSummary(ctx) {
      return {
        orders_on_screen: ctx.ordersOnScreen,
        orders_without_photo: ctx.ordersWithoutPhoto,
        camera_fault: ctx.camera?.fault || null,
      };
    },
    applyHelpTransition({ state, effect }) {
      const wasUnavailable = this.helpFlow?.step === FLOW_STEP.UNAVAILABLE;
      this.helpFlow = state;
      if (effect?.type === FLOW_EFFECT.REMEDY) {
        this.runHelpRemedy(effect.remedy);
      } else if (effect?.type === FLOW_EFFECT.ESCALATE) {
        this.escalateHelp(state);
      } else if (effect?.type === FLOW_EFFECT.LOAD) {
        // Only a topic that really lists from the server: with the brake on, back from the code
        // returns to a list read from the screen, and nothing has to be fetched.
        if (this.helpListSource(state.topic)) this.loadHelpPhotoList();
      } else if (effect?.type === FLOW_EFFECT.VIEW) {
        this.openHelpPhotoView(state.subject);
      } else if (state.step === FLOW_STEP.CLOSED) {
        this.finishHelp(state);
      }
      // El único final del flujo que no dejaba rastro: la cocina pidió ayuda y el mensaje NO salió.
      // Sin esto, en Athena se ve un `topic_selected` sin `escalated` y parece un abandono, cuando
      // es justo lo contrario — lo intentaron y la app no pudo. Solo al ENTRAR en el paso, no en
      // cada reintento fallido que repinte el mismo estado.
      if (state.step === FLOW_STEP.UNAVAILABLE && !wasUnavailable) {
        this.reportHelp('help_unavailable', {
          topic: state.topic?.id || null,
          reason: state.unavailableReason || null,
          remedy: state.remedy || null,
          source: state.source,
          surface: this.helpSurface,
        });
      }
    },
    onHelpChoose(topic) {
      if (!this.helpFlow) return;
      // Con el freno puesto, la Ayuda no lee pedidos del servidor: "ver las fotos" vuelve a ser el
      // tique de siempre y los temas que traen su lista vuelven a la de la pantalla. Sin `view` ni
      // `fetch`, el motor lo trata como cualquier otro tema sobre un pedido: con pedidos en
      // pantalla pregunta cuál (y, si no está, su código, que no toca el servidor); sin ninguno,
      // escribe a soporte sin preguntar, como antes.
      const effective = (topic.view || topic.fetch) && !this.isHelpPhotosEnabled()
        ? { ...topic, view: false, fetch: null }
        : topic;
      const env = this.helpEnv(effective);
      this.reportHelp('help_topic_selected', {
        topic: effective.id,
        order_attached: Boolean(effective.subject && env.subject),
        source: this.helpFlow.source,
        remedy: effective.remedy || null,
        view: Boolean(effective.view),
      });
      this.applyHelpTransition(chooseTopic(this.helpFlow, effective, env));
    },
    onHelpPick(item) {
      if (!this.helpFlow) return;
      // El modal devuelve el descriptor ya montado (o una lista de ellos en multiselección), así
      // que aquí no hace falta volver a buscar nada por id.
      const subject = Array.isArray(item)
        ? item.map((i) => i.descriptor).filter(Boolean)
        : (item?.descriptor || null);
      this.applyHelpTransition(pickSubject(this.helpFlow, subject, this.helpEnv()));
    },
    onHelpSubmitText(text) {
      if (!this.helpFlow) return;
      this.applyHelpTransition(submitText(this.helpFlow, text, this.helpEnv()));
    },
    // The code typed because the order was not in the list — or null, "I do not have it". Spaces
    // go (a code is read off a ticket, and a space typed between groups is not part of it); the
    // rest is left as typed, since codes mix digits and letters.
    onHelpSubmitCode(code) {
      if (!this.helpFlow) return;
      const cleaned = typeof code === 'string' ? code.replace(/\s+/g, '') : '';
      this.reportHelp('help_code_entered', {
        topic: this.helpFlow.topic?.id || null, typed: Boolean(cleaned), chars: cleaned.length,
      });
      this.applyHelpTransition(submitCode(this.helpFlow, cleaned || null, this.helpEnv()));
    },
    onHelpAnswer(fixed) {
      if (!this.helpFlow) return;
      const flow = this.helpFlow;
      // THE number for the automatic remedy: did it make the ticket unnecessary?
      this.reportHelp('help_remedy_result', {
        topic: flow.topic?.id || null,
        remedy: flow.remedy,
        fixed: Boolean(fixed),
        elapsed_ms: Date.now() - flow.openedAt,
        before: flow.evidence?.before || null,
        after: flow.evidence?.after || null,
      });
      this.applyHelpTransition(answerConfirm(flow, fixed, this.helpEnv()));
    },
    onHelpRetry() {
      if (this.helpFlow) this.applyHelpTransition(retryEscalation(this.helpFlow, this.helpEnv()));
    },
    onHelpReloadApp() {
      // From the "chat unavailable" step: the header's refresh, one tap closer. Not a remedy —
      // no follow-up question afterwards.
      this.reportHelp('help_topic_dismissed', {
        step: this.helpFlow?.step || null, topic: this.helpFlow?.topic?.id || null, origin: 'reload',
      });
      this.helpFlow = null;
      window.location.reload();
    },
    onHelpBack() {
      if (this.helpFlow) this.applyHelpTransition(flowGoBack(this.helpFlow));
    },
    onHelpDismiss(origin = 'button') {
      if (!this.helpFlow) return;
      const flow = this.helpFlow;
      // `step` is the number that says whether six options were too many: a dismiss on CHOOSE is
      // "none of these", a dismiss on PICK is "my order is not there".
      this.reportHelp('help_topic_dismissed', { step: flow.step, topic: flow.topic?.id || null, origin });
      // Leaving the question unanswered still closes the remedy funnel: every remedy_started
      // gets exactly one remedy_result, with `fixed: null` and why.
      if (flow.step === FLOW_STEP.CONFIRM) {
        this.reportHelp('help_remedy_result', {
          topic: flow.topic?.id || null, remedy: flow.remedy, fixed: null, reason: origin,
          elapsed_ms: Date.now() - flow.openedAt,
        });
      }
      this.applyHelpTransition(flowDismiss(flow));
    },
    runHelpRemedy(remedy) {
      const flow = this.helpFlow;
      if (!flow || remedy !== HELP_REMEDY.RELOAD) return;
      const before = this.helpSnapshotSummary(this.buildHelpContext());
      // The flow must survive the reload: persist what CONFIRM needs, then reload. On boot,
      // resumeHelpFollowUp() picks it up.
      const persisted = savePendingRemedy(window.sessionStorage, {
        flowId: flow.flowId, topicId: flow.topic.id, remedy, source: flow.source, subject: flow.subject, before,
      });
      this.reportHelp('help_remedy_started', { topic: flow.topic.id, remedy, persisted, before });
      setTimeout(() => window.location.reload(), HELP_FLOW.RELOAD_FEEDBACK_MS);
    },
    // After a remedy reload the app boots on its own; once CameraScreen is up, this asks "did
    // that fix it?" a few seconds later — long enough for the orders to have arrived, never over
    // an order being photographed, and never once the question has gone stale.
    /**
     * Abre la pregunta del remedio si hay una esperando y sigue vigente. La usan las dos puertas
     * por las que puede entrar: el temporizador de resumeHelpFollowUp y el botón de Ayuda.
     * Devuelve true si la ha abierto, para que openHelp sepa que no debe seguir.
     */
    takeHelpFollowUp(trigger) {
      const pending = this.helpPendingFollowUp;
      if (!pending) return false;
      // Caducada: se cierra el embudo con su motivo y el botón sigue su camino normal.
      if (Date.now() > pending.deadline) {
        this.helpPendingFollowUp = null;
        clearTimeout(this.helpResumeTimer);
        this.helpResumeTimer = null;
        this.reportHelp('help_remedy_result', {
          topic: pending.topic.id, remedy: pending.record.remedy, fixed: null, reason: 'expired',
        });
        return false;
      }
      this.helpPendingFollowUp = null;
      clearTimeout(this.helpResumeTimer);
      this.helpResumeTimer = null;
      const ctx = this.buildHelpContext();
      // `trigger` es la respuesta a "¿llegamos tarde con la pregunta?": si la mayoría de las
      // confirmaciones se abren por `help_button`, el retardo de 3 s es demasiado largo.
      this.reportHelp('help_followup_shown', {
        topic: pending.topic.id,
        remedy: pending.record.remedy,
        trigger,
        waited_ms: Date.now() - pending.record.startedAt,
      });
      // La pregunta vuelve tras una recarga: la parrilla a la que puede volver es la de ahora.
      this.helpVoiceEnabled = this.isHelpVoiceEnabled();
      this.helpFlow = resumeFlow(pending.record, pending.topic, {
        evidence: { before: pending.record.before || null, after: this.helpSnapshotSummary(ctx) },
      });
      // Sin respuesta, se cierra sola: nadie pidió un modal en este momento.
      clearTimeout(this.helpConfirmTimer);
      this.helpConfirmTimer = setTimeout(() => {
        this.helpConfirmTimer = null;
        if (this.helpFlow?.step === FLOW_STEP.CONFIRM) this.onHelpDismiss('timeout');
      }, HELP_FLOW.CONFIRM_TIMEOUT_MS);
      return true;
    },
    resumeHelpFollowUp() {
      const record = takePendingRemedy(window.sessionStorage, HELP_FLOW_ID);
      if (!record) return;
      // La agregadora se pregunta DESPUÉS del reinicio, así que la lista tiene que cargarse al
      // volver: la página en la que se abrió el modal ya no existe.
      this.loadHelpPlatforms();
      const topic = findHelpTopic(record.topicId);
      if (!topic) return;
      // Desde aquí la pregunta ya EXISTE, aunque todavía no se vea: el botón de Ayuda puede
      // adelantarla (takeHelpFollowUp) en vez de abrir la parrilla.
      this.helpPendingFollowUp = {
        record, topic, deadline: record.startedAt + HELP_FLOW.FOLLOWUP_DEADLINE_MS,
      };
      const attempt = () => {
        this.helpResumeTimer = null;
        if (!this.helpPendingFollowUp) return; // el botón se le adelantó
        // Busy = anything that should not be interrupted or covered: an order being photographed,
        // a blocking notification, another help flow, or a screen other than the kitchen's. All of
        // them retry; only the deadline drops the question (and says so in telemetry).
        const busy = this.isPhotographing || Boolean(this.currentNotification)
          || Boolean(this.helpFlow) || this.currentComponent !== 'CameraScreen';
        const decision = followUpDecision({
          startedAt: record.startedAt, deadlineMs: HELP_FLOW.FOLLOWUP_DEADLINE_MS, busy,
        });
        if (decision === FOLLOW_UP.EXPIRED) {
          this.helpPendingFollowUp = null;
          this.reportHelp('help_remedy_result', { topic: topic.id, remedy: record.remedy, fixed: null, reason: 'expired' });
          return;
        }
        if (decision === FOLLOW_UP.RETRY) {
          this.helpResumeTimer = setTimeout(attempt, HELP_FLOW.FOLLOWUP_RETRY_MS);
          return;
        }
        this.takeHelpFollowUp('timer');
      };
      clearTimeout(this.helpResumeTimer);
      this.helpResumeTimer = setTimeout(attempt, HELP_FLOW.FOLLOWUP_DELAY_MS);
    },
    escalateHelp(state) {
      const topic = state.topic;
      const ctx = this.buildHelpContext();
      // The order it is about — picked on the PICK step, or handed in through env at choose time.
      const subject = state.subject || null;
      const remedy = state.remedy ? { kind: state.remedy, fixed: state.remedyFixed } : null;
      const message = composeHelpMessage({
        topic, ctx, subject, remedy, text: state.text, fromView: state.fromView,
        t: (key, ...rest) => this.$t(key, ...rest),
      });
      // El texto libre también se envía, PORQUE YA ESTÁ ESCRITO: la persona lo ha contado en el
      // campo del modal, así que el mensaje está completo. Lo que no se envía nunca es un texto
      // libre vacío, que sería el tique de "Necesitamos ayuda con:" que esto vino a eliminar; el
      // motor ya no deja llegar aquí sin texto, y esto es el cinturón de esa tirantez.
      const sendDirect = this.isHelpSendDirect() && (!topic?.free || Boolean(state.text));
      const voice = Boolean(topic?.voice);
      // La conversación de este episodio, si la tablet ya abrió una hace poco (intercomConversation.js).
      const continueEnabled = this.isHelpContinueEnabled();
      const continueId = continueEnabled
        ? conversationToContinue(window.localStorage, { user: this.deviceId })
        : null;
      this.reportHelp('help_escalated', {
        topic: topic?.id || null,
        remedy: state.remedy,
        surface: this.helpSurface,
        subject_kind: Array.isArray(subject) ? 'order_list' : (subject?.kind || null),
        subject_count: Array.isArray(subject) ? subject.length : (subject ? 1 : 0),
        typed_chars: state.text ? state.text.length : 0,
        order_attached: Boolean(subject),
        source: state.source,
        messenger_loaded: true,
        method: sendDirect ? 'start_conversation' : 'compose',
        // A dónde va: 'continue' intenta la conversación del episodio; el resultado, en help_conversation.
        conversation: continueId ? 'continue' : 'new',
        from_view: Boolean(state.fromView),
      });
      this.deliverHelpMessage({ message, sendDirect, voice, continueEnabled, continueId, topic: topic?.id || null })
        .catch((error) => logger.error('Help message delivery failed', { error, method: 'deliverHelpMessage' }));
      this.finishHelp(state);
    },
    /**
     * Hace llegar el mensaje de la Ayuda a Intercom: en la conversación del episodio si la hay, o en
     * una nueva, como siempre. Seguir en la misma es un intento sobre el DOM de un tercero
     * (replyInIntercomConversation) con una regla que no se negocia: si falla ANTES de enviar nada, se
     * abre una nueva; si se pulsó enviar y no se pudo comprobar, no se reintenta, porque el mensaje
     * puede haber salido. Nunca llega dos veces, y nada de esto lanza. Con el freno puesto
     * (`continueEnabled` false) es exactamente lo de antes: ni se sigue, ni se apunta, ni se mide.
     */
    async deliverHelpMessage({ message, sendDirect, voice, continueEnabled, continueId, topic }) {
      const storage = window.localStorage;
      const user = this.deviceId;
      // Dos entregas solapadas (una cocina que repite mientras la primera aún espera al chat): solo la
      // última toca el registro, para que no lo pise otra que acaba después con una conversación vieja.
      this.helpDeliverySeq += 1;
      const seq = this.helpDeliverySeq;
      const latest = () => seq === this.helpDeliverySeq;
      let reply = null;
      if (continueId) {
        try {
          window.Intercom('showConversation', continueId);
          reply = await replyInIntercomConversation({
            // Una nota de voz sin envío directo no lleva mensaje delante: basta con el redactor listo.
            text: voice && !sendDirect ? null : message,
            send: sendDirect,
            isShown: () => messengerConversations(storage)?.shown === continueId,
          });
        } catch (_) {
          reply = { result: 'no_composer', waitMs: 0 };
        }
      }
      // `unconfirmed` cuenta como seguida: puede haber salido, y abrir otra la duplicaría.
      const continued = Boolean(reply) && ['sent', 'typed', 'ready', 'unconfirmed'].includes(reply.result);
      let captured = null;
      if (continued) {
        if (latest()) rememberConversation(storage, { id: continueId, user });
      } else {
        // La del episodio no ha servido (o no había): no se vuelve a intentar con ella.
        if (continueId && latest()) forgetConversation(storage);
        const before = continueEnabled ? conversationsBefore(storage) : [];
        if (sendDirect) {
          // Envía y abre la conversación: la cocina ve lo que se ha mandado y puede añadir detalle
          // encima, sin tener que redactar ni pulsar enviar. Para una nota de voz, además, deja el
          // redactor VACÍO, que es la condición para que Intercom enseñe su micrófono.
          window.Intercom('startConversation', message);
        } else if (voice) {
          // Sin envío directo el contexto no puede ir delante: con texto en el redactor Intercom quita
          // el micrófono. Se abre vacío y la nota de voz es el primer mensaje (el contacto ya dice
          // qué tablet y qué restaurante es).
          window.Intercom('showNewMessage');
        } else {
          // Pre-rellena el redactor: la persona revisa, añade detalle y pulsa enviar.
          window.Intercom('showNewMessage', message);
        }
        if (continueEnabled) {
          // Lo que tiene que estar en el hilo para reconocerla: la primera línea del mensaje. Una nota de
          // voz sin envío directo no manda texto delante, así que no hay por dónde reconocerla.
          const line = sendDirect || !voice ? String(message || '').split('\n')[0] : null;
          captured = this.captureHelpConversation({ before, sendDirect, line, user, latest });
        }
      }
      if (sendDirect && !voice && (!continued || reply.result === 'sent')) {
        this.showAppToast({ text: this.$t('message.helpFlowSentToast'), variant: 'success' });
      }
      if (voice) {
        // El micro en vez del teclado: enfocar el redactor sacaría el teclado encima de la grabación.
        this.startHelpVoiceNote({ sendDirect, acceptCurrent: continued });
      } else {
        // Cursor puesto en el redactor para que salga el teclado y se pueda seguir escribiendo sin
        // buscar el campo. Adorno de mejor esfuerzo: no se espera ni se comprueba.
        focusIntercomComposer();
      }
      if (!continueEnabled) return;
      const id = continued ? continueId : await captured;
      // Si esto funciona en la cocina: cuántos escalados siguen en la del episodio, por qué fallan los
      // que no (`reply`), si se quedó en el chat algo que pegó la app (`left`) y si se consigue apuntar
      // el id de las nuevas (`captured`).
      this.reportHelp('help_conversation', {
        topic,
        conversation: continued ? 'continued' : 'new',
        reply: reply ? reply.result : null, // null: no había conversación que seguir
        left: Boolean(reply && reply.left),
        wait_ms: reply ? reply.waitMs : 0,
        captured: continued ? null : Boolean(id),
        send_direct: sendDirect,
      });
    },
    /**
     * Apunta el id de la conversación que se acaba de abrir, para que el siguiente escalado del
     * episodio siga en ella. Con envío directo existe en cuanto el servidor la crea (1-2 s, medido);
     * pre-rellenada, solo cuando la persona pulsa enviar, así que se espera mientras el chat siga
     * abierto, como mucho lo que dura un episodio. Solo vale si su hilo lleva `line` (la primera
     * línea del mensaje), y con el chat a la vista: en ese rato la persona puede abrir otra desde el
     * propio chat y cerrarlo, y el messenger se queda apuntando a esa. Si cierra antes de que el hilo
     * se pinte, no se apunta nada y el siguiente escalado abre una nueva, como antes. Devuelve el id, o
     * null.
     */
    captureHelpConversation({ before, sendDirect, line, user, latest }) {
      if (!line) return Promise.resolve(null);
      const storage = window.localStorage;
      const startedAt = Date.now();
      return waitForNewConversation({
        storage,
        before,
        accept: () => messengerHasLine(line),
        timeoutMs: sendDirect ? 20000 : CONTINUE_WINDOW_MS,
        intervalMs: sendDirect ? 250 : 1000,
        // Recién llamado, el messenger aún no se ve: se le dan unos segundos antes de mirar si sigue abierto.
        stillLooking: () => sendDirect || Date.now() - startedAt < 5000 || messengerShown(),
      }).then((id) => {
        if (id && latest()) rememberConversation(storage, { id, user });
        return id;
      }).catch(() => null);
    },
    finishHelp(state) {
      clearTimeout(this.helpConfirmTimer);
      this.helpConfirmTimer = null;
      if (state.outcome === FLOW_OUTCOME.RESOLVED) {
        // Positive close: the app fixed it and no ticket was written.
        this.showAppToast({ text: this.$t('message.helpFlowResolvedToast'), variant: 'success' });
      }
      this.helpFlow = null;
      this.resetHelpPhotos();
    },
    // ---- "Quiero ver las fotos de un pedido" ----------------------------------------------
    // The list comes from the server (what already left the screen is the point), the photos are
    // public S3 objects the <img> loads as-is. Design and numbers in docs/flujo-ayuda-guiado.md.
    resetHelpPhotos() {
      // A new seq, so an answer still in flight lands nowhere instead of in the next flow.
      this.helpPhotoList = emptyHelpPhotoList(this.helpPhotoList.seq + 1);
      this.helpPhotoView = emptyHelpPhotoView(this.helpPhotoView.seq + 1);
    },
    photoOrderOpts() {
      return {
        platformLabel: (platform) => this.platformLabel(platform),
        aiOff: this.restaurant?.proActivated === false,
      };
    },
    photoTime(ms) {
      return photoTimeLabel(ms, { t: (key, ...rest) => this.$t(key, ...rest), locale: this.i18nLocale || 'es' });
    },
    // A tile of the photo list — and the header of the viewer, so the kitchen sees the same card
    // it tapped: code, platform, when the photo was taken, how many, and the verdict colour.
    photoPickItem(d) {
      // With the AI off there is no verdict: "Correcto" on a tile would claim a check never made.
      const badge = this.restaurant?.proActivated === false ? null : ({
        incorrect: ['message.helpFlowBadgeIncorrect', 'orange'],
        correct: ['message.helpFlowBadgeCorrect', 'green'],
        // Not blue, as on the pass: nothing is waiting any more, it simply has no result. And
        // not "con foto": every order in this list has one.
        pending: ['message.helpPhotosBadgeNoResult', 'slate'],
      }[d.verdict] || null);
      const count = (d.photos || []).length;
      const sublabel = [
        d.platformName,
        // An order without a photo has no photo time: when it came in is what the kitchen remembers.
        this.photoTime(d.lastPhotoAt ?? d.createdAt),
        count ? this.$t('message.helpPhotosCount', count) : null,
      ].filter(Boolean).join(' · ');
      return {
        id: d.orderId,
        label: d.code,
        sublabel,
        icon: d.platform ? this.getPlatformIcon(d.platform) : null,
        badgeKey: badge ? badge[0] : null,
        badgeTone: badge ? badge[1] : 'slate',
        descriptor: d,
      };
    },
    // Which list a topic fetches: the photographed orders ("ver las fotos", "dice que falta un
    // producto y está") or the ones that never got a photo ("salió sin foto").
    helpListSource(topic = this.helpFlow?.topic) {
      return topic?.fetch || (topic?.view ? 'photos' : null);
    },
    // One page of that list, as the entries the pick step shows. For the AI-wrong topic the red ones
    // go first — within the page, so a page loaded later never moves what is already on screen.
    async fetchHelpOrderPage(source, offset, topic = this.helpFlow?.topic) {
      const restaurantId = this.restaurant?.id;
      if (!restaurantId) throw new Error('no restaurant in session');
      const fetchPage = source === 'no_photo' ? fetchRecentOrdersWithoutPhoto : fetchRecentOrderPhotos;
      const rows = await fetchPage({ restaurantId, first: PHOTO_HISTORY_PAGE, offset });
      const entries = source === 'no_photo'
        ? orderHistoryFromServer(rows, this.photoOrderOpts())
        : photoHistoryFromServer(rows, this.photoOrderOpts());
      return { rows, entries: topic?.incorrectFirst ? incorrectFirst(entries) : entries };
    },
    /**
     * The first page of the list the topic asks for. Reused as is when it was fetched moments ago
     * for the same list (coming back from a photo, or from typing a code), refetched otherwise —
     * never refreshed silently under the finger.
     */
    async loadHelpPhotoList({ force = false } = {}) {
      // Back on the list, whatever the viewer was still fetching is no longer wanted: without a
      // new seq, a late answer for the order open in the camera would report a view nobody saw.
      this.helpPhotoView = emptyHelpPhotoView(this.helpPhotoView.seq + 1);
      const source = this.helpListSource() || 'photos';
      // Same server list, different order: "ver las fotos" by time, "falta un producto" with the red
      // ones first. A list reused across the two would show one topic the other's order.
      const key = `${source}${this.helpFlow?.topic?.incorrectFirst ? ':incorrect-first' : ''}`;
      const list = this.helpPhotoList;
      const fresh = list.status === 'ready' && list.key === key
        && Date.now() - list.fetchedAt < HELP_FLOW.PHOTOS_LIST_FRESH_MS;
      if (fresh && !force) return;
      const seq = list.seq + 1;
      this.helpPhotoList = { ...emptyHelpPhotoList(seq), status: 'loading', source, key };
      const startedAt = Date.now();
      try {
        const { rows, entries: orders } = await this.fetchHelpOrderPage(source, 0);
        if (this.helpPhotoList.seq !== seq) return; // closed, or a newer request owns the list
        this.helpPhotoList = {
          ...emptyHelpPhotoList(seq),
          status: 'ready', orders, offset: rows.length, lastPageSize: rows.length, fetchedAt: Date.now(), source, key,
        };
        this.reportHelp('help_photos_listed', { ok: true, page: 0, rows: rows.length, orders: orders.length, ms: Date.now() - startedAt, source });
      } catch (error) {
        if (this.helpPhotoList.seq !== seq) return;
        const status = this.isOnline === false ? 'offline' : 'failed';
        this.helpPhotoList = { ...emptyHelpPhotoList(seq), status, source, key };
        this.reportHelp('help_photos_listed', {
          ok: false, page: 0, reason: status, ms: Date.now() - startedAt, source,
          error: String(error?.errors?.[0]?.message || error?.message || error).slice(0, 160),
        });
      }
    },
    onHelpPhotosRefresh() {
      if (this.helpIsFetchedPick) this.loadHelpPhotoList({ force: true });
    },
    // "Ver pedidos anteriores": the next page goes BELOW what is on screen; nothing already
    // painted moves.
    async onHelpPhotosMore() {
      const list = this.helpPhotoList;
      if (!this.helpIsFetchedPick || list.status !== 'ready' || list.loadingMore) return;
      const { seq, offset, source } = list;
      this.helpPhotoList = { ...list, loadingMore: true };
      const startedAt = Date.now();
      try {
        const { rows, entries: older } = await this.fetchHelpOrderPage(source || 'photos', offset);
        if (this.helpPhotoList.seq !== seq) return;
        this.helpPhotoList = {
          ...this.helpPhotoList,
          orders: appendPhotoPage(this.helpPhotoList.orders, older),
          offset: offset + rows.length,
          lastPageSize: rows.length,
          loadingMore: false,
          fetchedAt: Date.now(),
        };
        this.reportHelp('help_photos_listed', {
          ok: true, page: Math.round(offset / PHOTO_HISTORY_PAGE), rows: rows.length, orders: older.length, ms: Date.now() - startedAt,
        });
      } catch (error) {
        if (this.helpPhotoList.seq !== seq) return;
        // What is on screen stays usable; only the button comes back.
        this.helpPhotoList = { ...this.helpPhotoList, loadingMore: false };
        this.showAppToast({ text: this.$t('message.helpPhotosFailed'), variant: 'warning' });
        this.reportHelp('help_photos_listed', {
          ok: false, page: Math.round(offset / PHOTO_HISTORY_PAGE), reason: this.isOnline === false ? 'offline' : 'failed',
          ms: Date.now() - startedAt, error: String(error?.errors?.[0]?.message || error?.message || error).slice(0, 160),
        });
      }
    },
    /**
     * Show one order's photos. Picked from the list, the photos are already here and the viewer
     * opens at once; the order open in the camera is fetched by id (the list may not hold it, and
     * its photos may still be uploading).
     */
    async openHelpPhotoView(subject) {
      if (!subject) return;
      const seq = this.helpPhotoView.seq + 1;
      const openedAt = Date.now();
      if (Array.isArray(subject.photos)) {
        this.helpPhotoView = { ...emptyHelpPhotoView(seq), status: 'ready', order: subject, from: 'list', openedAt };
        this.reportHelpPhotoViewed(subject, 'list');
        return;
      }
      // Lo que ya sabemos del pedido abierto pinta la cabecera mientras llegan sus fotos.
      const placeholder = { ...subject, photos: [], lastPhotoAt: null };
      this.helpPhotoView = { ...emptyHelpPhotoView(seq), status: 'loading', order: placeholder, from: 'open_order', openedAt };
      try {
        const row = await fetchOrderPhotos(subject.orderId);
        if (this.helpPhotoView.seq !== seq) return;
        const order = (row && describePhotoOrder(row, this.photoOrderOpts())) || placeholder;
        this.helpPhotoView = { ...this.helpPhotoView, status: 'ready', order };
        this.reportHelpPhotoViewed(order, 'open_order');
      } catch (error) {
        if (this.helpPhotoView.seq !== seq) return;
        this.helpPhotoView = { ...this.helpPhotoView, status: this.isOnline === false ? 'offline' : 'failed' };
        this.reportHelp('help_photos_viewed', {
          ok: false, from: 'open_order', order_id: subject.orderId,
          error: String(error?.errors?.[0]?.message || error?.message || error).slice(0, 160),
        });
      }
    },
    onHelpViewRefresh() {
      if (this.helpFlow?.step === FLOW_STEP.VIEW) this.openHelpPhotoView(this.helpFlow.subject);
    },
    // The question this feature is judged by (norma 12): did the kitchen get to SEE the photos,
    // and how old was the order it went looking for? `position` says whether the list order works.
    reportHelpPhotoViewed(d, from) {
      this.reportHelp('help_photos_viewed', {
        ok: true,
        from,
        order_id: d.orderId,
        photos: (d.photos || []).length,
        verdict: d.verdict || null,
        position: from === 'list' ? this.helpPhotoList.orders.findIndex((o) => o.orderId === d.orderId) : -1,
        age_min: d.lastPhotoAt ? Math.round((Date.now() - d.lastPhotoAt) / 60000) : null,
      });
    },
    // "Rápido" was half of the brief: how long the first photo took to appear, and any photo that
    // never did (a public S3 object that fails to load is a network or bucket problem to know about).
    onHelpPhotoLoad({ index, ok, ms, retried } = {}) {
      const view = this.helpPhotoView;
      if (!view.order || (ok && view.firstReported)) return;
      if (ok) this.helpPhotoView = { ...view, firstReported: true };
      this.reportHelp('help_photo_shown', {
        ok, index, ms, retried: Boolean(retried), from: view.from, order_id: view.order.orderId,
        since_open_ms: Date.now() - view.openedAt,
      });
    },
    onHelpViewSupport() {
      if (this.helpFlow) this.applyHelpTransition(askAboutSubject(this.helpFlow, this.helpEnv()));
    },
    resetHelpState() {
      clearTimeout(this.helpResumeTimer);
      this.helpResumeTimer = null;
      clearTimeout(this.helpConfirmTimer);
      this.helpConfirmTimer = null;
      this.helpFlow = null;
      this.helpPendingFollowUp = null;
      this.helpCameraState = null;
      this.helpOrdersOnScreen = [];
      this.helpPlatforms = [];
      this.resetHelpPhotos();
      clearPendingRemedy(window.sessionStorage, HELP_FLOW_ID);
    },
    async handleSignOut() {
      this.realtimeEpoch += 1; // a rebuild in flight must not resubscribe behind the sign-out
      this.realtimeReady = false; // the next login starts realtime itself, after its inits
      await this.clearDatabase();
      this.resetCoverageState();
      this.resetHelpState();
      this.currentComponent = 'LoginScreen';
    },
    startBackgroundUploader(restaurant, deviceId) {
      startBackgroundUploader(restaurant.id, deviceId);
    },
    async handlePhotoSaved(orderId) {
      // Remember locally-taken photos so the coverage detector doesn't count them as
      // "sin foto" while the upload / backend confirmation is still catching up.
      if (orderId) this.coverageRecentLocalPhoto[orderId] = Date.now();
      if (!this.restaurant?.id) {
        logger.warning('handlePhotoSaved called but restaurant is not available');
        return;
      }
      // A demo photo has no upload to trigger: src/liveDemo/live.js replays it locally.
      if (isLiveDemoId(orderId)) return;
      uploadPendingPhotos(this.restaurant.id, this.deviceId);
    },
    setRestaurant(restaurantId) {
      this.restaurant = restaurantId;
    },
    toastTitle(toast) {
      if (toast.type === 'delivered' && toast.via === 'scan') return this.$t('message.qrScanDeliveredTitle');
      if (toast.type === 'delivered') return this.$t('message.riderOrderDeliveredTitle');
      if (toast.isReminder) return this.$t('message.riderReminderTitle');
      return this.$t('message.riderArrivedTitle');
    },
    getPlatformIcon(platform) {
      try {
        return require(`@/assets/delivery_platforms/${platform}.png`);
      } catch (e) {
        return require('@/assets/delivery_platforms/OTHERS.png');
      }
    },
    platformLabel(platform) {
      return platformName(platform, (key) => this.$t(key));
    },
    waitUrgency(minutes) {
      if (minutes == null) return 'none';
      if (minutes < 2) return 'low';
      if (minutes < 5) return 'medium';
      return 'high';
    },
    getPrimaryDisplayId(order) {
      // The rider's code — matches the format validated in the Manual Order modal
      // (Glovo: 12 digits, Just Eat: 9 digits, Uber: 5 hex chars, OWN_DELIVERY: free)
      return order.deliveryId || order.ticketId || order.posId || order.orderId;
    },
    getSecondaryDisplayId(order) {
      // The internal kitchen code (ticketId / posId), shown below primary when it differs
      if (order.deliveryId) {
        return order.ticketId || order.posId || null;
      }
      if (order.ticketId) {
        return order.posId || null;
      }
      return null;
    },
    handleRiderCheckIn(order) {
      // If the order was already manually/auto-delivered, ignore re-arrivals
      if (this.riderDeliveredOrderIds[order.orderId]) return;
      const primaryId = this.getPrimaryDisplayId(order);
      const secondaryId = this.getSecondaryDisplayId(order);
      const platform = order.deliveryPlatform || null;
      const arrivalTime = this.getRiderArrivalDate(order);
      logger.info(`Rider check-in detected for order: ${primaryId}`);

      this.pushRiderToast({ primaryId, secondaryId, orderId: order.orderId, platform, arrivalTime, type: 'arrived' });
    },
    pushRiderToast({ primaryId, secondaryId = null, orderId, platform = null, arrivalTime = null, type = 'arrived', isReminder = false, via = null }) {
      const waitMinutes = arrivalTime
        ? Math.max(0, Math.floor((Date.now() - arrivalTime.getTime()) / 60000))
        : 0;
      const toastId = ++this.riderToastCounter;
      this.riderToastQueue.push({ id: toastId, primaryId, secondaryId, orderId, platform, waitMinutes, type, isReminder, via });
      if (via === 'scan') this.playOrderClosedSound();
      else this.playRiderSound(type, waitMinutes);
      // The `visibleRiderToasts` watcher starts the 8 s auto-dismiss timer only once this toast
      // is actually visible — that way queued-up toasts get their fair share of screen time.
    },
    onToastClick(toastId) {
      // If the user just swiped, suppress the click
      const state = this.swipeStates[toastId];
      if (state && state.moved) return;
      // On mobile a tap dismisses instantly; swipes still animate.
      // On tablet/desktop a click slides away to the left for visual consistency.
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (isMobile) {
        this.dismissRiderToastById(toastId);
      } else {
        this.animateToastAway(toastId, -1);
      }
    },
    animateToastAway(toastId, direction = -1) {
      // Seed with offset 0 so the browser has a baseline to transition from,
      // then move to off-screen on the next frame.
      this.swipeStates[toastId] = {
        startX: 0,
        startTime: Date.now(),
        offset: 0,
        dragging: false,
        moved: false
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const state = this.swipeStates[toastId];
          if (state) state.offset = direction * window.innerWidth;
        });
      });
      setTimeout(() => {
        this.dismissRiderToastById(toastId);
        delete this.swipeStates[toastId];
      }, 260);
    },
    onSwipeStart(toastId, event) {
      const touch = event.touches && event.touches[0];
      if (!touch) return;
      this.swipeStates[toastId] = {
        startX: touch.clientX,
        startTime: Date.now(),
        offset: 0,
        dragging: true,
        moved: false
      };
    },
    onSwipeMove(toastId, event) {
      const state = this.swipeStates[toastId];
      if (!state || !state.dragging) return;
      const touch = event.touches && event.touches[0];
      if (!touch) return;
      const dx = touch.clientX - state.startX;
      // Allow both directions
      state.offset = dx;
      if (Math.abs(dx) > 8) state.moved = true;
    },
    onSwipeEnd(toastId) {
      const state = this.swipeStates[toastId];
      if (!state) return;
      const dx = state.offset;
      const dt = Math.max(1, Date.now() - state.startTime);
      const velocity = Math.abs(dx) / dt; // px/ms
      state.dragging = false;
      // Dismiss threshold: 80px offset OR fast flick (velocity > 0.4 px/ms)
      if (Math.abs(dx) > 80 || velocity > 0.4) {
        const direction = dx < 0 ? -1 : 1;
        state.offset = direction * window.innerWidth;
        setTimeout(() => {
          this.dismissRiderToastById(toastId);
          delete this.swipeStates[toastId];
        }, 220);
      } else {
        state.offset = 0;
        setTimeout(() => {
          delete this.swipeStates[toastId];
        }, 240);
      }
    },
    dismissRiderToastWithAnimation(toastId) {
      // Used by the auto-dismiss timer — slide out to the left for consistency.
      if (!this.riderToastQueue.some(t => t.id === toastId)) return;
      this.animateToastAway(toastId, -1);
    },
    dismissRiderToastById(toastId) {
      // Clear any pending auto-dismiss timer for this toast
      if (this.riderToastTimers[toastId]) {
        clearTimeout(this.riderToastTimers[toastId]);
        delete this.riderToastTimers[toastId];
      }
      this.riderToastQueue = this.riderToastQueue.filter(t => t.id !== toastId);
    },
    isOrderCorrect(order) {
      return order?.order?.manualIsCorrect === true ||
        (order?.order?.orderAnalyses?.isCorrect === true && order?.order?.orderAnalyses?.appResponse === 'ORDER_ANALYZED') ||
        order?.order?.orderAnalyses?.appResponse === 'EMPTY_ORDER_CONTENT';
    },
    // A verdict is "terminal" once the order is decided correct OR decided wrong (not pending).
    // Keyed off isCorrect (symmetric both ways) so a just-turned-correct order is recognised as
    // terminal on later diffs and never double-toasts, regardless of appResponse timing.
    hasTerminalVerdict(order) {
      // isCorrect === false only counts once appResponse is set: the CV creates the row with the
      // DEFAULT false before any comparison (see verdictState.js).
      return isDecidedAnalysis(order?.order?.orderAnalyses, order?.order?.manualIsCorrect);
    },
    markForegroundVerdict(orderId) {
      if (orderId) this.foregroundVerdictOrders[orderId] = Date.now();
    },
    unmarkForegroundVerdict(orderId) {
      if (orderId) delete this.foregroundVerdictOrders[orderId];
    },
    setOpenCameraOrder(orderId) {
      this.openCameraOrderId = orderId || null;
    },
    clearOpenCameraOrder() {
      this.openCameraOrderId = null;
    },
    // Fired per order when the realtime orders refresh lands. When an order that was still
    // pending reaches a verdict — a background/queued analysis finishing, or an order auto-
    // turning green on reconnect — surface it as a non-blocking toast, so a busy operator with
    // a long list notices it. Foreground results are suppressed via foregroundVerdictOrders
    // (the modal already showed them). Only orders present before can "transition", so an
    // initial load or a freshly-arrived already-analysed order never mass-toasts.
    maybeToastVerdict(oldOrder, newOrder) {
      if (!oldOrder || this.hasTerminalVerdict(oldOrder)) return;
      const analysis = newOrder?.order?.orderAnalyses;
      // Symmetric with the incorrect case (isCorrect === false). Keying "correct" off
      // isOrderCorrect's stricter ORDER_ANALYZED requirement made the success toast miss
      // whenever appResponse wasn't set at the instant we diffed, while the incorrect toast
      // always fired — the asymmetry Eric saw (only wrong orders notified). This also fixes the
      // "background" result toast for correct orders, which is delivered through this same path.
      const nowCorrect = analysis?.isCorrect === true ||
        newOrder?.order?.manualIsCorrect === true ||
        analysis?.appResponse === 'EMPTY_ORDER_CONTENT';
      // A provisional row (isCorrect default false, appResponse still null) is not a verdict.
      const nowIncorrect = analysis?.isCorrect === false && isDecidedAnalysis(analysis);
      if (!nowCorrect && !nowIncorrect) return; // still pending

      const orderId = newOrder.orderId;
      // Same open-order suppression as the realtime path — never auto-toast the order the
      // operator is currently capturing in the foreground.
      if (this.openCameraOrderId && orderId === this.openCameraOrderId) return;
      // Deduped by analysisId against the realtime/queue paths — a full refetch must not
      // re-toast a verdict those already surfaced.
      const analysisId = analysis?.id || orderId;
      if (this.verdictToasted[analysisId]) return;
      const marked = this.foregroundVerdictOrders[orderId];
      if (marked && Date.now() - marked < 20000) {
        delete this.foregroundVerdictOrders[orderId]; // shown in the foreground — don't double-signal
        return;
      }
      this.verdictToasted[analysisId] = Date.now();
      this.pruneVerdictToasted();

      // Match the code shown on the order tile (OrderSelectionComponent.getOrderDisplayText):
      // ticketId → deliveryId → posId, so the toast names the order the operator sees.
      const label = newOrder.ticketId || newOrder.deliveryId || newOrder.posId || newOrder.orderId;
      this.showAppToast(nowCorrect
        ? { orderLabel: label, tag: this.$t('message.tagCorrect'), body: '', variant: 'success' }
        : { orderLabel: label, tag: this.$t('message.tagReview'), body: this.$t('message.reviewGeneric'), variant: 'warning' });
    },
    getRiderArrivalDate(order) {
      const ev = order?.order?.orderEvents?.find(e => e.event === 'RIDER_CHECK_IN');
      if (!ev?.registeredAt) return null;
      let ts = ev.registeredAt;
      if (typeof ts === 'string' && !ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) {
        ts += 'Z';
      }
      return new Date(ts);
    },
    checkRiderReminders() {
      if (!this.processedOrders) return;
      const now = Date.now();
      const REMINDER_INTERVAL_MS = 2 * 60 * 1000; // 2 min
      for (const order of this.processedOrders) {
        const arrival = this.getRiderArrivalDate(order);
        if (!arrival) continue;
        // Skip delivered (manually or auto)
        if (this.riderDeliveredOrderIds[order.orderId]) continue;
        // Only remind for orders not yet analyzed (not correct)
        if (this.isOrderCorrect(order)) continue;

        const minutesWaiting = Math.floor((now - arrival.getTime()) / 60000);
        if (minutesWaiting < 2) continue;

        // Recurring: only push if at least 2 min since last reminder for this order
        const lastReminder = this.riderReminderTracked[order.orderId] || 0;
        if (now - lastReminder < REMINDER_INTERVAL_MS) continue;
        this.riderReminderTracked[order.orderId] = now;

        const primaryId = this.getPrimaryDisplayId(order);
        const secondaryId = this.getSecondaryDisplayId(order);
        const platform = order.deliveryPlatform || null;
        logger.warning(`Rider reminder: order ${primaryId} waiting ${minutesWaiting} min`);

        this.pushRiderToast({ primaryId, secondaryId, orderId: order.orderId, platform, arrivalTime: arrival, type: 'reminder', isReminder: true });
      }
    },
    checkRiderAutoDelivery() {
      if (!this.processedOrders) return;
      const now = Date.now();
      const EXTERNAL_EXIT_GRACE_MS = 60 * 1000; // 1 min — matches get_raw_orders_realtime external_exit_grace_minutes
      for (const order of this.processedOrders) {
        if (this.riderDeliveredOrderIds[order.orderId]) continue;

        // Platform (EXTERNAL_) exit: the courier left / the order was delivered on the platform. Hide it
        // 1 min after the event — same grace as the backend fetch — so it disappears in step with a
        // reload instead of at once. Own exits are hidden immediately (the operator's tap / its relay).
        const extExit = order.order?.orderEvents?.find(e =>
          e.event === 'EXTERNAL_RIDER_LEFT_THE_RESTAURANT' || e.event === 'EXTERNAL_ORDER_DELIVERED_TO_CUSTOMER');
        if (extExit?.registeredAt) {
          let ts = extExit.registeredAt;
          if (typeof ts === 'string' && !ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) ts += 'Z';
          if (now - new Date(ts).getTime() >= EXTERNAL_EXIT_GRACE_MS) {
            this.riderDeliveredOrderIds[order.orderId] = now;
            delete this.riderReminderTracked[order.orderId];
            continue;
          }
        }

        if (!this.isOrderCorrect(order)) continue;
        const arrival = this.getRiderArrivalDate(order);
        if (!arrival) continue;
        const minutes = Math.floor((now - arrival.getTime()) / 60000);
        if (minutes >= 10) {
          logger.info(`Auto-delivering order ${order.orderId} (rider waited ${minutes} min)`);
          this.riderDeliveredOrderIds[order.orderId] = now;
          delete this.riderReminderTracked[order.orderId];
          reportForensicEvent('rider_marked_left', {
            order_id: order.orderId,
            delivery_id: order.deliveryId ?? null,
            platform: order.deliveryPlatform || null,
            minutes_waited: minutes,
            source: 'auto',
          });
        }
      }
    },
    // `source` is how the hand-over was seen: «manual», the operator's tap, or «scan», the QR gun reading
    // the ticket.
    markRiderDelivered(order, { source = 'manual' } = {}) {
      if (!order || !order.orderId) return;
      if (this.riderDeliveredOrderIds[order.orderId]) return;
      const primaryId = this.getPrimaryDisplayId(order);
      const secondaryId = this.getSecondaryDisplayId(order);
      const platform = order.deliveryPlatform || null;
      logger.info(`Order marked as delivered to rider: ${primaryId}`);

      this.riderDeliveredOrderIds[order.orderId] = Date.now();
      delete this.riderReminderTracked[order.orderId];
      // Operator tap that removes the order from the rider list (maps to
      // RIDER_LEFT_THE_RESTAURANT). source distinguishes it from the auto path.
      const riderArrival = this.getRiderArrivalDate(order);
      reportForensicEvent('rider_marked_left', {
        order_id: order.orderId,
        delivery_id: order.deliveryId ?? null,
        platform,
        minutes_waited: riderArrival ? Math.floor((Date.now() - riderArrival.getTime()) / 60000) : null,
        source,
      });

      // Persist the handover as a RIDER_LEFT_THE_RESTAURANT order_event. This makes the hide durable
      // (the backend rider-arm anti-joins RIDER_LEFT → stops returning the order, so it can't resurrect
      // on reload the way the in-memory tombstone alone did), fans an order_update out to the other
      // tablets so they hide it too, and feeds the own-delivery dwell metric. Fire-and-forget: the card
      // is already hidden above, so a failure just leaves the pre-existing optimistic behavior.
      // Only the explicit tap writes the event — the 10-min auto-deliver stays client-side (it's a
      // timeout, not an observed departure) so it never pollutes the dwell metric.
      if (source === 'scan') {
        // The scan is the only sign the rider took the bag (no tap follows it), so a network failure is
        // retried and, if it never lands, said on screen.
        this.saveRiderLeftFromScan(order.orderId, primaryId);
      } else {
        registerRiderLeft(order.orderId);
      }

      this.pushRiderToast({ primaryId, secondaryId, orderId: order.orderId, platform, type: 'delivered', via: source === 'scan' ? 'scan' : null });
    },
    playRiderSound(type = 'arrived', waitMinutes = 0) {
      // Throttle to avoid overlapping sounds when multiple toasts fire almost at once
      const now = Date.now();
      if (now - this.lastRiderSoundAt < 1500) return;
      this.lastRiderSoundAt = now;

      if (type === 'delivered') {
        this.playRiderDeliveredChime();
        return;
      }

      // Escalating urgency:
      // - arrived (just now)              → arrival sound at 1.0×
      // - reminder <10 min waiting         → arrival sound at 1.15× (subtle "still waiting" cue)
      // - reminder ≥10 min waiting         → long-wait sound (more urgent)
      const isLongWait = type === 'reminder' && waitMinutes >= 10;
      const isMediumWait = type === 'reminder' && waitMinutes < 10;
      const audioEl = isLongWait ? this.$refs.riderWaitingLongSound : this.$refs.riderArrivedSound;
      const rate = isMediumWait ? 1.15 : 1.0;
      if (!audioEl) return;
      try {
        audioEl.pause();
        audioEl.currentTime = 0;
        audioEl.playbackRate = rate;
        const p = audioEl.play();
        if (p && typeof p.then === 'function') {
          // The mp3 path fails differently from WebAudio: autoplay policy rejects play()
          // with NotAllowedError instead of handing back a suspended context, so both
          // outcomes go to fx_audio_state (see src/audio.js) and not just to the logger,
          // which never reached product telemetry.
          p.then(() => reportAudioPlaybackOk('rider'))
            .catch(err => {
              reportAudioPlaybackBlocked('rider', err);
              logger.warning('Rider sound blocked by browser', { error: err });
            });
        }
      } catch (e) {
        reportAudioPlaybackBlocked('rider', e);
        logger.error('Error playing rider sound', { error: e });
      }
    },
    // The order closed by the QR gun: a rising major arpeggio (C6 E6 G6, the last held), the classic
    // «done» of a till, longer and brighter than the two-note «Entregado» tap so the kitchen hears that
    // the scan landed without looking. Not throttled: two bags scanned in a row each get theirs.
    playOrderClosedSound() {
      const audioCtx = getSharedAudioContext('rider');
      if (!audioCtx) return;
      try {
        const t0 = audioCtx.currentTime;
        [[1046.5, 0, 0.12], [1318.5, 0.1, 0.12], [1568, 0.2, 0.38]].forEach(([freq, at, length]) => {
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          osc.connect(gain);
          gain.connect(audioCtx.destination);
          osc.type = 'triangle';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.0001, t0 + at);
          gain.gain.exponentialRampToValueAtTime(0.35, t0 + at + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + length);
          osc.start(t0 + at);
          osc.stop(t0 + at + length + 0.02);
        });
        if (audioCtx.state === 'running') reportAudioPlaybackOk('rider');
      } catch (e) {
        logger.error('Error playing order closed sound', { error: e });
      }
    },
    playRiderDeliveredChime() {
      // Shared context, same as the coverage cue: a fresh one per chime leaked audio threads
      // against Chrome's per-page cap, and it was born `suspended` in a kiosk just the same —
      // only without anything watching. Every note below is scheduled relative to
      // `currentTime`, so the chime is bit-for-bit the same as before.
      const audioCtx = getSharedAudioContext('rider');
      if (!audioCtx) return;
      try {
        const frequencies = [880, 1320];
        frequencies.forEach((freq, i) => {
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          osc.connect(gain);
          gain.connect(audioCtx.destination);
          osc.frequency.value = freq;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.2, audioCtx.currentTime + i * 0.12);
          gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + i * 0.12 + 0.11);
          osc.start(audioCtx.currentTime + i * 0.12);
          osc.stop(audioCtx.currentTime + i * 0.12 + 0.12);
        });
      } catch (e) {
        logger.error('Error playing delivered chime', { error: e });
      }
    },
    // --- Photo coverage alert (100% local, bottom-up nudge) --------------------
    // Detects on the tablet when several orders pile up without a photo and shows a
    // non-blocking top banner asking the team to resume taking photos: any tap outside
    // dismisses it and it auto-closes after AUTO_DISMISS_MIN unanswered. Runs off
    // the existing 30 s interval (never the order-list render), so it adds no cost to
    // the hot path on low-end tablets. Reuses the photoCount / createdAt signals that
    // are already maintained end-to-end — nothing is recomputed.
    getOrderCreatedDate(order) {
      let ts = order?.createdAt || order?.order?.createdAt;
      if (!ts) return null;
      // Timestamps without a timezone are UTC (same normalization as rider arrivals).
      if (typeof ts === 'string' && !ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) {
        ts += 'Z';
      }
      return new Date(ts);
    },
    // minAgeMin: the coverage alert only counts orders past GRACE_MIN (young ones are
    // normal flow); the changelog gate passes 0 because for "is the kitchen busy?" a
    // 2-minute-old pending order counts just as much as a 20-minute-old one.
    countOverduePhotoCoverage(minAgeMin = COVERAGE_ALERT.GRACE_MIN) {
      if (!this.processedOrders) return 0;
      const now = Date.now();
      let count = 0;
      for (const order of this.processedOrders) {
        if ((order.photoCount || 0) > 0) continue;           // already photographed (any tablet)
        // Photographed locally but the upload/backend confirmation may still be in flight
        // (slow kitchen network): don't count it as "sin foto" — that would be a false alarm.
        if (this.coverageRecentLocalPhoto[order.orderId]) continue;
        if (this.isOrderCorrect(order)) continue;            // already resolved / green
        if (this.riderDeliveredOrderIds[order.orderId]) continue;
        const created = this.getOrderCreatedDate(order);
        if (!created) continue;
        const minutes = Math.floor((now - created.getTime()) / 60000);
        // "Overdue" = past the grace period but not a stale zombie: blue (no-photo) orders
        // never age out locally, so cap the window to avoid counting abandoned ones.
        if (minutes >= minAgeMin && minutes <= COVERAGE_ALERT.MAX_MIN) count++;
      }
      return count;
    },
    checkPhotoCoverage() {
      // Live sales demo on: three orders waiting for their photo are the scenario, not a store in trouble.
      if (liveDemoOn()) {
        if (this.coverageAlert.visible) this.hideCoverageAlert();
        return;
      }
      // Never over the login screen (the banner lives outside <component :is>, always mounted).
      if (this.currentComponent === 'LoginScreen' || !this.restaurant) {
        if (this.coverageAlert.visible) this.hideCoverageAlert();
        return;
      }
      // Experiment arm from PostHog (`photo-coverage-alerts`, assigned per restaurant_id):
      //  - 'banner': the full nudge.
      //  - 'shadow': the detector runs IDENTICALLY (thresholds, local-photo suppression,
      //    budget, 2 min episode lifecycle) and emits the same fx_coverage_* telemetry, but
      //    nothing is shown or heard. It gives the control stores the exact moments they
      //    "would have been nudged", so recovery after an episode can be compared arm vs arm.
      //  - off: nothing runs.
      const variant = this.coverageFlagVariant();
      if (!variant) {
        this.coverageVariant = null;
        if (this.coverageAlert.visible) this.hideCoverageAlert();
        return;
      }
      this.coverageVariant = variant;
      const now = Date.now();
      const count = this.countOverduePhotoCoverage();

      // Close the promise made by "Sí, en el siguiente pedido", either way:
      //  - recovered inside the window  → thank them once, reset the escalation.
      //  - window expired still short   → the promise was broken: record it and escalate,
      //    so the next alert arrives sooner and louder instead of being a carbon copy.
      if (!this.coverageAlert.visible && this.coverageAckAt) {
        if (count < COVERAGE_ALERT.THRESHOLD) {
          const minutesSinceAck = Math.round((now - this.coverageAckAt) / 60000);
          this.coverageAckAt = 0;
          this.coverageAckUntil = 0;
          this.coverageAckBroken = 0;
          this.showCoverageThanks(minutesSinceAck, count);
        } else if (now >= this.coverageAckUntil) {
          this.coverageAckAt = 0;
          this.coverageAckUntil = 0;
          this.coverageAckBroken += 1;
          this.captureCoverage('coverage_ack_broken', {
            overdue_count: count,
            consecutive: this.coverageAckBroken,
            window_min: Math.round(COVERAGE_ALERT.COOLDOWN_ACK_MIN)
          });
        }
      }

      if (this.coverageAlert.visible) {
        // Auto-resolve when coverage recovers (they photographed / orders cleared).
        if (count < COVERAGE_ALERT.THRESHOLD) {
          this.resolveCoverageAlert('recovered', count);
          return;
        }
        this.coverageAlert.count = count; // keep the live count fresh while shown
        // No re-sound while shown: the banner is a nudge, not a demand. It sounds once on
        // show and auto-closes shortly after (see showCoverageAlert) — the Gaudí forensics
        // proved that pressing harder on an unanswered alert only suppresses photos further.
        return;
      }

      // Don't fire while the tab is backgrounded — nobody would see or hear it.
      if (document.hidden) return;
      // Only fire where the banner can actually render (it's gated to CameraScreen and
      // hidden while photographing): firing elsewhere would sound, spend budget and time
      // out unseen. The 30 s tick retries once they're back.
      if (this.currentComponent !== 'CameraScreen' || this.isPhotographing) return;
      // Suppressed right after an acknowledge / snooze / support / re-arm so we don't nag.
      if (now < this.coverageSnoozedUntil || now < this.coverageCooldownUntil) return;

      if (count >= COVERAGE_ALERT.THRESHOLD) {
        const scope = this.coverageBudgetSpent(now);
        if (scope) {
          // Budget spent: stop asking. Emit once per budget so the store surfaces as a
          // manager signal — this is the line where nudging the employee stops working.
          if (this.coverageCappedScope !== scope) {
            this.coverageCappedScope = scope;
            this.captureCoverage('coverage_capped', {
              scope,
              overdue_count: count,
              alerts_last_shift: this.coverageAlertsWithin(now, COVERAGE_ALERT.SHIFT_WINDOW_H),
              alerts_last_day: this.coverageAlertsWithin(now, 24)
            });
          }
          return;
        }
        this.coverageCappedScope = null;
        this.showCoverageAlert(count);
      }
    },
    // Alerts shown inside the last `hours`, from the persisted rolling history.
    coverageAlertsWithin(now, hours) {
      const from = now - hours * 3600 * 1000;
      return this.coverageHistory.filter(t => t > from).length;
    },
    // Which budget (if any) is spent right now. Shift is checked first: it is the tighter
    // one and the more useful label when both bind.
    coverageBudgetSpent(now) {
      if (this.coverageAlertsWithin(now, COVERAGE_ALERT.SHIFT_WINDOW_H) >= COVERAGE_ALERT.MAX_PER_SHIFT) return 'shift';
      if (this.coverageAlertsWithin(now, 24) >= COVERAGE_ALERT.MAX_PER_DAY) return 'day';
      return null;
    },
    // Multivariate flag; a plain boolean `true` (legacy on/off flag) means the full banner.
    coverageFlagVariant() {
      const flag = posthog.getFeatureFlag('photo-coverage-alerts');
      if (flag === 'shadow') return 'shadow';
      if (flag === 'banner' || flag === true) return 'banner';
      return null;
    },
    // Escalation ladder for the cue: each consecutive broken promise starts one step up.
    // Capped so it never becomes a siren. (The in-alert re-sound is gone with the redesign:
    // the cue plays once, when the banner appears.)
    coverageSoundLevel() {
      return Math.min(COVERAGE_ALERT.MAX_SOUND_LEVEL, this.coverageAckBroken);
    },
    showCoverageAlert(count) {
      const now = Date.now();
      this.coverageAlert = { visible: true, count };
      this.coverageAlertShownAt = now;
      this.coverageAlertStep = 'prompt';
      this.armCoverageAutoClose();
      // Spend one alert from the rolling budget, and persist so a reload can't refill it.
      this.coverageHistory = [...this.coverageHistory, now].filter(t => t > now - 24 * 3600 * 1000);
      saveCoverageHistory(this.restaurant?.id, this.coverageHistory);
      // A repeat after a broken promise gets the insistent cue — same motif, twice, louder.
      // Shadow arm: the episode exists for telemetry only; nothing must be heard.
      if (this.coverageVariant !== 'shadow') this.playCoverageSound(this.coverageSoundLevel());
      // Low-volume product event (no PII; device_id/restaurant_id are pinned server-side).
      this.captureCoverage('coverage_alert_shown', {
        overdue_count: count,
        threshold: COVERAGE_ALERT.THRESHOLD,
        grace_min: COVERAGE_ALERT.GRACE_MIN,
        after_broken_ack: this.coverageAckBroken
      });
    },
    // Unanswered ≠ pinned: the banner closes itself so it can never sit for hours the way
    // the blocking sheet did. A real timer (not the 30 s tick) so it matches the countdown
    // bar the component draws from these same numbers. Re-armed on every step navigation:
    // someone reading the reasons is INSIDE the alert, and the timer must never close it
    // under their finger mid-decision.
    armCoverageAutoClose() {
      clearTimeout(this.coverageAutoCloseTimer);
      this.coverageAutoCloseArmedAt = Date.now();
      this.coverageAutoCloseTimer = setTimeout(() => {
        if (this.coverageAlert.visible) {
          this.resolveCoverageAlert('timeout', this.coverageAlert.count);
        }
      }, COVERAGE_ALERT.AUTO_DISMISS_MIN * 60000);
    },
    onCoverageStepChange(step) {
      this.coverageAlertStep = step;
      if (this.coverageAlert.visible) this.armCoverageAutoClose();
    },
    hideCoverageAlert() {
      this.coverageAlert = { visible: false, count: 0 };
      this.coverageAlertShownAt = 0;
      this.coverageAutoCloseArmedAt = 0;
      clearTimeout(this.coverageAutoCloseTimer);
    },
    coverageMinutesShown() {
      return this.coverageAlertShownAt
        ? Math.floor((Date.now() - this.coverageAlertShownAt) / 60000)
        : 0;
    },
    // With a 2 min cap, minutes no longer discriminate anything (everything is 0-2): the
    // post-redesign question — "did they dismiss it without reading it?" — needs seconds.
    coverageSecondsShown() {
      return this.coverageAlertShownAt
        ? Math.round((Date.now() - this.coverageAlertShownAt) / 1000)
        : 0;
    },
    // Coverage telemetry rides the forensic lane (AppSync device-events -> EventBridge ->
    // Firehose -> Athena `bronze_tracker_forensic.events` as `fx_coverage_*`), NOT PostHog.
    // Why: the questions we need answered — did a photo follow the "yes"? did coverage hold
    // for the next 3/10 orders? — are JOINs against the order/photo data that already lives
    // in Athena; in PostHog those events are stranded. Bonus: device_id/restaurant_id are
    // pinned server-side from the authenticated channel (not client-supplied), and it costs
    // nothing against the PostHog event tier. PostHog keeps the rollout flag + error tracking.
    captureCoverage(event, props) {
      // Telemetry must never break the UX flow — swallow any emit error.
      try {
        // Every coverage event carries the experiment arm, so episodes can be split arm vs arm
        // in Athena without joining against the flag assignment list.
        reportForensicEvent(event, { ...props, variant: this.coverageVariant });
      } catch (e) {
        logger.console(`coverage telemetry failed for ${event}: ${e}`);
      }
    },
    resolveCoverageAlert(reason, count, dismissOrigin = null) {
      const minutesShown = this.coverageMinutesShown();
      const secondsShown = this.coverageSecondsShown();
      const shownAt = this.coverageAlertShownAt;
      const step = this.coverageAlertStep;
      this.hideCoverageAlert();
      // An instant OUTSIDE tap didn't really expose the alert (they were already touching
      // the screen): give the budget slot back (shownAt is the exact timestamp
      // showCoverageAlert pushed). The ✕/Escape is deliberate — "seen it, closing it" —
      // so it keeps the slot spent, or the banner would bounce back at someone who already
      // decided.
      if (reason === 'dismissed' && dismissOrigin === 'outside'
          && secondsShown < COVERAGE_ALERT.DISMISS_REFUND_SEC) {
        this.coverageHistory = this.coverageHistory.filter(t => t !== shownAt);
        saveCoverageHistory(this.restaurant?.id, this.coverageHistory);
      }
      // Every close without an explicit answer re-arms with a hold-off: after a recovery so
      // a count oscillating around the threshold doesn't re-fire every tick, and after a
      // timeout/dismiss so the same alert doesn't bounce right back (the budget still caps
      // how many times it can return).
      this.coverageCooldownUntil = Date.now() + COVERAGE_ALERT.REARM_MIN * 60000;
      this.captureCoverage('coverage_alert_resolved', {
        reason,
        overdue_count: count,
        minutes_shown: minutesShown,
        seconds_shown: secondsShown,
        step,
        ...(dismissOrigin ? { dismiss_origin: dismissOrigin } : {})
      });
    },
    // Shared exit path for the three user responses: apply the state change (cooldown /
    // snooze / hide) FIRST, then emit telemetry last, so a capture error can never make a
    // button feel dead.
    finishCoverage(response, cooldownMin, snoozeMin = 0) {
      const minutesShown = this.coverageMinutesShown();
      const secondsShown = this.coverageSecondsShown();
      const count = this.coverageAlert.count;
      const now = Date.now();
      if (cooldownMin) this.coverageCooldownUntil = now + cooldownMin * 60000;
      if (snoozeMin) this.coverageSnoozedUntil = now + snoozeMin * 60000;
      this.hideCoverageAlert();
      this.captureCoverage('coverage_alert_response', {
        response,
        overdue_count: count,
        minutes_shown: minutesShown,
        seconds_shown: secondsShown
      });
    },
    onCoverageAck() {
      // A "yes" that follows a broken "yes" buys less peace — the promise window shrinks.
      const cooldownMin = this.coverageAckBroken > 0
        ? COVERAGE_ALERT.COOLDOWN_ACK_REPEAT_MIN
        : COVERAGE_ALERT.COOLDOWN_ACK_MIN;
      this.finishCoverage('ack', cooldownMin);
      this.coverageAckAt = Date.now();
      this.coverageAckUntil = this.coverageAckAt + cooldownMin * 60000;
    },
    showCoverageThanks(minutesSinceAck, overdueCount) {
      this.coverageThanksVisible = true;
      clearTimeout(this.coverageThanksTimer);
      this.coverageThanksTimer = setTimeout(() => {
        this.coverageThanksVisible = false;
      }, 5000);
      // The effectiveness metric of the nudge: promise made ("yes") → promise kept.
      this.captureCoverage('coverage_recovered_after_ack', {
        minutes_since_ack: minutesSinceAck,
        overdue_count: overdueCount
      });
    },
    onCoverageBusy() {
      this.finishCoverage('busy', 0, COVERAGE_ALERT.SNOOZE_MIN);
    },
    onCoverageDismiss(origin) {
      // The listener removal rides the watcher flush (async): a two-finger tap can emit
      // dismiss twice before the banner is gone. Only the first one counts.
      if (!this.coverageAlert.visible) return;
      // Any tap outside the banner (or on its ✕ / Escape) closes it. Deliberate: using the
      // app always outranks reading the alert, so touching ANYTHING dismisses it. Counted
      // as a resolution (not a response) — the operator never answered the question.
      this.resolveCoverageAlert('dismissed', this.coverageAlert.count, origin || 'outside');
    },
    onCoverageSupport() {
      const count = this.coverageAlert.count;
      this.finishCoverage('support', COVERAGE_ALERT.COOLDOWN_SUPPORT_MIN);
      this.openCoverageSupport(count);
    },
    openCoverageSupport(count) {
      const store = this.restaurant?.name || '';
      const message = this.$t('message.coverageSupportPrefill', { store, count });
      if (window.Intercom) {
        // Pre-fills the Intercom composer with context; the user reviews and hits send.
        // (The SDK cannot send programmatically — showNewMessage only pre-populates.)
        window.Intercom('showNewMessage', message);
      } else {
        // Offline / Intercom not loaded: fall back to the existing help entry point.
        this.openHelp('coverage_alert');
      }
    },
    resetCoverageState() {
      // Clean slate on logout / session expiry so a new session doesn't inherit a stale
      // alert, cooldown, or local-photo suppression map.
      this.hideCoverageAlert();
      this.coverageSnoozedUntil = 0;
      this.coverageCooldownUntil = 0;
      this.coverageRecentLocalPhoto = {};
      this.coverageAckAt = 0;
      this.coverageAckUntil = 0;
      this.coverageAckBroken = 0;
      this.coverageThanksVisible = false;
      clearTimeout(this.coverageThanksTimer);
      this.isPhotographing = false;
      // The budget belongs to the restaurant, not the session: it is deliberately NOT reset
      // here, or signing out and back in would refill it. loadCoverageHistory drops it when
      // a different restaurant logs into this tablet.
      this.coverageCappedScope = null;
    },
    playCoverageSound(level = 0) {
      // Share the rider sound throttle so a coincident 30 s tick can't double up.
      const now = Date.now();
      if (now - this.lastRiderSoundAt < 1500) return;
      this.lastRiderSoundAt = now;
      // Designed to survive a working kitchen, not to alarm it. Three choices do the work:
      //  - Register: a rising C6-E6-G6 arpeggio (1046/1319/1568 Hz). Extraction hoods,
      //    fryers and chatter pile up below ~800 Hz, and human hearing peaks around
      //    2-4 kHz, so the same amplitude is far more audible up here than the old
      //    659/784 Hz pair, which sat right inside the rumble.
      //  - Timbre: triangle, not sine. Its odd harmonics give the ear several cues to
      //    latch onto; a pure sine is the easiest waveform for broadband noise to mask.
      //  - Rhythm: three short pulses. Repetition beats level for detection in noise,
      //    which is what lets this stay musical instead of turning into a siren.
      // `level` escalates by REPEATING the motif rather than by shouting: each step adds a
      // burst and a little gain. Repetition is what wins in noise, and it keeps the cue
      // recognisable as the same sound instead of mutating into an alarm.
      //   0 — first ask            1 burst,  0.34
      //   1 — unanswered / broken  2 bursts, 0.42
      //   2 — still unanswered     3 bursts, 0.50 (ceiling)
      const ctx = getSharedAudioContext('coverage');
      if (!ctx) return;
      try {
        const base = ctx.currentTime;
        const motif = [1046.5, 1318.5, 1568.0]; // C6 · E6 · G6
        const tier = Math.max(0, Math.min(COVERAGE_ALERT.MAX_SOUND_LEVEL, Math.round(level)));
        const peakGain = [0.34, 0.42, 0.50][tier];
        const step = 0.13;      // gap between pulses
        const dur = 0.16;       // pulse length
        const bursts = [[0], [0, 0.62], [0, 0.56, 1.12]][tier];
        for (const burstAt of bursts) {
          motif.forEach((f, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'triangle';
            osc.frequency.value = f;
            const t0 = base + burstAt + i * step;
            gain.gain.setValueAtTime(0.0001, t0);
            gain.gain.exponentialRampToValueAtTime(peakGain, t0 + 0.012); // crisp attack
            gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
            osc.start(t0);
            osc.stop(t0 + dur + 0.02);
          });
        }
      } catch (e) {
        logger.error('Error playing coverage sound', { error: e });
      }
    },
    initializeIntercom() {
      import('@intercom/messenger-js-sdk').then(({ default: Intercom }) => {
        Intercom({
          app_id: INTERCOM_APP_ID,
          region: INTERCOM_REGION,
        });
        
        // Hide Intercom by default
        if (window.Intercom) {
          window.Intercom('hide');
          // The tutorial links support sends in the conversation open the tutorial here, not in a new
          // tab: re-arm the messenger's frames every second while it is shown (Intercom rewrites them).
          window.Intercom('onShow', () => {
            const arm = () => catchTutorialLinks(document, (name) => this.openTutorialLink(name));
            arm();
            if (this.tutorialLinkTimer) clearInterval(this.tutorialLinkTimer);
            this.tutorialLinkTimer = setInterval(arm, 1000);
          });
          window.Intercom('onHide', () => {
            if (this.tutorialLinkTimer) clearInterval(this.tutorialLinkTimer);
            this.tutorialLinkTimer = null;
          });
        }
      });
    },
  },
};
</script>

<style>
body,
html {
  margin: 0;
  padding: 0;
  background-color: var(--bz-p-0c1117);
  font-family: "DM Sans", sans-serif;
  color: var(--bz-p-white);
  overflow: hidden;
  /* Prevent pinch zoom and other touch interactions */
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  /* Fix the height to match viewport */
  height: 100%;
  width: 100%;
  position: fixed;
}

/* Ensure app container fills the screen */
#app {
  height: 100%;
  width: 100%;
  overflow: hidden;
  /* Prevent overscroll behavior */
  overscroll-behavior: none;
  /* Ensure proper iOS rendering */
  -webkit-overflow-scrolling: touch;
}

/* The page's own text size stays the system's: no `text-size-adjust: none`, so a user who raised the font size (or
   zooms) gets it (WCAG 1.4.4). */
* {
  -webkit-tap-highlight-color: transparent;
}

/* The focus-outline reset that lived here moved to src/ui/cascade.css, into the `bz-reset`
   cascade layer: outside a layer it also erased the focus ring of every PrimeVue component. */

/* The app's press, on every button: it dims in 100 ms, and — unless the system asked for less motion — shrinks
   to 0.96, decelerating (the system's ease-out, not the browser's `ease`). */
button,
a,
[role="button"],
.order-button,
.header-button,
.option-button,
.mobile-toggle-btn,
.mode-toggle,
.close-button,
.back-button,
.back-icon,
.update-icon {
  transition: opacity 100ms var(--bz-ease-out);
  -webkit-tap-highlight-color: transparent;
}

button:active,
a:active,
[role="button"]:active,
.order-button:active,
.header-button:active,
.option-button:active,
.mobile-toggle-btn:active,
.mode-toggle:active,
.close-button:active,
.back-button:active,
.back-icon:active,
.update-icon:active {
  opacity: 0.85;
}

@media (prefers-reduced-motion: no-preference) {
  button,
  a,
  [role="button"],
  .order-button,
  .header-button,
  .option-button,
  .mobile-toggle-btn,
  .mode-toggle,
  .close-button,
  .back-button,
  .back-icon,
  .update-icon {
    transition: transform 100ms var(--bz-ease-out), opacity 100ms var(--bz-ease-out);
  }

  button:active,
  a:active,
  [role="button"]:active,
  .order-button:active,
  .header-button:active,
  .option-button:active,
  .mobile-toggle-btn:active,
  .mode-toggle:active,
  .close-button:active,
  .back-button:active,
  .back-icon:active,
  .update-icon:active {
    transform: scale(0.96);
  }
}

/* Prevent animation on disabled buttons */
button:disabled:active {
  transform: none;
  opacity: 1;
}

/* =========================================================================
   Toasts — the cards of the system (.bz-toast, src/ui/system.css), laid out here.
   Designed to be read at 1–2 m: the order code (36) > the tag (a capsule at 14) > the minutes waiting.
   The rider's is in its own red; the messages sit on the raised surface. Nothing pulses (norm 1): the
   urgency of a wait of 10 minutes or more is the red, its border and its words.
   ========================================================================= */
.rider-toast-container {
  position: fixed;
  top: calc(var(--bz-bar-height) + var(--bz-space-3));
  left: var(--bz-gutter);
  width: min(440px, calc(100vw - 2 * var(--bz-gutter)));
  z-index: var(--bz-z-rider-toasts);
  display: flex;
  flex-direction: column;
  pointer-events: none;
}

/* Clear of the coverage banner. Measured: 161 px on a tablet (225 with the second step's tiles on two rows, in French),
   213-221 stacked at 800 px, and 297-353 on a phone, where its buttons and tiles take a row each. */
.rider-toast-container.is-below-coverage { top: calc(env(safe-area-inset-top, 0px) + 240px); }

@media screen and (max-width: 599px) {
  .rider-toast-container.is-below-coverage { top: calc(env(safe-area-inset-top, 0px) + 372px); }
}

.rider-toast-container > .bz-toast {
  position: relative;
  overflow: hidden;
  width: 100%;
  margin-bottom: var(--bz-target-gap);
  cursor: pointer;
  pointer-events: auto;
  touch-action: pan-y;
  font-family: var(--bz-font);
  font-optical-sizing: auto;
  font-synthesis: none;
  font-kerning: normal;
  /* Enters with a fade; the slide below only when the system allows movement. It leaves faster than it came. */
  animation: riderToastFadeIn var(--bz-duration) var(--bz-ease-out);
  transition: opacity var(--bz-duration-fast) var(--bz-ease-out);
}

.rider-toast-container > .bz-toast.is-swiping {
  transition: none;
}

@keyframes riderToastFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes riderToastSlideIn {
  from { transform: translateX(calc(var(--bz-space-4) * -1)); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes riderToastDropIn {
  from { transform: translateY(calc(var(--bz-space-4) * -1)); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@media (prefers-reduced-motion: no-preference) {
  .rider-toast-container > .bz-toast {
    animation-name: riderToastSlideIn;
    transition: transform var(--bz-duration-fast) var(--bz-ease-out), opacity var(--bz-duration-fast) var(--bz-ease-out);
  }
}

/* A phone: the toasts come down from the bar, not in from the side. */
@media (prefers-reduced-motion: no-preference) and (max-width: 767px) {
  .rider-toast-container > .bz-toast {
    animation-name: riderToastDropIn;
  }
}

/* With "reduce motion" a toast that is let go fades where it stands: the slide off the screen is the
   inline `transform` of the swipe, and only a finger on the glass (.is-swiping) keeps it. */
@media (prefers-reduced-motion: reduce) {
  .rider-toast-container > .bz-toast:not(.is-swiping) {
    transform: none !important;
  }
}

/* A rider waiting 10 minutes or more: the toast turns to the rider's solid red, fixed — what is urgent is
   read at a glance in the solid, never in the dark container, which loses its colour under a kitchen's
   glare (paleta-bronze.md, «Cómo se usa», 3). Its tag and its icon disc go to the container to stay apart. */
.rider-toast-container > .bz-toast.is-urgent {
  border-color: transparent;
  background: var(--bz-rider-solid);
  color: var(--bz-on-rider-solid);
}

.rider-toast-container > .bz-toast.is-urgent .bz-chip--rider,
.rider-toast-container > .bz-toast.is-urgent .bz-toast__icon {
  background: var(--bz-rider-container);
  color: var(--bz-on-rider-container);
}

/* The tag: a capsule at 14. On the rider's red it is its solid red with white. */
.rider-toast-tag {
  align-self: flex-start;
  margin-bottom: var(--bz-space-1);
}

.rider-toast-meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--bz-space-3);
}

.bz-toast--rider .bz-chip--rider {
  background: var(--bz-rider-solid);
  color: var(--bz-on-rider-solid);
}

/* A code is long (12 digits) and must never push the card wider. */
.rider-toast-container .bz-toast__code {
  overflow-wrap: anywhere;
}

/* The ticket code and the platform, under the rider's code. */
.rider-toast-sub {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--bz-space-1) var(--bz-space-3);
}

.bz-toast--rider .bz-toast__text {
  color: inherit;
}

.rider-toast-platform {
  display: inline-flex;
  align-items: center;
  gap: var(--bz-space-2);
  min-width: 0;
}

.rider-toast-platform-icon {
  flex: 0 0 auto;
  width: var(--bz-icon-md);
  height: var(--bz-icon-md);
  object-fit: contain;
  border-radius: calc(var(--bz-radius-sm) / 2);
  background: var(--bz-control);
}

.rider-toast-wait .bz-icon {
  font-size: var(--bz-icon-xs);
}

/* The icon's disc takes the colour of what the message says. */
.bz-toast__icon.is-msg-success,
.bz-toast.is-delivered .bz-toast__icon {
  background: var(--bz-ok-container);
  color: var(--bz-on-ok-container);
}

.bz-toast__icon.is-msg-warning {
  background: var(--bz-warn-container);
  color: var(--bz-on-warn-container);
}

/* A message with no order: the icon and one line, 18/24. */
.rider-toast-msg-text,
.rider-toast-msg-body {
  color: var(--bz-ink);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-label);
  white-space: pre-line;
  overflow-wrap: anywhere;
}

/* The countdown: a fine rounded bar inside the card's padding, on the content's margins, emptying for as long
   as the toast stays. It says how much is left, so it keeps running with "reduce motion". It used to run
   along the card's very edge, where the corner radius cut its ends off (Adrián, 13-oct-2026); now the card
   keeps room for it under the content (16 + 4 + 12), and the bar is a capsule of its own.
   The fill slides out to the left inside a clear track (translateX, compositable) instead of shrinking
   (scaleX would flatten its round end): what is left keeps a round end, and the track's own clips the start. */
.rider-toast-container > .bz-toast {
  padding-bottom: var(--bz-space-7);
}

.rider-toast-progress {
  position: absolute;
  left: var(--bz-space-4);
  right: var(--bz-space-5);
  bottom: var(--bz-space-4);
  height: var(--bz-space-1);
  overflow: hidden;
  border-radius: var(--bz-radius-full);
}

.rider-toast-progress-fill {
  width: 100%;
  height: 100%;
  border-radius: var(--bz-radius-full);
  background: var(--bz-rider-solid);
  animation: riderProgressShrink 8s linear forwards;
}

/* On the urgent toast, the rider's solid red: its red bar would vanish into it, so it takes the card's ink. */
.rider-toast-container > .bz-toast.is-urgent .rider-toast-progress-fill {
  background: var(--bz-on-rider-solid);
}

.rider-toast-progress-fill.is-delivered,
.rider-toast-progress-fill.is-msg-success { background: var(--bz-ok); }
.rider-toast-progress-fill.is-msg-info { background: var(--bz-ink-2); }
.rider-toast-progress-fill.is-msg-warning { background: var(--bz-warn); }

@keyframes riderProgressShrink {
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
}

/* =========================================================================
   Coverage thanks — the positive close of the photo-coverage loop: a snackbar of the system
   (.bz-snackbar), non-blocking and auto-dismissing. It sits ABOVE the order tray (about 133 px
   tall, and a little more once its margins are the system's), so it never covers a card (norm 3).
   Only opacity and transform animate.
   ========================================================================= */
.coverage-thanks-toast {
  position: fixed;
  bottom: 168px;
  left: 0;
  right: 0;
  width: max-content;
  max-width: calc(100vw - 2 * var(--bz-gutter));
  margin: 0 auto;
  z-index: var(--bz-z-coverage-thanks);
  font-family: var(--bz-font);
  pointer-events: none;
}

/* A phone has no tray: the orders are a column down the whole screen, so the snackbar takes the foot of it. */
@media screen and (max-width: 767px) {
  .coverage-thanks-toast {
    bottom: calc(env(safe-area-inset-bottom, 0px) + var(--bz-space-6));
  }
}

.coverage-thanks-enter-active {
  transition: opacity var(--bz-duration) var(--bz-ease-out);
}
.coverage-thanks-leave-active {
  transition: opacity var(--bz-duration-fast) var(--bz-ease-out);
}
.coverage-thanks-enter-from,
.coverage-thanks-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .coverage-thanks-enter-active {
    transition: opacity var(--bz-duration) var(--bz-ease-out), transform var(--bz-duration) var(--bz-ease-out);
  }
  .coverage-thanks-leave-active {
    transition: opacity var(--bz-duration-fast) var(--bz-ease-out), transform var(--bz-duration-fast) var(--bz-ease-out);
  }
  .coverage-thanks-enter-from,
  .coverage-thanks-leave-to {
    transform: translateY(var(--bz-space-3));
  }
}

/* =========================================================================
   Intercom notification stack — Finn's replies must not cover the kitchen's
   controls.
   Intercom pins the stack of unread message previews to the bottom-right
   corner (bottom: 10px), which is exactly where the tracker keeps its action
   lane: the order row on the home screen and the Analyze button once an order
   is open. Those previews persist for hours until someone taps them, and the
   frame swallows the taps inside its 400px box — a tap on the green Analyze
   button opened the chat instead of analysing the order.
   Lifting the stack above that lane is enough: it still grows upwards, away
   from the orders, and the messenger window itself is untouched (opening the
   chat is a deliberate act, and Intercom drops the stack while it is open).
   Every number is the measured bottom edge of what must stay clear, plus
   ~16px of air:
     - tablets: 133px for .classic-layout (the order row) and 132px for
       .order-info-wrapper (the capture footer);
     - phones: 262px for .last-photo-stack (bottom 160 + ~102 tall), which
       already clears the 172px footer (156px + its 16px padding, content-box)
       and the 238px top edge of .duplicate-hint.
   Known and accepted: on ~1024px-wide tablets a long .duplicate-hint (the
   "3 black captures" variant) can still run under the stack's left edge. It is
   transient, pointer-events: none, and lifting the stack further would push it
   into the ticket instead.
   The frame is matched by its `name` prefix — Intercom's own classes are
   generated hashes, and the widget updates from their CDN without a deploy
   here. If they ever rename the frame the stack silently drops back to the
   corner: check the `iframe[data-intercom-frame]` names in the DOM. The
   deploy-proof alternative is `hide_notifications` plus our own unread badge
   fed by Intercom('onUnreadCountChange').
   ========================================================================= */
iframe[name^="intercom-notification"] {
  bottom: 278px !important;
}

@media screen and (min-width: 768px) {
  iframe[name^="intercom-notification"] {
    bottom: 148px !important;
  }
}

/* While a dialog is open (src/ui/components/BzDialog.vue sets the class), the stack goes: at 148 px
   it still falls on a dialog's lower buttons — the help flow's last tile, measured — and a dialog is
   the whole of the kitchen's attention. `visibility` takes its taps with it; the previews are back
   the moment the dialog closes. */
body.bz-dialog-open iframe[name^="intercom-notification"] {
  visibility: hidden !important;
}
</style>
